import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Stack,
  Chip,
  Collapse,
  Link as MuiLink
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  FileDownload as FileDownloadIcon,
  ShowChart as ShowChartIcon,
  InfoOutlined as InfoOutlinedIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  History as HistoryIcon,
  CalendarMonth as CalendarMonthNavIcon,
  TableRows as TableRowsIcon,
  Assessment as AssessmentIcon,
  MoreVert as MoreVertIcon,
  Comment as CommentIcon,
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  getDay,
  parseISO
} from 'date-fns';
import { Bar, Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  PointElement,
  LineController,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { getReleaseCalendarEvents, createReleaseCalendarEvent, updateReleaseCalendarEvent, deleteReleaseCalendarEvent, getCustomerInstances, saveCustomerInstances, refreshCustomerInstancesCBV, refreshCustomerInstancesMSV, refreshCustomerInstancesAzure, refreshCustomerInstancesBulk, getCustomerInstancesBulkProgress, stopCustomerInstancesBulk, fetchCustomerInstanceRowCBV, fetchCustomerInstanceRowMSV, fetchCustomerInstanceRowAzure, updateCustomerInstanceRow, deleteCustomerInstanceRow, addCustomerInstanceRow, getCustomerInstanceCbvResourceHistory, getHeapRecommendations, refreshHeapRecommendationOne, refreshHeapRecommendationAll, getDbConnectionRecommendations, refreshDbConnectionRecommendationOne, refreshDbConnectionRecommendationAll, getThreadRecommendations, refreshThreadRecommendationOne, refreshThreadRecommendationAll, getProductionAnalysisNotes, upsertProductionAnalysisNote, bulkUpsertProductionAnalysisNotes, refreshProductionAnalysisSnow, getProductionAnalysisSchedulerStatus, getRecommendationLevelTransitions, postMigrationCdnLookup } from '../../services/apiService';
import { canPerformQAActions, isAdmin, isAuthenticated } from '../../services/authService';
import { PREPOPULATED_2026 } from '../../data/releaseCalendarPrepopulated';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ProductionProgressTab from './ProductionProgressTab';
import MemoryImprovementsTab from './MemoryImprovementsTab';
import InfrastructureResourceTab from './InfrastructureResourceTab';
import CdnView from './CdnView';
import AzureConfigTab from './AzureConfigTab';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  PointElement,
  LineController,
  Title,
  ChartTooltip,
  Legend
);

const EVENT_TYPES = [
  { value: 'maintenance_dev_freeze', label: 'Maintenance Release - Dev Freeze', shortLabel: 'Dev Freeze', color: '#FFF689' },
  { value: 'maintenance_release', label: 'Maintenance Release - Release', shortLabel: 'Maintenance Release', color: '#B4CFA4' },
  { value: 'feature_dev_freeze', label: 'Feature Release - Dev Freeze', shortLabel: 'Feature Dev Freeze', color: '#F4D35E' },
  { value: 'feature_release', label: 'Feature Release - Release', shortLabel: 'Feature Release', color: '#62866C' },
  { value: 'pm_signoff', label: 'PM Sign Off on Release', shortLabel: 'PM Sign Off', color: '#8C7DA8' },
  { value: 'deployment_weekend', label: 'Release Deployment Weekends', shortLabel: 'Deployment', color: '#81B2D9' },
  { value: 'deployment_non_prod', label: 'Deployment (non-prod)', shortLabel: 'Deployment (non-prod)', color: '#A0C5E3' },
  { value: 'deployment_standard', label: 'Deployment (Standard)', shortLabel: 'Deployment (Standard)', color: '#81B2D9' },
  { value: 'deployment_vip', label: 'Deployment (VIP)', shortLabel: 'Deployment (VIP)', color: '#32769B' },
  { value: 'weekend_holiday', label: 'Weekend & Public Holidays', shortLabel: 'Holiday', color: '#e0e0e0' },
  { value: 'working_day', label: 'Working day', shortLabel: 'Working day', color: 'transparent' }
];

const EVENT_COLOR_MAP = Object.fromEntries(EVENT_TYPES.filter((e) => e.color).map((e) => [e.value, e.color]));

/** Return true if hex background is dark (use white text for contrast). */
const isDarkBg = (hex) => {
  if (!hex || hex === 'transparent') return false;
  const h = hex.replace(/^#/, '');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

/** Deep-link: main tabs 0 Calendar, 1 Customer instances, 2 Production analysis. */
const parseRcTabFromSearch = (searchParams) => {
  const rc = searchParams.get('rcTab');
  if (rc != null) {
    const n = parseInt(rc, 10);
    if (!Number.isNaN(n) && n >= 0 && n <= 2) return n;
  }
  if (searchParams.get('paSub') != null) return 2;
  return 0;
};

/** Deep-link: Production analysis sub-tabs 0 Memory, 1 Threads, 2 DB, 3 Progress, 4 Eng - Memory Improvements. */
const parsePaSubFromSearch = (searchParams) => {
  const pa = searchParams.get('paSub');
  if (pa != null) {
    const n = parseInt(pa, 10);
    if (!Number.isNaN(n) && n >= 0 && n <= 5) return n;
  }
  return 0;
};

/** Deep-link: Customer instances inner tabs 0 Release view, 1 Infrastructure resource view, 2 CDN View, 3 Azure Configuration. */
const parseCiViewFromSearch = (searchParams) => {
  const ci = searchParams.get('ciView');
  if (ci != null) {
    const n = parseInt(ci, 10);
    if (!Number.isNaN(n) && n >= 0 && n <= 2) return n;
  }
  return 0;
};

/** API / UI: which Production analysis dimension (separate SNOW ticket + dates per client). */
const PA_ANALYSIS = {
  MEMORY: 'memory',
  THREADS: 'threads',
  DB: 'db'
};

const PA_ANALYSIS_LABEL = {
  memory: 'JVM memory',
  threads: 'Threads',
  db: 'DB connections'
};

function paAnalysisTypeToSelectionKey(analysisType) {
  if (analysisType === PA_ANALYSIS.MEMORY) return 'memory';
  if (analysisType === PA_ANALYSIS.THREADS) return 'threads';
  return 'db';
}

/**
 * Slice for one dimension; falls back to legacy top-level fields for memory only (pre-migration data).
 */
function getPaAnalysisSlice(note, analysisType) {
  if (!note) {
    return { comment: '', serviceNowNumber: '', snowRaisedOn: null, snowExecutedOn: null, snowSyncError: '' };
  }
  const a = note.analyses?.[analysisType];
  const hasNested =
    a &&
    (String(a.serviceNowNumber || '').trim() ||
      String(a.comment || '').trim() ||
      a.snowRaisedOn ||
      a.snowExecutedOn ||
      String(a.snowSyncError || '').trim());
  if (hasNested) {
    return {
      comment: a.comment ?? '',
      serviceNowNumber: a.serviceNowNumber ?? '',
      snowRaisedOn: a.snowRaisedOn ?? null,
      snowExecutedOn: a.snowExecutedOn ?? null,
      snowSyncError: a.snowSyncError ?? ''
    };
  }
  if (analysisType === PA_ANALYSIS.MEMORY && note.serviceNowNumber != null && String(note.serviceNowNumber).trim() !== '') {
    return {
      comment: note.comment ?? '',
      serviceNowNumber: note.serviceNowNumber ?? '',
      snowRaisedOn: note.snowRaisedOn ?? null,
      snowExecutedOn: note.snowExecutedOn ?? null,
      snowSyncError: note.snowSyncError ?? ''
    };
  }
  return { comment: '', serviceNowNumber: '', snowRaisedOn: null, snowExecutedOn: null, snowSyncError: '' };
}

/** Production analysis SNOW filter: `all` | `only` (has ticket #) | `not` (no ticket #). */
const passesProductionAnalysisSnowFilter = (mode, stableKey, notes, analysisType) => {
  if (mode === 'all') return true;
  const slice = getPaAnalysisSlice(notes[stableKey], analysisType);
  const hasSnow = Boolean(slice.serviceNowNumber?.trim());
  if (mode === 'only') return hasSnow;
  if (mode === 'not') return !hasSnow;
  return true;
};

/** Map deployment event type to group value(s) used in Customer Instances CSV */
const DEPLOYMENT_GROUP_MAP = {
  deployment_vip: ['vip'],
  deployment_standard: ['Standard'],
  deployment_non_prod: ['preprod', 'non-prod', 'nonprod'],
  deployment_weekend: ['Standard', 'vip', 'preprod']
};

/** Possible CSV column names for "group" (VIP / Standard / preprod). Check these when matching deployment lists. */
const GROUP_COLUMN_KEYS = ['group_name', 'Group', 'group'];

/** Datadog Logs lookback for heap recommendation — 15 days, aggregated per UTC day. */
const HEAP_RECOMMENDATION_DAYS = 15;

/** DB connection (@CADBC) analysis window — 15 days. */
const DB_CONNECTION_RECOMMENDATION_DAYS = 15;

/** Tomcat threads (@AT) analysis window — 15 days. */
const THREAD_RECOMMENDATION_DAYS = 15;

/** Scheduler status timestamps (ISO) shown in Asia/Kolkata */
function formatDateTimeIst(iso) {
  if (iso == null || iso === '') return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

/** UX-only phases while a single-instance heap refresh runs (no granular server progress). */
const HEAP_DATADOG_REFRESH_PHASES = ['Time range & query', 'Paging log events', 'Daily totals & thresholds'];

/** HTTPS URL for Instance Domain (opens client site from heap table). */
function clientInstanceUrl(row) {
  const raw = String(row?.['Instance Domain'] ?? row?.instanceDomain ?? '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+/, '')}`;
}

function heapWarnDaysCellSx(theme, n) {
  if (n == null || !Number.isFinite(n)) return { fontWeight: 600 };
  if (n <= 0) return { color: 'text.secondary', fontWeight: 600 };
  const intensity = Math.min(1, n / 10);
  return {
    fontWeight: 700,
    color: theme.palette.warning.dark,
    bgcolor: alpha(theme.palette.warning.main, 0.1 + intensity * 0.26),
    borderRadius: 1,
    px: 1,
    py: 0.5,
    display: 'inline-block',
    minWidth: '2.25rem',
    textAlign: 'right'
  };
}

function heapCritDaysCellSx(theme, n) {
  if (n == null || !Number.isFinite(n)) return { fontWeight: 600 };
  if (n <= 0) return { color: 'text.secondary', fontWeight: 600 };
  const intensity = Math.min(1, n / 10);
  return {
    fontWeight: 700,
    color: theme.palette.error.dark,
    bgcolor: alpha(theme.palette.error.main, 0.1 + intensity * 0.26),
    borderRadius: 1,
    px: 1,
    py: 0.5,
    display: 'inline-block',
    minWidth: '2.25rem',
    textAlign: 'right'
  };
}

/** Format ISO/date for SNOW columns; empty string if unset. */
function formatProductionAnalysisSnowDate(isoOrDate) {
  if (!isoOrDate) return '';
  try {
    const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return '';
    return format(d, 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

/** Single SNOW cell: Raised / Executed lines; both empty if not set. */
function productionAnalysisSnowCellContent(note, analysisType) {
  const slice = getPaAnalysisSlice(note, analysisType);
  const r = formatProductionAnalysisSnowDate(slice.snowRaisedOn);
  const e = formatProductionAnalysisSnowDate(slice.snowExecutedOn);
  return {
    raisedLine: r ? `Raised: ${r}` : '',
    executedLine: e ? `Executed: ${e}` : '',
    error: slice.snowSyncError || ''
  };
}

function csvEscapeCell(val) {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Compute memory-change summary from a customer-instance row (uses `_cbvResourceHistory` attached by the API).
 * Returns null when no history exists. An "upgrade" is a strict increase in Max Memory between two consecutive
 * history entries; a "downgrade" is a strict decrease. The very first entry is not a change on its own.
 */
function computeMemoryUpgrades(row) {
  const hist = Array.isArray(row?._cbvResourceHistory) ? row._cbvResourceHistory : [];
  if (!hist.length) return null;
  const sorted = [...hist]
    .map((h) => ({
      recordedAt: h.recordedAt ? new Date(h.recordedAt) : null,
      maxMemory: h.maxMemory != null && Number.isFinite(Number(h.maxMemory)) ? Number(h.maxMemory) : null
    }))
    .filter((h) => h.recordedAt && !isNaN(h.recordedAt.getTime()))
    .sort((a, b) => a.recordedAt - b.recordedAt);
  const changes = [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (prev.maxMemory == null || cur.maxMemory == null) continue;
    if (cur.maxMemory === prev.maxMemory) continue;
    changes.push({
      recordedAt: cur.recordedAt,
      fromMb: prev.maxMemory,
      toMb: cur.maxMemory,
      direction: cur.maxMemory > prev.maxMemory ? 'upgrade' : 'downgrade'
    });
  }
  const upgrades = changes.filter((c) => c.direction === 'upgrade').length;
  const downgrades = changes.filter((c) => c.direction === 'downgrade').length;
  const last = changes.length ? changes[changes.length - 1] : null;
  const lastUpgrade = [...changes].reverse().find((c) => c.direction === 'upgrade') || null;
  return { count: changes.length, upgrades, downgrades, last, lastUpgrade, changes };
}

/** MB → GiB, rounded to one decimal for display. */
function mbToGiBDisplay(mb) {
  if (mb == null || !Number.isFinite(Number(mb))) return null;
  const gib = (Number(mb) * 1024 * 1024) / 1024 ** 3;
  return Math.round(gib * 10) / 10;
}

/** Stable key for server-persisted snapshots (must match releaseCalendarController getMemoryRecommendationStableKey). */
const getMemoryRecommendationStableKey = (row) => {
  const name = String(row?.['Instance Name'] ?? row?.instanceName ?? '').trim();
  const domain = String(row?.['Instance Domain'] ?? row?.instanceDomain ?? '').trim();
  return `${name}-${domain}`;
};

/** Plain row snapshot for heap API so JSON bodies always include Instance Name / Domain (undefined keys are omitted by JSON.stringify). */
function buildHeapRecommendationRowPayload(row) {
  const instanceName = String(row?.['Instance Name'] ?? row?.instanceName ?? '').trim();
  const instanceDomain = String(row?.['Instance Domain'] ?? row?.instanceDomain ?? '').trim();
  const friendly = String(row?.['Friendly name'] ?? row?.friendlyName ?? '').trim();
  const region = String(row?.Region ?? row?.region ?? '').trim();
  const maxRaw = row?.['Max Memory'] ?? row?.maxMemoryMb;
  const payload = {
    'Instance Name': instanceName,
    'Instance Domain': instanceDomain,
    'Friendly name': friendly || instanceName,
    Region: region,
    'Max Memory': maxRaw != null && maxRaw !== '' ? maxRaw : ''
  };
  // Bulk progress keys use Mongo _id when present; keep it so resume/checkpoint matches.
  if (row?._id) payload._id = row._id;
  return payload;
}

/** Row snapshot for DB connection recommendation API (Max DB Connections from CBV / infrastructure). */
function buildDbConnectionRecommendationRowPayload(row) {
  const instanceName = String(row?.['Instance Name'] ?? row?.instanceName ?? '').trim();
  const instanceDomain = String(row?.['Instance Domain'] ?? row?.instanceDomain ?? '').trim();
  const friendly = String(row?.['Friendly name'] ?? row?.friendlyName ?? '').trim();
  const region = String(row?.Region ?? row?.region ?? '').trim();
  const maxDbRaw = row?.['Max DB Connections'];
  const payload = {
    'Instance Name': instanceName,
    'Instance Domain': instanceDomain,
    'Friendly name': friendly || instanceName,
    Region: region,
    'Max DB Connections': maxDbRaw != null && maxDbRaw !== '' ? maxDbRaw : ''
  };
  if (row?._id) payload._id = row._id;
  return payload;
}

/** Row snapshot for thread recommendation API (Max Threads / Tomcat from CBV). */
function buildThreadRecommendationRowPayload(row) {
  const instanceName = String(row?.['Instance Name'] ?? row?.instanceName ?? '').trim();
  const instanceDomain = String(row?.['Instance Domain'] ?? row?.instanceDomain ?? '').trim();
  const friendly = String(row?.['Friendly name'] ?? row?.friendlyName ?? '').trim();
  const region = String(row?.Region ?? row?.region ?? '').trim();
  const maxThreadsRaw = row?.['Max Threads'];
  const payload = {
    'Instance Name': instanceName,
    'Instance Domain': instanceDomain,
    'Friendly name': friendly || instanceName,
    Region: region,
    'Max Threads': maxThreadsRaw != null && maxThreadsRaw !== '' ? maxThreadsRaw : ''
  };
  if (row?._id) payload._id = row._id;
  return payload;
}

const getGroupValue = (row) => {
  for (const k of GROUP_COLUMN_KEYS) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') return String(row[k]).trim();
    const normalized = k.toLowerCase().replace(/\s/g, '_');
    const found = Object.keys(row).find((key) => key.toLowerCase().replace(/\s/g, '_') === normalized);
    if (found != null && row[found] !== undefined && row[found] !== null && String(row[found]).trim() !== '')
      return String(row[found]).trim();
  }
  return '';
};

/** Possible CSV column names for region (env). */
const REGION_COLUMN_KEYS = ['env_name', 'Region', 'region'];

const getRegionValue = (row) => {
  for (const k of REGION_COLUMN_KEYS) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') return String(row[k]).trim();
    const normalized = k.toLowerCase().replace(/\s/g, '_');
    const found = Object.keys(row).find((key) => key.toLowerCase().replace(/\s/g, '_') === normalized);
    if (found != null && row[found] !== undefined && row[found] !== null && String(row[found]).trim() !== '')
      return String(row[found]).trim();
  }
  return '';
};

/** Persisted recommendation level for production analysis health strip (all instances in tab). */
function getProductionAnalysisLevel(entry) {
  if (!entry) return 'none';
  const lv = entry?.recommendation?.level;
  if (lv === 'critical' || lv === 'warning' || lv === 'good') return lv;
  return 'none';
}

/**
 * Filter list by recommendation band: all | critical | warning | good (OK) | none (no data) | oom (memory tab only;
 * matches entries with at least one OutOfMemoryError event in the lookback window).
 */
function passesProductionAnalysisHealthFilter(mode, entry) {
  if (mode === 'all') return true;
  if (mode === 'oom') {
    const n = entry?.oomSummary?.totalEvents;
    return n != null && Number.isFinite(Number(n)) && Number(n) > 0;
  }
  if (mode === 'oomAutoRestart') {
    const n = entry?.oomAutoRestartSummary?.totalEvents;
    return n != null && Number.isFinite(Number(n)) && Number(n) > 0;
  }
  return getProductionAnalysisLevel(entry) === mode;
}

function formatProductionAnalysisLevelLabel(level) {
  if (level === 'critical') return 'Critical';
  if (level === 'warning') return 'Warning';
  if (level === 'good') return 'OK';
  return 'No data';
}

/** Returns true for cloud/named regions (starts with z or df, case-insensitive). Everything else is on-prem. */
function isNamedRegion(r) {
  if (!r) return false;
  const low = r.toLowerCase();
  return low.startsWith('z') || low.startsWith('df');
}

/** Region filter for production analysis tables: all | on-prem | exact named region. */
function passesProductionAnalysisRegionFilter(mode, row) {
  if (mode === 'all') return true;
  const r = getRegionValue(row);
  if (mode === 'on-prem') return !isNamedRegion(r); // blank or non-z/non-df
  return r === mode;
}

function aggregateProductionAnalysisHealth(rowsWithEntry) {
  const counts = { critical: 0, warning: 0, good: 0, none: 0, total: 0 };
  for (const { entry } of rowsWithEntry) {
    counts.total += 1;
    const level = getProductionAnalysisLevel(entry);
    counts[level] += 1;
  }
  return counts;
}

/** SNOW sync fields on notes: raised / executed dates for this analysis type (same client cohort as stats.total). */
function aggregateProductionAnalysisSnow(rowsWithStableKey, notes, analysisType) {
  const map = notes && typeof notes === 'object' ? notes : {};
  let raised = 0;
  let executed = 0;
  for (const { stableKey } of rowsWithStableKey) {
    const slice = getPaAnalysisSlice(map[stableKey], analysisType);
    if (slice.snowRaisedOn) raised += 1;
    if (slice.snowExecutedOn) executed += 1;
  }
  return { raised, executed };
}

function pctOf(n, total) {
  if (!total || n == null) return '0.0';
  return ((100 * n) / total).toFixed(1);
}

/**
 * Overall recommendation mix: % and client counts (all instances in tab).
 * @param {{ raised?: number, executed?: number }} [snow] — from aggregateProductionAnalysisSnow; % use same total as stats.
 */
function ProductionAnalysisHealthReport({ stats, snow }) {
  const { total = 0, critical = 0, warning = 0, good = 0, none = 0 } = stats || {};
  const snowRaised = snow?.raised ?? 0;
  const snowExecuted = snow?.executed ?? 0;
  const showSnow = snow != null;
  const metricCell = (label, n, color) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', lineHeight: 1.2, display: 'block' }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap', mt: 0.15 }}>
        <Typography component="span" sx={{ color, fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.2 }}>
          {pctOf(n, total)}%
        </Typography>
        <Typography
          component="span"
          color="text.secondary"
          sx={{ fontSize: '0.68rem', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
        >
          ({n})
        </Typography>
      </Box>
    </Box>
  );
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: { xs: 'none', lg: '4 1 0%' },
        minWidth: 0,
        width: { xs: '100%' },
        maxWidth: '100%',
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 0,
        alignSelf: 'stretch'
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, fontWeight: 700, lineHeight: 1.2, fontSize: '0.65rem' }}>
        Metrics
      </Typography>
      {total === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No instances
        </Typography>
      ) : (
        <>
          <Box
            role="img"
            aria-label={`Recommendation mix: ${pctOf(critical, total)}% critical, ${pctOf(warning, total)}% warning, ${pctOf(
              good,
              total
            )}% OK, ${pctOf(none, total)}% no data`}
            sx={{
              display: 'flex',
              width: '100%',
              height: 12,
              borderRadius: 0.75,
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider'
            }}
          >
            {critical > 0 && (
              <Tooltip title={`Critical ${pctOf(critical, total)}% (${critical} clients)`}>
                <Box sx={{ flex: critical, minWidth: critical ? 4 : 0, bgcolor: 'error.main' }} />
              </Tooltip>
            )}
            {warning > 0 && (
              <Tooltip title={`Warning ${pctOf(warning, total)}% (${warning} clients)`}>
                <Box sx={{ flex: warning, minWidth: warning ? 4 : 0, bgcolor: 'warning.main' }} />
              </Tooltip>
            )}
            {good > 0 && (
              <Tooltip title={`OK ${pctOf(good, total)}% (${good} clients)`}>
                <Box sx={{ flex: good, minWidth: good ? 4 : 0, bgcolor: 'success.main' }} />
              </Tooltip>
            )}
            {none > 0 && (
              <Tooltip title={`No data ${pctOf(none, total)}% (${none} clients)`}>
                <Box
                  sx={(theme) => ({
                    flex: none,
                    minWidth: none ? 4 : 0,
                    bgcolor: alpha(theme.palette.text.primary, 0.12)
                  })}
                />
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 0.75,
              columnGap: 1.25,
              rowGap: 0.5,
              alignItems: 'start'
            }}
          >
            {metricCell('Critical', critical, 'error.main')}
            {metricCell('Warning', warning, 'warning.dark')}
            {metricCell('OK', good, 'success.dark')}
            {metricCell('No data', none, 'text.secondary')}
          </Box>
          {total > 0 && showSnow && (
            <Box
              sx={{
                pt: 0.85,
                mt: 0.25,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, fontSize: '0.62rem', letterSpacing: 0.4, textTransform: 'uppercase' }}
              >
                SNOW status
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 0.75,
                  columnGap: 1.25,
                  alignItems: 'start'
                }}
              >
                {metricCell('Raised', snowRaised, 'info.dark')}
                {metricCell('Executed', snowExecuted, 'success.dark')}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.35, opacity: 0.92 }}>
                % of {total} client{total === 1 ? '' : 's'} with synced raised / executed dates (refresh SNOW on rows with a ticket #).
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {total} client{total === 1 ? '' : 's'} · % of total (recommendations above)
          </Typography>
        </>
      )}
    </Paper>
  );
}

const LEGEND_GROUPS = [
  { lineLabel: 'Maintenance Release', values: ['maintenance_dev_freeze', 'maintenance_release'] },
  { lineLabel: 'Feature Release events', values: ['feature_dev_freeze', 'feature_release'] },
  { lineLabel: 'Deployment', values: ['deployment_non_prod', 'deployment_standard', 'deployment_vip', 'deployment_weekend'] },
  { lineLabel: 'Others', values: ['pm_signoff', 'weekend_holiday', 'working_day'] }
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Release Calendar view: one month at a time with left/right navigation.
 * Prepopulated data from screenshot is always shown; API events override when present.
 * Only QA Manager and Admin can add events.
 */
/** Parse one CSV line respecting quoted fields (commas inside quotes stay) */
const parseCSVLine = (line) => {
  const out = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (!inQuotes && c === ',') {
      out.push(current.trim());
      current = '';
    } else {
      current += c;
    }
  }
  out.push(current.trim());
  return out;
};

/** Parse CSV text with header row; returns { rows, columns } so all columns display in order */
const parseCSVWithHeaders = (text) => {
  const lines = text.trim().split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) return { rows: [], columns: [] };
  const headerValues = parseCSVLine(lines[0]);
  const columns = headerValues.map((h) => h.replace(/^"|"$/g, '').trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row = {};
    columns.forEach((col, j) => {
      row[col] = values[j] != null ? String(values[j]).replace(/^"|"$/g, '').trim() : '';
    });
    rows.push(row);
  }
  return { rows, columns };
};

/** Format cell value: show date only for ISO date/time strings */
const formatCellValue = (val) => {
  const s = String(val ?? '').trim();
  if (!s) return '';
  const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  return s;
};

const escapeCsvField = (val) => {
  const s = String(val ?? '');
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** Export columns: uploaded order first, then any extra keys present on rows (e.g. after refresh). */
function buildCustomerInstancesCsv(columns, rows) {
  const hidden = new Set(['_id']);
  const cols = [...(columns || [])];
  const seen = new Set(cols.map(String));
  (rows || []).forEach((row) => {
    Object.keys(row || {}).forEach((k) => {
      if (hidden.has(k) || seen.has(k)) return;
      seen.add(k);
      cols.push(k);
    });
  });
  if (!cols.length) return '';
  const lines = [cols.map((c) => escapeCsvField(c)).join(',')];
  (rows || []).forEach((row) => {
    lines.push(cols.map((c) => escapeCsvField(formatCellValue(row[c]))).join(','));
  });
  return lines.join('\r\n');
}

/** Build getCBV.action URL for a customer instance row (Instance Domain + Instance Name). */
const getCbvUrl = (row) => {
  const domain = (row['Instance Domain'] ?? '').trim();
  const name = (row['Instance Name'] ?? '').trim();
  if (!domain || !name) return null;
  const base = domain.startsWith('http') ? domain : `https://${domain}`;
  const path = `${base.replace(/\/$/, '')}/${name.replace(/^\//, '').replace(/\/$/, '')}`;
  return `${path}/getCBV.action`;
};

/** Columns to hide from Customer Instances tab table */
const HIDDEN_CUSTOMER_COLUMNS = ['date', 'pingdom_id', 'pingdom id', '_id', 'application', 'msv', 'azure', 'instance_name', 'instance name', '_cbvRev', '_cbvResourceHistory', '_cbvhistory'];

/** CBV capacity metrics: still stored on rows / export; hidden from Release view table; shown in Infrastructure view. */
const CBV_METRICS_HIDDEN_FROM_TABLE = ['Max Memory', 'Max Threads', 'Max DB Connections'];

/** From getCBV: App server (machine name) and DB Server — Infrastructure view only. */
const INFRASTRUCTURE_ONLY_TABLE_COLUMNS = ['App server', 'DB Server'];

/** Omitted from Infrastructure view table (CIAM / Wijmo). */
const INFRASTRUCTURE_EXCLUDED_COLUMNS = ['CIAM', 'Wijmo(ASP)', 'Wijmo(System)'];

/** Infrastructure view: identity columns + Max Memory / Threads / DB at end; no CIAM/Wijmo. */
function buildInfrastructureTableColumns(baseVisible) {
  const rest = (baseVisible || []).filter(
    (c) => !INFRASTRUCTURE_EXCLUDED_COLUMNS.includes(c) && !CBV_METRICS_HIDDEN_FROM_TABLE.includes(c)
  );
  return [...rest, ...CBV_METRICS_HIDDEN_FROM_TABLE];
}

/** Customer list dialog: show only these columns (label → possible CSV header keys) */
const CUSTOMER_LIST_COLUMNS = [
  { label: 'Instance Name', keys: ['instance', 'Instance Name'] },
  { label: 'Instance Domain', keys: ['instance_domain', 'Instance Domain'] },
  { label: 'Release', keys: ['Release', 'current_version', 'Current Version', 'Version'] },
  { label: 'Region', keys: ['env_name', 'Region'] }
];

const getCustomerListValue = (row, col) => {
  for (const k of col.keys) {
    if (row[k] !== undefined && row[k] !== null) return formatCellValue(row[k]);
    const normalized = k.toLowerCase().replace(/\s/g, '_');
    const found = Object.keys(row).find((key) => key.toLowerCase().replace(/\s/g, '_') === normalized);
    if (found != null) return formatCellValue(row[found]);
  }
  return '';
};

function hostFromUrl(u) {
  if (!u || typeof u !== 'string') return '';
  try {
    const s = u.trim();
    const url = s.startsWith('http') ? s : `https://${s}`;
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function getInstanceUrlForRow(row) {
  const domain = String(row['Instance Domain'] ?? '').trim();
  const name = String(row['Instance Name'] ?? '').trim();
  if (!domain || !name) return '';
  const base = domain.startsWith('http') ? domain : `https://${domain}`;
  return `${base.replace(/\/$/, '')}/${name.replace(/^\//, '')}`;
}

const CDN_VALUES = ['Cloudflare', 'Akamai', 'No CDN'];

const ReleaseCalendarView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [year] = useState(() => new Date().getFullYear());
  const [monthIndex, setMonthIndex] = useState(() => new Date().getMonth());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDate, setAddDate] = useState(dayjs().year(year));
  const [addEventType, setAddEventType] = useState(EVENT_TYPES[0].value);
  const [addRelease, setAddRelease] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDate, setEditDate] = useState(dayjs().year(year));
  const [editEventType, setEditEventType] = useState(EVENT_TYPES[0].value);
  const [editRelease, setEditRelease] = useState('');
  const [editId, setEditId] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [tabValue, setTabValue] = useState(() => parseRcTabFromSearch(searchParams));
  const [customerInstances, setCustomerInstances] = useState([]);
  const [customerColumns, setCustomerColumns] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerInstancesLoading, setCustomerInstancesLoading] = useState(false);
  const [customerInstancesSaving, setCustomerInstancesSaving] = useState(false);
  const [refreshingCBV, setRefreshingCBV] = useState(false);
  const [refreshingMSV, setRefreshingMSV] = useState(false);
  const [refreshingAzure, setRefreshingAzure] = useState(false);
  const [rowActionLoading, setRowActionLoading] = useState({});
  const [filterEnv, setFilterEnv] = useState([]);
  const [filterGroup, setFilterGroup] = useState([]);
  const [filterVersion, setFilterVersion] = useState([]);
  const [filterCdn, setFilterCdn] = useState([]);
  const [cdnMap, setCdnMap] = useState({});
  const [cdnLookupBusy, setCdnLookupBusy] = useState(false);
  const [excludeNR, setExcludeNR] = useState(true);
  const CUSTOMER_INSTANCES_ROWS_PER_PAGE = 100;
  const [customerInstancesPage, setCustomerInstancesPage] = useState(0);
  const [customerSortBy, setCustomerSortBy] = useState(null);
  const [customerSortOrder, setCustomerSortOrder] = useState('asc');
  const [customersDialogOpen, setCustomersDialogOpen] = useState(false);
  const [customersDialogGroup, setCustomersDialogGroup] = useState(null);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [refreshDialogOnlyFailed, setRefreshDialogOnlyFailed] = useState(false);
  const [refreshDialogSelection, setRefreshDialogSelection] = useState({ cbv: true, msv: true, azure: true });
  const [refreshingFromDialog, setRefreshingFromDialog] = useState(false);
  const [instanceBulkProgress, setInstanceBulkProgress] = useState(null);
  const [stoppingBulk, setStoppingBulk] = useState(false);
  const [rowRefreshDialogRowId, setRowRefreshDialogRowId] = useState(null);
  const [rowRefreshDialogSelection, setRowRefreshDialogSelection] = useState({ cbv: false, msv: false, azure: false });
  const [rowRefreshDialogRunning, setRowRefreshDialogRunning] = useState(false);
  /** CBV resource history dialog: Max Memory / Threads / Max DB connections over time */
  const [cbvResourceHistoryDialog, setCbvResourceHistoryDialog] = useState(null);
  const [heapEntriesByStableKey, setHeapEntriesByStableKey] = useState({});
  const [heapBulkProgress, setHeapBulkProgress] = useState(null);
  /** Daily cron: memory + thread + DB Datadog bulk (server-side). */
  const [productionAnalysisSchedulerStatus, setProductionAnalysisSchedulerStatus] = useState(null);
  /** Per analysis tab: selected stableKeys for bulk SNOW. */
  const [paRowSelection, setPaRowSelection] = useState({ memory: {}, threads: {}, db: {} });
  const [paBulkSnowDialog, setPaBulkSnowDialog] = useState(null);
  const [paBulkSnowSaving, setPaBulkSnowSaving] = useState(false);
  const [refreshingHeapAll, setRefreshingHeapAll] = useState(false);
  const [refreshingHeapStableKey, setRefreshingHeapStableKey] = useState(null);
  const [heapDailyDialog, setHeapDailyDialog] = useState(null);
  /** 1–50: Datadog pre-filter — log lines where free heap is below this percent of total (default 10). */
  const [heapFreePctThreshold, setHeapFreePctThreshold] = useState(10);
  /** Last refresh: show exact Datadog query + counts (debug 0-result searches). */
  const [heapLastQueryDebug, setHeapLastQueryDebug] = useState(null);
  /** Heap tab: help dialog (query explanation + last query). */
  const [heapHelpDialogOpen, setHeapHelpDialogOpen] = useState(false);
  /** Cycles through HEAP_DATADOG_REFRESH_PHASES while refreshingHeapStableKey is set. */
  const [heapRefreshPhaseIndex, setHeapRefreshPhaseIndex] = useState(0);
  const [recommendationClientSearch, setRecommendationClientSearch] = useState('');
  /** Min days (inclusive) — empty = no filter. */
  /** Memory change filter on the Memory analysis tab: 'all' | 'changed' | 'upgraded' | 'downgraded'. */
  const [heapFilterMemoryChange, setHeapFilterMemoryChange] = useState('all');
  /** When true, only show instances that require attention (crit days >3 in 7d, OOM in 7d, or upgraded but still critical/OOM). */
  const [heapFilterRequireFocus, setHeapFilterRequireFocus] = useState(false);
  const [dbConnEntriesByStableKey, setDbConnEntriesByStableKey] = useState({});
  const [dbConnBulkProgress, setDbConnBulkProgress] = useState(null);
  const [refreshingDbConnAll, setRefreshingDbConnAll] = useState(false);
  const [refreshingDbConnStableKey, setRefreshingDbConnStableKey] = useState(null);
  const [dbConnDailyDialog, setDbConnDailyDialog] = useState(null);
  const [dbConnLastQueryDebug, setDbConnLastQueryDebug] = useState(null);
  const [dbConnHelpDialogOpen, setDbConnHelpDialogOpen] = useState(false);
  /** 1–99: @CADBC &gt; this % of Max DB Connections (default 75). */
  const [dbConnPctOfMax, setDbConnPctOfMax] = useState(75);
  const [dbConnClientSearch, setDbConnClientSearch] = useState('');
  const [dbConnFilterWarnMin, setDbConnFilterWarnMin] = useState('');
  const [dbConnFilterCritMin, setDbConnFilterCritMin] = useState('');
  const [dbConnPage, setDbConnPage] = useState(0);
  const [threadEntriesByStableKey, setThreadEntriesByStableKey] = useState({});
  const [threadBulkProgress, setThreadBulkProgress] = useState(null);
  const [refreshingThreadAll, setRefreshingThreadAll] = useState(false);
  const [refreshingThreadStableKey, setRefreshingThreadStableKey] = useState(null);
  const [threadDailyDialog, setThreadDailyDialog] = useState(null);
  const [threadLastQueryDebug, setThreadLastQueryDebug] = useState(null);
  const [threadHelpDialogOpen, setThreadHelpDialogOpen] = useState(false);
  /** 1–99: @AT &gt; this % of Max Threads (default 75). */
  const [threadPctOfMax, setThreadPctOfMax] = useState(75);
  const [threadClientSearch, setThreadClientSearch] = useState('');
  const [threadFilterWarnMin, setThreadFilterWarnMin] = useState('');
  const [threadFilterCritMin, setThreadFilterCritMin] = useState('');
  const [threadPage, setThreadPage] = useState(0);
  const [editDialogRowId, setEditDialogRowId] = useState(null);
  const [editFormValues, setEditFormValues] = useState({});
  const [editDialogSaving, setEditDialogSaving] = useState(false);
  const [addInstanceDialogOpen, setAddInstanceDialogOpen] = useState(false);
  const [addInstanceFormValues, setAddInstanceFormValues] = useState({});
  const [addInstanceSaving, setAddInstanceSaving] = useState(false);
  const [deleteConfirmRowId, setDeleteConfirmRowId] = useState(null);
  const [deleteConfirmRowName, setDeleteConfirmRowName] = useState('');
  const [deleteRowLoading, setDeleteRowLoading] = useState(false);
  /** 0 Release, 1 Infra, 2 Production Analysis (Customer Instances subtabs). */
  const [customerInstancesViewTab, setCustomerInstancesViewTab] = useState(() => parseCiViewFromSearch(searchParams));
  /** Production Analysis: 0 Memory, 1 Threads, 2 DB connections. */
  const [productionAnalysisTab, setProductionAnalysisTab] = useState(() => parsePaSubFromSearch(searchParams));
  /** stableKey -> { comment, serviceNowNumber, updatedAt? } */
  const [productionAnalysisNotes, setProductionAnalysisNotes] = useState({});
  const [paActionMenuAnchor, setPaActionMenuAnchor] = useState(null);
  const [paActionMenuContext, setPaActionMenuContext] = useState(null);
  const [paNoteDialog, setPaNoteDialog] = useState(null);
  const [paNoteSaving, setPaNoteSaving] = useState(false);
  /** SNOW: all rows | only rows with a ticket # | only rows without a ticket #. */
  const [productionAnalysisSnowFilter, setProductionAnalysisSnowFilter] = useState('all');
  /** Recommendation level filter (Critical / Warning / OK / No Data). */
  const [productionAnalysisHealthFilter, setProductionAnalysisHealthFilter] = useState('all');
  /** Region filter: all | __blank__ | env name (shared across Memory / Threads / DB tabs). */
  const [productionAnalysisRegionFilter, setProductionAnalysisRegionFilter] = useState('all');
  const [refreshingPaSnow, setRefreshingPaSnow] = useState(false);
  const [recommendationTransitions, setRecommendationTransitions] = useState([]);
  const [recommendationTransitionsLoading, setRecommendationTransitionsLoading] = useState(false);
  const [recommendationTransitionsError, setRecommendationTransitionsError] = useState(null);
  const [statusChangesExpanded, setStatusChangesExpanded] = useState(false);
  const [paSearchQuery, setPaSearchQuery] = useState('');
  const [paSearchSelectedKey, setPaSearchSelectedKey] = useState(null);
  const [paSearchTransitions, setPaSearchTransitions] = useState({ memory: [], threads: [], db: [] });
  const [paSearchTransitionsLoading, setPaSearchTransitionsLoading] = useState(false);
  /** Per-column substring filters (Infrastructure resource view table only). */
  const [infraColumnFilters, setInfraColumnFilters] = useState({});

  const showCustomerInstanceRowActions = isAdmin();

  useEffect(() => {
    const rc = searchParams.get('rcTab');
    const pa = searchParams.get('paSub');
    const ci = searchParams.get('ciView');
    let newTab = null;
    if (rc != null) {
      const n = parseInt(rc, 10);
      if (!Number.isNaN(n) && n >= 0 && n <= 2) newTab = n;
    } else if (pa != null) {
      newTab = 2;
    }
    if (newTab !== null) setTabValue(newTab);
    if (pa != null) {
      const n = parseInt(pa, 10);
      if (!Number.isNaN(n) && n >= 0 && n <= 5) setProductionAnalysisTab(n);
    }
    if (ci != null) {
      const n = parseInt(ci, 10);
      if (!Number.isNaN(n) && n >= 0 && n <= 2) setCustomerInstancesViewTab(n);
    }
  }, [searchParams]);

  const eventsByDate = useMemo(() => {
    const map = new Map();
    const apiByDate = new Map();
    events.forEach((ev) => {
      const d = typeof ev.date === 'string' ? parseISO(ev.date) : new Date(ev.date);
      const key = format(d, 'yyyy-MM-dd');
      const existing = apiByDate.get(key) || [];
      apiByDate.set(key, [...existing, ev.eventType]);
    });
    Object.entries(PREPOPULATED_2026).forEach(([dateKey, eventType]) => {
      map.set(dateKey, apiByDate.has(dateKey) ? apiByDate.get(dateKey) : [eventType]);
    });
    apiByDate.forEach((arr, key) => { if (!map.has(key)) map.set(key, arr); });
    return map;
  }, [events]);

  const eventInfoByDate = useMemo(() => {
    const map = new Map();
    const apiByDate = new Map();
    events.forEach((ev) => {
      const d = typeof ev.date === 'string' ? parseISO(ev.date) : new Date(ev.date);
      const key = format(d, 'yyyy-MM-dd');
      const existing = apiByDate.get(key) || [];
      apiByDate.set(key, [...existing, { eventType: ev.eventType, release: ev.release || '', id: ev._id || null }]);
    });
    Object.entries(PREPOPULATED_2026).forEach(([dateKey, eventType]) => {
      map.set(dateKey, apiByDate.has(dateKey) ? apiByDate.get(dateKey) : [{ eventType, release: '', id: null }]);
    });
    apiByDate.forEach((arr, key) => { if (!map.has(key)) map.set(key, arr); });
    return map;
  }, [events]);

  const loadEvents = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const opts = forceRefresh ? { useCache: false } : {};
      const data = await getReleaseCalendarEvents(year, opts);
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load release calendar events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [year]);

  const loadInstanceBulkProgress = useCallback(async (prevStatus) => {
    try {
      const res = await getCustomerInstancesBulkProgress();
      const progress = res?.data?.progress ?? null;
      setInstanceBulkProgress(progress);
      if (prevStatus === 'running' && (!progress || progress.status === 'idle' || progress.status === 'stopped')) {
        setStoppingBulk(false);
        getCustomerInstances()
          .then(({ columns, rows }) => {
            setCustomerColumns(columns);
            setCustomerInstances(rows);
          })
          .catch(() => {});
      }
    } catch {}
  }, []);

  const loadHeapRecommendations = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      const { entries, bulkProgress } = await getHeapRecommendations();
      setHeapEntriesByStableKey(entries && typeof entries === 'object' ? entries : {});
      setHeapBulkProgress(bulkProgress);
    } catch (err) {
      console.error('loadHeapRecommendations failed:', err);
      if (!silent) {
        setError(err.message || 'Failed to load memory analysis data from the server');
      }
    }
  }, []);

  useEffect(() => {
    loadHeapRecommendations();
  }, [loadHeapRecommendations]);

  useEffect(() => {
    loadInstanceBulkProgress(null);
  }, [loadInstanceBulkProgress]);

  useEffect(() => {
    if (!instanceBulkProgress || instanceBulkProgress.status !== 'running') return;
    const id = setInterval(() => loadInstanceBulkProgress('running'), 3000);
    return () => clearInterval(id);
  }, [instanceBulkProgress, loadInstanceBulkProgress]);

  const handleStopBulk = useCallback(async () => {
    setStoppingBulk(true);
    try {
      await stopCustomerInstancesBulk();
    } catch {
      setStoppingBulk(false);
    }
  }, []);

  const loadProductionAnalysisNotes = useCallback(async () => {
    try {
      const notes = await getProductionAnalysisNotes();
      setProductionAnalysisNotes(notes && typeof notes === 'object' ? notes : {});
    } catch (err) {
      console.error('loadProductionAnalysisNotes failed:', err);
    }
  }, []);

  useEffect(() => {
    if (tabValue !== 2) return undefined;
    loadProductionAnalysisNotes();
    let cancelled = false;
    const loadScheduler = async () => {
      try {
        const st = await getProductionAnalysisSchedulerStatus();
        if (!cancelled) setProductionAnalysisSchedulerStatus(st);
      } catch {
        if (!cancelled) setProductionAnalysisSchedulerStatus(null);
      }
    };
    loadScheduler();
    const intervalId = setInterval(loadScheduler, 120000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [tabValue, loadProductionAnalysisNotes]);

  useEffect(() => {
    if (tabValue !== 2) return undefined;
    // Always load for the Memory sub-tab so the "New critical" banner auto-populates.
    // For other sub-tabs, still gate on the status-changes accordion being expanded.
    if (!statusChangesExpanded && productionAnalysisTab !== 1) return undefined;
    let cancelled = false;
    (async () => {
      setRecommendationTransitionsLoading(true);
      setRecommendationTransitionsError(null);
      try {
        const analysisType =
          productionAnalysisTab === 1 ? 'memory' : productionAnalysisTab === 2 ? 'threads' : 'db';
        const payload = await getRecommendationLevelTransitions(analysisType, 30);
        if (!cancelled) {
          setRecommendationTransitions(Array.isArray(payload?.transitions) ? payload.transitions : []);
        }
      } catch (e) {
        if (!cancelled) {
          setRecommendationTransitionsError(e.message || 'Failed to load status changes');
          setRecommendationTransitions([]);
        }
      } finally {
        if (!cancelled) setRecommendationTransitionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabValue, productionAnalysisTab, statusChangesExpanded]);

  const transitionMetrics = useMemo(() => {
    const transitions = Array.isArray(recommendationTransitions) ? recommendationTransitions : [];
    const resolved = transitions.filter((t) => t.direction === 'improved');
    const worsened = transitions.filter((t) => t.direction === 'worsened');
    const other = transitions.filter((t) => t.direction !== 'improved' && t.direction !== 'worsened');

    const uniqueResolvedClients = new Set(resolved.map((t) => t.stableKey)).size;
    const uniqueWorsenedClients = new Set(worsened.map((t) => t.stableKey)).size;

    const dayMap = new Map();
    for (const t of transitions) {
      const d = t.dayKey;
      if (!d) continue;
      if (!dayMap.has(d)) dayMap.set(d, { day: d, resolved: 0, worsened: 0 });
      const row = dayMap.get(d);
      if (t.direction === 'improved') row.resolved += 1;
      else if (t.direction === 'worsened') row.worsened += 1;
    }
    const daily = Array.from(dayMap.values()).sort((a, b) =>
      a.day < b.day ? -1 : a.day > b.day ? 1 : 0
    );

    const newBySeverity = { critical: 0, warning: 0 };
    for (const t of worsened) {
      if (t.level === 'critical') newBySeverity.critical += 1;
      else if (t.level === 'warning') newBySeverity.warning += 1;
    }
    const resolvedFromSeverity = { critical: 0, warning: 0 };
    for (const t of resolved) {
      if (t.previousLevel === 'critical') resolvedFromSeverity.critical += 1;
      else if (t.previousLevel === 'warning') resolvedFromSeverity.warning += 1;
    }

    return {
      resolved,
      worsened,
      other,
      uniqueResolvedClients,
      uniqueWorsenedClients,
      daily,
      newBySeverity,
      resolvedFromSeverity,
      net: resolved.length - worsened.length
    };
  }, [recommendationTransitions]);

  const handleSaveProductionAnalysisNote = useCallback(async () => {
    if (!paNoteDialog) return;
    setPaNoteSaving(true);
    setError(null);
    try {
      const res = await upsertProductionAnalysisNote({
        stableKey: paNoteDialog.stableKey,
        analysisType: paNoteDialog.analysisType,
        comment: paNoteDialog.comment,
        serviceNowNumber:
          paNoteDialog.analysisType === PA_ANALYSIS.MEMORY ? '' : paNoteDialog.serviceNowNumber
      });
      if (res?.success && res.note) {
        setProductionAnalysisNotes((prev) => ({
          ...prev,
          [res.note.stableKey]: res.note
        }));
        setPaNoteDialog(null);
      } else {
        setError(res?.message || 'Could not save note');
      }
    } catch (err) {
      setError(err.message || 'Could not save note');
    } finally {
      setPaNoteSaving(false);
    }
  }, [paNoteDialog]);

  const openPaBulkSnowDialog = useCallback((analysisType) => {
    const sk = paAnalysisTypeToSelectionKey(analysisType);
    const keys = Object.keys(paRowSelection[sk] || {});
    if (keys.length === 0) return;
    setPaBulkSnowDialog({ analysisType, keys, serviceNowNumber: '', comment: '' });
  }, [paRowSelection]);

  const handleSavePaBulkSnow = useCallback(async () => {
    if (!paBulkSnowDialog) return;
    setPaBulkSnowSaving(true);
    setError(null);
    try {
      const res = await bulkUpsertProductionAnalysisNotes({
        stableKeys: paBulkSnowDialog.keys,
        analysisType: paBulkSnowDialog.analysisType,
        comment: paBulkSnowDialog.comment,
        serviceNowNumber: paBulkSnowDialog.serviceNowNumber
      });
      if (res?.success && res.notes && typeof res.notes === 'object') {
        setProductionAnalysisNotes((prev) => ({ ...prev, ...res.notes }));
        const dim = paAnalysisTypeToSelectionKey(paBulkSnowDialog.analysisType);
        setPaRowSelection((prev) => ({ ...prev, [dim]: {} }));
        setPaBulkSnowDialog(null);
      } else {
        setError(res?.message || 'Could not save ServiceNow for selected rows');
      }
    } catch (err) {
      setError(err.message || 'Could not save ServiceNow for selected rows');
    } finally {
      setPaBulkSnowSaving(false);
    }
  }, [paBulkSnowDialog]);

  useEffect(() => {
    const active =
      heapBulkProgress && Array.isArray(heapBulkProgress.keys) && heapBulkProgress.keys.length > 0;
    if (!active) return undefined;
    const id = setInterval(() => {
      loadHeapRecommendations({ silent: true });
    }, 5000);
    return () => clearInterval(id);
  }, [heapBulkProgress, loadHeapRecommendations]);

  const loadDbConnectionRecommendations = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      const { entries, bulkProgress } = await getDbConnectionRecommendations();
      setDbConnEntriesByStableKey(entries && typeof entries === 'object' ? entries : {});
      setDbConnBulkProgress(bulkProgress);
    } catch (err) {
      console.error('loadDbConnectionRecommendations failed:', err);
      if (!silent) {
        setError(err.message || 'Failed to load DB connection analysis data from the server');
      }
    }
  }, []);

  useEffect(() => {
    loadDbConnectionRecommendations();
  }, [loadDbConnectionRecommendations]);

  useEffect(() => {
    const active =
      dbConnBulkProgress && Array.isArray(dbConnBulkProgress.keys) && dbConnBulkProgress.keys.length > 0;
    if (!active) return undefined;
    const id = setInterval(() => {
      loadDbConnectionRecommendations({ silent: true });
    }, 5000);
    return () => clearInterval(id);
  }, [dbConnBulkProgress, loadDbConnectionRecommendations]);

  const loadThreadRecommendations = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    try {
      const { entries, bulkProgress } = await getThreadRecommendations();
      setThreadEntriesByStableKey(entries && typeof entries === 'object' ? entries : {});
      setThreadBulkProgress(bulkProgress);
    } catch (err) {
      console.error('loadThreadRecommendations failed:', err);
      if (!silent) {
        setError(err.message || 'Failed to load thread analysis data from the server');
      }
    }
  }, []);

  useEffect(() => {
    loadThreadRecommendations();
  }, [loadThreadRecommendations]);

  useEffect(() => {
    const active =
      threadBulkProgress && Array.isArray(threadBulkProgress.keys) && threadBulkProgress.keys.length > 0;
    if (!active) return undefined;
    const id = setInterval(() => {
      loadThreadRecommendations({ silent: true });
    }, 5000);
    return () => clearInterval(id);
  }, [threadBulkProgress, loadThreadRecommendations]);

  useEffect(() => {
    let cancelled = false;
    setCustomerInstancesLoading(true);
    getCustomerInstances()
      .then(({ columns, rows }) => {
        if (cancelled) return;
        setCustomerColumns(columns);
        setCustomerInstances(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setCustomerInstances([]);
          setCustomerColumns([]);
        }
      })
      .finally(() => {
        if (!cancelled) setCustomerInstancesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (customerInstances.length === 0) return;
    const hosts = [...new Set(
      customerInstances.map((r) => hostFromUrl(getInstanceUrlForRow(r))).filter(Boolean)
    )];
    if (hosts.length === 0) return;
    setCdnLookupBusy(true);
    postMigrationCdnLookup(hosts)
      .then((res) => { if (res?.success && res?.map) setCdnMap(res.map); })
      .catch(() => {})
      .finally(() => setCdnLookupBusy(false));
  }, [customerInstances]);

  const handlePrevMonth = () => setMonthIndex((i) => (i <= 0 ? 11 : i - 1));
  const handleNextMonth = () => setMonthIndex((i) => (i >= 11 ? 0 : i + 1));

  const handleAddEvent = async () => {
    if (!addDate || !addEventType) return;
    setSubmitting(true);
    try {
      const dateStr = addDate.format('YYYY-MM-DD');
      await createReleaseCalendarEvent({ date: dateStr, eventType: addEventType, release: addRelease.trim() });
      setAddDialogOpen(false);
      setAddDate(dayjs().year(year));
      setAddEventType(EVENT_TYPES[0].value);
      setAddRelease('');
      await loadEvents(true);
    } catch (err) {
      setError(err.message || 'Failed to add event');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (dateKey, eventType, id, release = '') => {
    const [y, m, d] = dateKey.split('-').map(Number);
    setEditDate(dayjs(new Date(y, m - 1, d)));
    setEditEventType(eventType);
    setEditRelease(release || '');
    setEditId(id || null);
    setEditDialogOpen(true);
  };

  const handleEditEvent = async () => {
    if (!editDate || !editEventType) return;
    setEditSubmitting(true);
    try {
      const dateStr = editDate.format('YYYY-MM-DD');
      const payload = { date: dateStr, eventType: editEventType, release: editRelease.trim() };
      if (editId) {
        await updateReleaseCalendarEvent(editId, payload);
      } else {
        await createReleaseCalendarEvent(payload);
      }
      setEditDialogOpen(false);
      setEditId(null);
      await loadEvents(true);
    } catch (err) {
      setError(err.message || 'Failed to save event');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!editId) return;
    setEditSubmitting(true);
    try {
      await deleteReleaseCalendarEvent(editId);
      setEditDialogOpen(false);
      setEditId(null);
      await loadEvents(true);
    } catch (err) {
      setError(err.message || 'Failed to delete event');
    } finally {
      setEditSubmitting(false);
    }
  };

  const canAdd = canPerformQAActions();

  const uniqueEnvNames = useMemo(() => {
    const named = new Set();
    let hasOnPrem = false;
    customerInstances.forEach((row) => {
      const v = getRegionValue(row);
      if (isNamedRegion(v)) named.add(v);
      else if (v !== undefined && v !== null) hasOnPrem = true;
    });
    const sorted = Array.from(named).sort();
    if (hasOnPrem) sorted.push('On-Prem');
    return sorted;
  }, [customerInstances]);

  const uniqueGroupNames = useMemo(() => {
    const set = new Set();
    customerInstances.forEach((row) => {
      const v = getGroupValue(row);
      if (v) set.add(v);
    });
    return Array.from(set).sort();
  }, [customerInstances]);

  const releaseColumnKey = useMemo(() => {
    const keys = ['Release', 'current_version', 'Current Version', 'Version'];
    return customerColumns.find((col) => {
      const n = col.toLowerCase().replace(/\s/g, '_');
      return keys.some((k) => n === k.toLowerCase().replace(/\s/g, '_'));
    }) || customerColumns.find((col) => col.toLowerCase().includes('version') || col.toLowerCase().includes('release')) || null;
  }, [customerColumns]);

  /** True if row has "NR" in any getCBV-derived field (Release, Instance, CIAM, Wijmo). */
  const rowHasNRInCBV = (row, relKey) => {
    if (relKey && String(row[relKey] ?? '').trim() === 'NR') return true;
    const inst = String(row['Instance'] ?? row['Instance Type'] ?? '').trim();
    if (inst === 'NR') return true;
    if (String(row['CIAM'] ?? '').trim() === 'NR') return true;
    if (String(row['Wijmo(ASP)'] ?? '').trim() === 'NR') return true;
    if (String(row['Wijmo(System)'] ?? '').trim() === 'NR') return true;
    return false;
  };

  const uniqueReleaseValues = useMemo(() => {
    const set = new Set();
    const key = releaseColumnKey;
    if (!key) return [];
    customerInstances.forEach((row) => {
      const v = row[key] != null ? String(row[key]).trim() : '';
      if (v) set.add(v);
    });
    return Array.from(set).sort();
  }, [customerInstances, releaseColumnKey]);

  const getCdnDisplayForRow = (row) => {
    const host = hostFromUrl(getInstanceUrlForRow(row));
    if (!host) return '—';
    const lookupDone = Object.prototype.hasOwnProperty.call(cdnMap, host);
    if (cdnLookupBusy && !lookupDone) return null; // null = show spinner
    const detected = lookupDone ? cdnMap[host] : null;
    return detected === 'Cloudflare' || detected === 'Akamai' ? detected : 'No CDN';
  };

  const customerColumnsBase = useMemo(() => {
    return customerColumns.filter((col) => {
      const normalized = col.toLowerCase().replace(/\s/g, '_');
      return !HIDDEN_CUSTOMER_COLUMNS.some((hidden) => normalized === hidden.toLowerCase().replace(/\s/g, '_'));
    });
  }, [customerColumns]);

  const customerColumnsVisible = useMemo(
    () =>
      customerColumnsBase.filter(
        (col) =>
          !CBV_METRICS_HIDDEN_FROM_TABLE.includes(col) && !INFRASTRUCTURE_ONLY_TABLE_COLUMNS.includes(col)
      ),
    [customerColumnsBase]
  );

  const customerColumnsInfrastructure = useMemo(
    () => buildInfrastructureTableColumns(customerColumnsBase),
    [customerColumnsBase]
  );

  const effectiveTableColumns = useMemo(
    () => (customerInstancesViewTab === 0 ? customerColumnsVisible : customerColumnsInfrastructure),
    [customerInstancesViewTab, customerColumnsVisible, customerColumnsInfrastructure]
  );

  useEffect(() => {
    if (!customerSortBy) return;
    if (!effectiveTableColumns.includes(customerSortBy)) setCustomerSortBy(null);
  }, [customerInstancesViewTab, effectiveTableColumns, customerSortBy]);

  const filteredCustomerInstances = useMemo(() => {
    let list = customerInstances;
    if (customerSearch.trim()) {
      const q = customerSearch.trim().toLowerCase();
      list = list.filter((row) =>
        Object.values(row).some((val) => String(val).toLowerCase().includes(q))
      );
    }
    if (filterEnv.length > 0) {
      list = list.filter((row) => {
        const v = getRegionValue(row);
        if (filterEnv.includes(v)) return true;
        if (filterEnv.includes('On-Prem') && !isNamedRegion(v)) return true;
        return false;
      });
    }
    if (filterGroup.length > 0) {
      list = list.filter((row) => filterGroup.includes(getGroupValue(row)));
    }
    if (filterVersion.length > 0 && releaseColumnKey) {
      list = list.filter((row) => filterVersion.includes(String(row[releaseColumnKey] ?? '').trim()));
    }
    if (filterCdn.length > 0) {
      list = list.filter((row) => {
        const host = hostFromUrl(getInstanceUrlForRow(row));
        if (!host) return filterCdn.includes('No CDN');
        const lookupDone = Object.prototype.hasOwnProperty.call(cdnMap, host);
        if (!lookupDone) return true;
        const detected = cdnMap[host];
        const cdnVal = detected === 'Cloudflare' || detected === 'Akamai' ? detected : 'No CDN';
        return filterCdn.includes(cdnVal);
      });
    }
    if (excludeNR) {
      list = list.filter((row) => !rowHasNRInCBV(row, releaseColumnKey));
    }
    return list;
  }, [customerInstances, customerSearch, filterEnv, filterGroup, filterVersion, filterCdn, cdnMap, releaseColumnKey, excludeNR]);

  const filteredCustomerInstancesForTable = useMemo(() => {
    if (customerInstancesViewTab !== 1) return filteredCustomerInstances;
    let list = filteredCustomerInstances;
    customerColumnsInfrastructure.forEach((key) => {
      const filter = (infraColumnFilters[key] || '').trim().toLowerCase();
      if (!filter) return;
      list = list.filter((row) => String(row[key] ?? '').toLowerCase().includes(filter));
    });
    return list;
  }, [filteredCustomerInstances, customerInstancesViewTab, customerColumnsInfrastructure, infraColumnFilters]);

  const sortedCustomerInstances = useMemo(() => {
    if (!customerSortBy) return filteredCustomerInstancesForTable;
    const key = customerSortBy;
    return [...filteredCustomerInstancesForTable].sort((a, b) => {
      const va = a[key] != null ? String(a[key]) : '';
      const vb = b[key] != null ? String(b[key]) : '';
      const cmp = va.localeCompare(vb, undefined, { numeric: true });
      return customerSortOrder === 'asc' ? cmp : -cmp;
    });
  }, [filteredCustomerInstancesForTable, customerSortBy, customerSortOrder]);

  /**
   * Production analysis: all instances except NR rows (same NR rules as Customer instances “Exclude NR”).
   * Ignores search/region/group/version filters so those controls don’t hide rows here.
   */
  const productionAnalysisSortedInstances = useMemo(() => {
    const list = customerInstances.filter((row) => !rowHasNRInCBV(row, releaseColumnKey));
    return [...list].sort((a, b) => {
      const na = String(a['Friendly name'] ?? a.friendlyName ?? a['Instance Name'] ?? a.instanceName ?? '').toLowerCase();
      const nb = String(b['Friendly name'] ?? b.friendlyName ?? b['Instance Name'] ?? b.instanceName ?? '').toLowerCase();
      return na.localeCompare(nb, undefined, { numeric: true });
    });
  }, [customerInstances, releaseColumnKey]);

  const productionAnalysisRegionOptions = useMemo(() => {
    const namedRegions = new Set();
    let hasOnPrem = false;
    for (const row of productionAnalysisSortedInstances) {
      const v = getRegionValue(row);
      if (isNamedRegion(v)) namedRegions.add(v);
      else hasOnPrem = true; // blank or non-z/non-df region → on-prem bucket
    }
    return { regions: Array.from(namedRegions).sort(), hasOnPrem };
  }, [productionAnalysisSortedInstances]);

  const effectiveProductionAnalysisRegionFilter = useMemo(() => {
    const f = productionAnalysisRegionFilter;
    if (f === 'all') return 'all';
    if (f === 'on-prem') return productionAnalysisRegionOptions.hasOnPrem ? 'on-prem' : 'all';
    if (productionAnalysisRegionOptions.regions.includes(f)) return f;
    return 'all';
  }, [productionAnalysisRegionFilter, productionAnalysisRegionOptions]);

  useEffect(() => {
    const f = productionAnalysisRegionFilter;
    if (f === 'all' || f === 'on-prem') return;
    if (!productionAnalysisRegionOptions.regions.includes(f)) {
      setProductionAnalysisRegionFilter('all');
    }
  }, [productionAnalysisRegionFilter, productionAnalysisRegionOptions]);

  const heapSourceInstances = useMemo(
    () => (tabValue === 2 ? productionAnalysisSortedInstances : sortedCustomerInstances),
    [tabValue, productionAnalysisSortedInstances, sortedCustomerInstances]
  );

  const paginatedCustomerInstances = useMemo(() => {
    const start = customerInstancesPage * CUSTOMER_INSTANCES_ROWS_PER_PAGE;
    return sortedCustomerInstances.slice(start, start + CUSTOMER_INSTANCES_ROWS_PER_PAGE);
  }, [sortedCustomerInstances, customerInstancesPage, CUSTOMER_INSTANCES_ROWS_PER_PAGE]);

  const heapMemoryChangesByStableKey = useMemo(() => {
    const map = {};
    for (const row of heapSourceInstances) {
      const stableKey = getMemoryRecommendationStableKey(row);
      map[stableKey] = computeMemoryUpgrades(row);
    }
    return map;
  }, [heapSourceInstances]);

  const heapRows = useMemo(() => {
    return heapSourceInstances.map((row) => {
      const stableKey = getMemoryRecommendationStableKey(row);
      return {
        row,
        stableKey,
        entry: heapEntriesByStableKey[stableKey],
        memoryChanges: heapMemoryChangesByStableKey[stableKey] || null
      };
    });
  }, [heapSourceInstances, heapEntriesByStableKey, heapMemoryChangesByStableKey]);

  const heapRefreshClientLabel = useMemo(() => {
    if (!refreshingHeapStableKey) return '';
    const row = heapSourceInstances.find(
      (r) => getMemoryRecommendationStableKey(r) === refreshingHeapStableKey
    );
    if (!row) return '';
    return (
      String(row['Friendly name'] ?? '').trim() ||
      String(row['Instance Name'] ?? row.instanceName ?? '').trim() ||
      ''
    );
  }, [refreshingHeapStableKey, heapSourceInstances]);

  useEffect(() => {
    if (!refreshingHeapStableKey) {
      setHeapRefreshPhaseIndex(0);
      return undefined;
    }
    setHeapRefreshPhaseIndex(0);
    const id = setInterval(() => {
      setHeapRefreshPhaseIndex((i) => (i + 1) % HEAP_DATADOG_REFRESH_PHASES.length);
    }, 2600);
    return () => clearInterval(id);
  }, [refreshingHeapStableKey]);

  const filteredHeapRows = useMemo(() => {
    const search = recommendationClientSearch.trim().toLowerCase();
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    const requiresFocus = (entry, memoryChanges) => {
      if (!entry) return false;
      // Critical days > 3 — use pre-computed summary value (same number shown in UI)
      // Avoids relying on dailyViolationTotals which may be absent on older entries
      if ((entry.summary?.criticalDaysTotal ?? 0) > 3) return true;
      // OOM events in last 7 days
      const oomLast7 = (Array.isArray(entry.oomSummary?.daily) ? entry.oomSummary.daily : [])
        .filter((d) => new Date(d.date).getTime() >= sevenDaysAgo)
        .reduce((acc, d) => acc + (Number(d.totalEvents) || 0), 0);
      if (oomLast7 > 0) return true;
      // Memory upgraded but instance is still critical or has OOM
      if ((memoryChanges?.upgrades ?? 0) > 0) {
        const level = entry.recommendation?.level;
        const hasOom = (entry.oomSummary?.totalEvents ?? 0) > 0;
        if (level === 'critical' || hasOom) return true;
      }
      return false;
    };

    return heapRows.filter(({ row, entry, memoryChanges }) => {
      if (!passesProductionAnalysisRegionFilter(effectiveProductionAnalysisRegionFilter, row)) return false;
      if (search) {
        const instanceName = String(row['Instance Name'] ?? row.instanceName ?? '').toLowerCase();
        const friendlyName = String(row['Friendly name'] ?? row.friendlyName ?? '').toLowerCase();
        if (!instanceName.includes(search) && !friendlyName.includes(search)) return false;
      }
      if (!passesProductionAnalysisHealthFilter(productionAnalysisHealthFilter, entry)) return false;
      if (heapFilterMemoryChange !== 'all') {
        const relevant = (memoryChanges?.changes || []).filter((c) => {
          if (heapFilterMemoryChange === 'upgraded') return c.direction === 'upgrade';
          if (heapFilterMemoryChange === 'downgraded') return c.direction === 'downgrade';
          return true;
        });
        if (relevant.length === 0) return false;
      }
      if (heapFilterRequireFocus && !requiresFocus(entry, memoryChanges)) return false;
      return true;
    });
  }, [
    heapRows,
    recommendationClientSearch,
    heapFilterMemoryChange,
    heapFilterRequireFocus,
    productionAnalysisHealthFilter,
    effectiveProductionAnalysisRegionFilter
  ]);

  const heapHealthSummary = useMemo(() => aggregateProductionAnalysisHealth(filteredHeapRows), [filteredHeapRows]);

  const paginatedHeapRows = useMemo(() => {
    const start = customerInstancesPage * CUSTOMER_INSTANCES_ROWS_PER_PAGE;
    return filteredHeapRows.slice(start, start + CUSTOMER_INSTANCES_ROWS_PER_PAGE);
  }, [filteredHeapRows, customerInstancesPage, CUSTOMER_INSTANCES_ROWS_PER_PAGE]);

  const dbConnRows = useMemo(
    () =>
      heapSourceInstances.map((row) => {
        const stableKey = getMemoryRecommendationStableKey(row);
        return { row, stableKey, entry: dbConnEntriesByStableKey[stableKey] };
      }),
    [heapSourceInstances, dbConnEntriesByStableKey]
  );

  const filteredDbConnRows = useMemo(() => {
    const search = dbConnClientSearch.trim().toLowerCase();
    const warnMinRaw = dbConnFilterWarnMin.trim();
    const critMinRaw = dbConnFilterCritMin.trim();
    const warnMin = warnMinRaw === '' ? null : parseInt(warnMinRaw, 10);
    const critMin = critMinRaw === '' ? null : parseInt(critMinRaw, 10);
    return dbConnRows.filter(({ row, stableKey, entry }) => {
      if (!passesProductionAnalysisSnowFilter(productionAnalysisSnowFilter, stableKey, productionAnalysisNotes, PA_ANALYSIS.DB)) {
        return false;
      }
      if (!passesProductionAnalysisRegionFilter(effectiveProductionAnalysisRegionFilter, row)) return false;
      if (search) {
        const instanceName = String(row['Instance Name'] ?? row.instanceName ?? '').toLowerCase();
        const friendlyName = String(row['Friendly name'] ?? row.friendlyName ?? '').toLowerCase();
        if (!instanceName.includes(search) && !friendlyName.includes(search)) return false;
      }
      const sum = entry?.summary;
      if (warnMin != null && Number.isFinite(warnMin)) {
        const w = sum?.warningDaysTotal;
        if (w == null || w < warnMin) return false;
      }
      if (critMin != null && Number.isFinite(critMin)) {
        const c = sum?.criticalDaysTotal;
        if (c == null || c < critMin) return false;
      }
      if (!passesProductionAnalysisHealthFilter(productionAnalysisHealthFilter, entry)) return false;
      return true;
    });
  }, [
    dbConnRows,
    dbConnClientSearch,
    dbConnFilterWarnMin,
    dbConnFilterCritMin,
    productionAnalysisSnowFilter,
    productionAnalysisHealthFilter,
    productionAnalysisNotes,
    effectiveProductionAnalysisRegionFilter
  ]);

  const dbConnHealthSummary = useMemo(() => aggregateProductionAnalysisHealth(filteredDbConnRows), [filteredDbConnRows]);
  const dbConnSnowSummary = useMemo(
    () => aggregateProductionAnalysisSnow(filteredDbConnRows, productionAnalysisNotes, PA_ANALYSIS.DB),
    [filteredDbConnRows, productionAnalysisNotes]
  );

  const paginatedDbConnRows = useMemo(() => {
    const start = dbConnPage * CUSTOMER_INSTANCES_ROWS_PER_PAGE;
    return filteredDbConnRows.slice(start, start + CUSTOMER_INSTANCES_ROWS_PER_PAGE);
  }, [filteredDbConnRows, dbConnPage, CUSTOMER_INSTANCES_ROWS_PER_PAGE]);

  const dbConnBulkProgressLabel = useMemo(() => {
    const bp = dbConnBulkProgress;
    if (!bp || !Array.isArray(bp.keys) || bp.keys.length === 0) return null;
    const total = bp.keys.length;
    const done = typeof bp.nextIndex === 'number' ? Math.min(bp.nextIndex, total) : 0;
    return { done, total };
  }, [dbConnBulkProgress]);

  const threadRows = useMemo(
    () =>
      heapSourceInstances.map((row) => {
        const stableKey = getMemoryRecommendationStableKey(row);
        return { row, stableKey, entry: threadEntriesByStableKey[stableKey] };
      }),
    [heapSourceInstances, threadEntriesByStableKey]
  );

  const filteredThreadRows = useMemo(() => {
    const search = threadClientSearch.trim().toLowerCase();
    const warnMinRaw = threadFilterWarnMin.trim();
    const critMinRaw = threadFilterCritMin.trim();
    const warnMin = warnMinRaw === '' ? null : parseInt(warnMinRaw, 10);
    const critMin = critMinRaw === '' ? null : parseInt(critMinRaw, 10);
    return threadRows.filter(({ row, stableKey, entry }) => {
      if (!passesProductionAnalysisSnowFilter(productionAnalysisSnowFilter, stableKey, productionAnalysisNotes, PA_ANALYSIS.THREADS)) {
        return false;
      }
      if (!passesProductionAnalysisRegionFilter(effectiveProductionAnalysisRegionFilter, row)) return false;
      if (search) {
        const instanceName = String(row['Instance Name'] ?? row.instanceName ?? '').toLowerCase();
        const friendlyName = String(row['Friendly name'] ?? row.friendlyName ?? '').toLowerCase();
        if (!instanceName.includes(search) && !friendlyName.includes(search)) return false;
      }
      const sum = entry?.summary;
      if (warnMin != null && Number.isFinite(warnMin)) {
        const w = sum?.warningDaysTotal;
        if (w == null || w < warnMin) return false;
      }
      if (critMin != null && Number.isFinite(critMin)) {
        const c = sum?.criticalDaysTotal;
        if (c == null || c < critMin) return false;
      }
      if (!passesProductionAnalysisHealthFilter(productionAnalysisHealthFilter, entry)) return false;
      return true;
    });
  }, [
    threadRows,
    threadClientSearch,
    threadFilterWarnMin,
    threadFilterCritMin,
    productionAnalysisSnowFilter,
    productionAnalysisHealthFilter,
    productionAnalysisNotes,
    effectiveProductionAnalysisRegionFilter
  ]);

  const threadHealthSummary = useMemo(() => aggregateProductionAnalysisHealth(filteredThreadRows), [filteredThreadRows]);
  const threadSnowSummary = useMemo(
    () => aggregateProductionAnalysisSnow(filteredThreadRows, productionAnalysisNotes, PA_ANALYSIS.THREADS),
    [filteredThreadRows, productionAnalysisNotes]
  );

  const paSearchResults = useMemo(() => {
    if (!paSearchQuery.trim()) return [];
    const q = paSearchQuery.toLowerCase().trim();
    const keys = new Set([
      ...Object.keys(heapEntriesByStableKey),
      ...Object.keys(dbConnEntriesByStableKey),
      ...Object.keys(threadEntriesByStableKey),
    ]);
    const results = [];
    for (const key of keys) {
      const heapEntry = heapEntriesByStableKey[key];
      const dbEntry = dbConnEntriesByStableKey[key];
      const threadEntry = threadEntriesByStableKey[key];
      const entry = heapEntry || dbEntry || threadEntry;
      if (!entry) continue;
      const name = (entry.friendlyName || entry.instanceName || key).toLowerCase();
      const instName = (entry.instanceName || '').toLowerCase();
      if (name.includes(q) || instName.includes(q)) {
        results.push({ stableKey: key, friendlyName: entry.friendlyName || entry.instanceName || key, instanceName: entry.instanceName, region: entry.region, heapEntry, dbEntry, threadEntry });
      }
    }
    return results.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));
  }, [paSearchQuery, heapEntriesByStableKey, dbConnEntriesByStableKey, threadEntriesByStableKey]);

  useEffect(() => {
    if (paSearchResults.length === 1) setPaSearchSelectedKey(paSearchResults[0].stableKey);
    else if (paSearchResults.length === 0) setPaSearchSelectedKey(null);
    else setPaSearchSelectedKey((prev) => (paSearchResults.some(r => r.stableKey === prev) ? prev : null));
  }, [paSearchResults]);

  useEffect(() => {
    if (productionAnalysisTab !== 0 || !paSearchSelectedKey) {
      setPaSearchTransitions({ memory: [], threads: [], db: [] });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setPaSearchTransitionsLoading(true);
      try {
        const [mem, thr, db] = await Promise.all([
          getRecommendationLevelTransitions('memory', 30),
          getRecommendationLevelTransitions('threads', 30),
          getRecommendationLevelTransitions('db', 30),
        ]);
        if (!cancelled) {
          setPaSearchTransitions({
            memory: (mem?.transitions || []).filter(t => t.stableKey === paSearchSelectedKey),
            threads: (thr?.transitions || []).filter(t => t.stableKey === paSearchSelectedKey),
            db: (db?.transitions || []).filter(t => t.stableKey === paSearchSelectedKey),
          });
        }
      } catch {
        if (!cancelled) setPaSearchTransitions({ memory: [], threads: [], db: [] });
      } finally {
        if (!cancelled) setPaSearchTransitionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productionAnalysisTab, paSearchSelectedKey]);

  const handleRefreshProductionAnalysisSnow = useCallback(async () => {
    let filtered;
    let analysisType;
    if (productionAnalysisTab === 1) {
      filtered = filteredHeapRows;
      analysisType = PA_ANALYSIS.MEMORY;
    } else if (productionAnalysisTab === 2) {
      filtered = filteredThreadRows;
      analysisType = PA_ANALYSIS.THREADS;
    } else {
      filtered = filteredDbConnRows;
      analysisType = PA_ANALYSIS.DB;
    }
    const stableKeys = filtered.map(({ stableKey }) => stableKey);
    if (!stableKeys.length) return;
    setRefreshingPaSnow(true);
    setError(null);
    try {
      const res = await refreshProductionAnalysisSnow(stableKeys, analysisType);
      if (res?.success && res.notes && typeof res.notes === 'object') {
        setProductionAnalysisNotes((prev) => ({ ...prev, ...res.notes }));
      }
      await loadProductionAnalysisNotes();
    } catch (err) {
      setError(err.message || 'Refresh SNOW status failed (check login and ServiceNow configuration).');
    } finally {
      setRefreshingPaSnow(false);
    }
  }, [
    productionAnalysisTab,
    filteredHeapRows,
    filteredThreadRows,
    filteredDbConnRows,
    loadProductionAnalysisNotes
  ]);

  /** Sync ServiceNow raised/executed for one or more instances on a given analysis slice (silent on failure). */
  const refreshProductionAnalysisSnowForStableKeys = useCallback(async (stableKeys, analysisType) => {
    if (!stableKeys?.length) return;
    try {
      const res = await refreshProductionAnalysisSnow(stableKeys, analysisType);
      if (res?.success && res.notes && typeof res.notes === 'object') {
        setProductionAnalysisNotes((prev) => ({ ...prev, ...res.notes }));
      }
      await loadProductionAnalysisNotes();
    } catch (err) {
      console.warn('SNOW status refresh failed:', err?.message || err);
    }
  }, [loadProductionAnalysisNotes]);

  const paginatedThreadRows = useMemo(() => {
    const start = threadPage * CUSTOMER_INSTANCES_ROWS_PER_PAGE;
    return filteredThreadRows.slice(start, start + CUSTOMER_INSTANCES_ROWS_PER_PAGE);
  }, [filteredThreadRows, threadPage, CUSTOMER_INSTANCES_ROWS_PER_PAGE]);

  const heapPageStableKeys = useMemo(() => paginatedHeapRows.map((r) => r.stableKey), [paginatedHeapRows]);
  const heapPageAllSelected = useMemo(
    () => heapPageStableKeys.length > 0 && heapPageStableKeys.every((k) => paRowSelection.memory[k]),
    [heapPageStableKeys, paRowSelection.memory]
  );
  const heapPageIndeterminate = useMemo(
    () => heapPageStableKeys.some((k) => paRowSelection.memory[k]) && !heapPageAllSelected,
    [heapPageStableKeys, paRowSelection.memory, heapPageAllSelected]
  );
  const heapSelectionCount = useMemo(() => Object.keys(paRowSelection.memory).length, [paRowSelection.memory]);

  const threadPageStableKeys = useMemo(() => paginatedThreadRows.map((r) => r.stableKey), [paginatedThreadRows]);
  const threadPageAllSelected = useMemo(
    () => threadPageStableKeys.length > 0 && threadPageStableKeys.every((k) => paRowSelection.threads[k]),
    [threadPageStableKeys, paRowSelection.threads]
  );
  const threadPageIndeterminate = useMemo(
    () => threadPageStableKeys.some((k) => paRowSelection.threads[k]) && !threadPageAllSelected,
    [threadPageStableKeys, paRowSelection.threads, threadPageAllSelected]
  );
  const threadSelectionCount = useMemo(() => Object.keys(paRowSelection.threads).length, [paRowSelection.threads]);

  const dbPageStableKeys = useMemo(() => paginatedDbConnRows.map((r) => r.stableKey), [paginatedDbConnRows]);
  const dbPageAllSelected = useMemo(
    () => dbPageStableKeys.length > 0 && dbPageStableKeys.every((k) => paRowSelection.db[k]),
    [dbPageStableKeys, paRowSelection.db]
  );
  const dbPageIndeterminate = useMemo(
    () => dbPageStableKeys.some((k) => paRowSelection.db[k]) && !dbPageAllSelected,
    [dbPageStableKeys, paRowSelection.db, dbPageAllSelected]
  );
  const dbSelectionCount = useMemo(() => Object.keys(paRowSelection.db).length, [paRowSelection.db]);

  const selectAllFilteredHeap = useCallback(() => {
    setPaRowSelection((prev) => {
      const next = { ...prev, memory: { ...prev.memory } };
      for (const { stableKey } of filteredHeapRows) {
        next.memory[stableKey] = true;
      }
      return next;
    });
  }, [filteredHeapRows]);

  const clearHeapSelection = useCallback(() => {
    setPaRowSelection((prev) => ({ ...prev, memory: {} }));
  }, []);

  const toggleHeapPageSelection = useCallback(() => {
    setPaRowSelection((prev) => {
      const next = { ...prev, memory: { ...prev.memory } };
      const allOn = heapPageStableKeys.length > 0 && heapPageStableKeys.every((k) => next.memory[k]);
      if (allOn) {
        for (const k of heapPageStableKeys) delete next.memory[k];
      } else {
        for (const k of heapPageStableKeys) next.memory[k] = true;
      }
      return next;
    });
  }, [heapPageStableKeys]);

  const selectAllFilteredThread = useCallback(() => {
    setPaRowSelection((prev) => {
      const next = { ...prev, threads: { ...prev.threads } };
      for (const { stableKey } of filteredThreadRows) {
        next.threads[stableKey] = true;
      }
      return next;
    });
  }, [filteredThreadRows]);

  const clearThreadSelection = useCallback(() => {
    setPaRowSelection((prev) => ({ ...prev, threads: {} }));
  }, []);

  const toggleThreadPageSelection = useCallback(() => {
    setPaRowSelection((prev) => {
      const next = { ...prev, threads: { ...prev.threads } };
      const allOn = threadPageStableKeys.length > 0 && threadPageStableKeys.every((k) => next.threads[k]);
      if (allOn) {
        for (const k of threadPageStableKeys) delete next.threads[k];
      } else {
        for (const k of threadPageStableKeys) next.threads[k] = true;
      }
      return next;
    });
  }, [threadPageStableKeys]);

  const selectAllFilteredDb = useCallback(() => {
    setPaRowSelection((prev) => {
      const next = { ...prev, db: { ...prev.db } };
      for (const { stableKey } of filteredDbConnRows) {
        next.db[stableKey] = true;
      }
      return next;
    });
  }, [filteredDbConnRows]);

  const clearDbSelection = useCallback(() => {
    setPaRowSelection((prev) => ({ ...prev, db: {} }));
  }, []);

  const toggleDbPageSelection = useCallback(() => {
    setPaRowSelection((prev) => {
      const next = { ...prev, db: { ...prev.db } };
      const allOn = dbPageStableKeys.length > 0 && dbPageStableKeys.every((k) => next.db[k]);
      if (allOn) {
        for (const k of dbPageStableKeys) delete next.db[k];
      } else {
        for (const k of dbPageStableKeys) next.db[k] = true;
      }
      return next;
    });
  }, [dbPageStableKeys]);

  const threadBulkProgressLabel = useMemo(() => {
    const bp = threadBulkProgress;
    if (!bp || !Array.isArray(bp.keys) || bp.keys.length === 0) return null;
    const total = bp.keys.length;
    const done = typeof bp.nextIndex === 'number' ? Math.min(bp.nextIndex, total) : 0;
    return { done, total };
  }, [threadBulkProgress]);

  const exportHeapTableCsv = useCallback(() => {
    const rows = filteredHeapRows;
    if (!rows.length) return;
    const headers = [
      'Instance Name',
      'Friendly Name',
      'URL',
      'Region',
      'Status',
      'Threshold %',
      'Max Memory (MB)',
      'Current (GiB)',
      'Proposed (GiB)',
      'Max events / day',
      'Total events',
      'Warn days',
      'Crit days',
      'OOM events',
      'Memory changes (count)',
      'Memory upgrades',
      'Memory downgrades',
      'Last memory change (UTC)',
      'Last memory change (MB)',
      'Last memory change (GiB)',
      'Last memory change direction',
      'Recommendation',
      'Details',
      'Datadog log query',
      'OOM Datadog log query',
      'Computed at (UTC)',
      'Error'
    ];
    const lines = [headers.map(csvEscapeCell).join(',')];
    for (const { row, entry, memoryChanges } of rows) {
      const sum = entry?.summary;
      const maxMbRaw = row['Max Memory'];
      const maxMb =
        maxMbRaw != null && String(maxMbRaw).trim() !== ''
          ? Number(String(maxMbRaw).replace(/,/g, ''))
          : '';
      const inst = String(row['Instance Name'] ?? row.instanceName ?? '').trim();
      const friendly = String(row['Friendly name'] ?? row.friendlyName ?? '').trim();
      const url = clientInstanceUrl(row) ?? '';
      const region = getRegionValue(row) ?? '';
      const status = formatProductionAnalysisLevelLabel(getProductionAnalysisLevel(entry));
      const thr = entry?.freePctThreshold != null ? String(entry.freePctThreshold) : '';
      const cur = entry?.currentMemoryGiB != null ? Number(entry.currentMemoryGiB).toFixed(2) : '';
      const prop = entry?.proposedMemoryGiB != null ? Number(entry.proposedMemoryGiB).toFixed(2) : '';
      const maxDay = sum?.maxDailyCount != null ? String(sum.maxDailyCount) : '';
      const total = sum?.violationEventsTotal != null ? String(sum.violationEventsTotal) : '';
      const wd = sum?.warningDaysTotal != null ? String(sum.warningDaysTotal) : '';
      const cd = sum?.criticalDaysTotal != null ? String(sum.criticalDaysTotal) : '';
      const oomEv =
        entry?.oomSummary?.error != null
          ? `Error: ${entry.oomSummary.error}`
          : entry?.oomSummary?.totalEvents != null
            ? String(entry.oomSummary.totalEvents)
            : '';
      const memCount = memoryChanges?.count ?? 0;
      const memUp = memoryChanges?.upgrades ?? 0;
      const memDown = memoryChanges?.downgrades ?? 0;
      const lastChange = memoryChanges?.last;
      const lastChangeDate =
        lastChange?.recordedAt != null ? format(new Date(lastChange.recordedAt), 'yyyy-MM-dd HH:mm:ss') : '';
      const lastChangeMb =
        lastChange ? `${lastChange.fromMb} → ${lastChange.toMb}` : '';
      const lastChangeGib =
        lastChange && mbToGiBDisplay(lastChange.fromMb) != null && mbToGiBDisplay(lastChange.toMb) != null
          ? `${mbToGiBDisplay(lastChange.fromMb)} → ${mbToGiBDisplay(lastChange.toMb)}`
          : '';
      const lastChangeDir = lastChange?.direction ?? '';
      const title = entry?.recommendation?.title ?? '';
      const details = entry?.recommendation?.details ?? '';
      const logQuery = entry?.logQuery ?? '';
      const oomLogQuery = entry?.oomLogQuery ?? '';
      const computedAt =
        entry?.computedAt != null ? format(new Date(entry.computedAt), 'yyyy-MM-dd HH:mm:ss') : '';
      const err = entry?.error != null ? String(entry.error) : '';
      const vals = [
        inst,
        friendly,
        url,
        region,
        status,
        thr,
        maxMb === '' ? '' : String(maxMb),
        cur,
        prop,
        maxDay,
        total,
        wd,
        cd,
        oomEv,
        String(memCount),
        String(memUp),
        String(memDown),
        lastChangeDate,
        lastChangeMb,
        lastChangeGib,
        lastChangeDir,
        title,
        details,
        logQuery,
        oomLogQuery,
        computedAt,
        err
      ];
      lines.push(vals.map(csvEscapeCell).join(','));
    }
    const csv = `\uFEFF${lines.join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `heap-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [filteredHeapRows]);

  const exportThreadTableCsv = useCallback(() => {
    const rows = filteredThreadRows;
    if (!rows.length) return;
    const headers = [
      'Instance Name',
      'Friendly Name',
      'URL',
      'Region',
      'Status',
      'Threshold %',
      'Max Threads',
      'Active threads threshold',
      'Current max threads',
      'Proposed max threads',
      'Max events / day',
      'Total events',
      'Warn days',
      'Crit days',
      'Recommendation',
      'Details',
      'Datadog log query',
      'Computed at (UTC)',
      'Error'
    ];
    const lines = [headers.map(csvEscapeCell).join(',')];
    for (const { row, entry } of rows) {
      const sum = entry?.summary;
      const maxThrRaw = row['Max Threads'];
      const maxThr =
        maxThrRaw != null && String(maxThrRaw).trim() !== ''
          ? Number(String(maxThrRaw).replace(/,/g, ''))
          : '';
      const inst = String(row['Instance Name'] ?? row.instanceName ?? '').trim();
      const friendly = String(row['Friendly name'] ?? row.friendlyName ?? '').trim();
      const url = clientInstanceUrl(row) ?? '';
      const region = getRegionValue(row) ?? '';
      const status = formatProductionAnalysisLevelLabel(getProductionAnalysisLevel(entry));
      const thrPct = entry?.pctOfMax != null ? String(entry.pctOfMax) : '';
      const atThr = entry?.thresholds?.atThreshold != null ? String(entry.thresholds.atThreshold) : '';
      const curT = entry?.currentMaxThreads != null ? String(entry.currentMaxThreads) : '';
      const propT = entry?.proposedMaxThreads != null ? String(entry.proposedMaxThreads) : '';
      const maxDay = sum?.maxDailyCount != null ? String(sum.maxDailyCount) : '';
      const total = sum?.violationEventsTotal != null ? String(sum.violationEventsTotal) : '';
      const wd = sum?.warningDaysTotal != null ? String(sum.warningDaysTotal) : '';
      const cd = sum?.criticalDaysTotal != null ? String(sum.criticalDaysTotal) : '';
      const title = entry?.recommendation?.title ?? '';
      const details = entry?.recommendation?.details ?? '';
      const logQuery = entry?.logQuery ?? '';
      const computedAt =
        entry?.computedAt != null ? format(new Date(entry.computedAt), 'yyyy-MM-dd HH:mm:ss') : '';
      const err = entry?.error != null ? String(entry.error) : '';
      const vals = [
        inst,
        friendly,
        url,
        region,
        status,
        thrPct,
        maxThr === '' ? '' : String(maxThr),
        atThr,
        curT,
        propT,
        maxDay,
        total,
        wd,
        cd,
        title,
        details,
        logQuery,
        computedAt,
        err
      ];
      lines.push(vals.map(csvEscapeCell).join(','));
    }
    const csv = `\uFEFF${lines.join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `thread-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [filteredThreadRows]);

  const exportDbConnTableCsv = useCallback(() => {
    const rows = filteredDbConnRows;
    if (!rows.length) return;
    const headers = [
      'Instance Name',
      'Friendly Name',
      'URL',
      'Region',
      'Status',
      'Threshold %',
      'Max DB Connections',
      'Active DB connections threshold',
      'Current max DB connections',
      'Proposed max DB connections',
      'Max events / day',
      'Total events',
      'Warn days',
      'Crit days',
      'Recommendation',
      'Details',
      'Datadog log query',
      'Computed at (UTC)',
      'Error'
    ];
    const lines = [headers.map(csvEscapeCell).join(',')];
    for (const { row, entry } of rows) {
      const sum = entry?.summary;
      const maxDbRaw = row['Max DB Connections'];
      const maxDb =
        maxDbRaw != null && String(maxDbRaw).trim() !== ''
          ? Number(String(maxDbRaw).replace(/,/g, ''))
          : '';
      const inst = String(row['Instance Name'] ?? row.instanceName ?? '').trim();
      const friendly = String(row['Friendly name'] ?? row.friendlyName ?? '').trim();
      const url = clientInstanceUrl(row) ?? '';
      const region = getRegionValue(row) ?? '';
      const status = formatProductionAnalysisLevelLabel(getProductionAnalysisLevel(entry));
      const thrPct = entry?.pctOfMax != null ? String(entry.pctOfMax) : '';
      const cadbcThr = entry?.thresholds?.cadbcThreshold != null ? String(entry.thresholds.cadbcThreshold) : '';
      const curDb = entry?.currentMaxDb != null ? String(entry.currentMaxDb) : '';
      const propDb = entry?.proposedMaxDb != null ? String(entry.proposedMaxDb) : '';
      const maxDay = sum?.maxDailyCount != null ? String(sum.maxDailyCount) : '';
      const total = sum?.violationEventsTotal != null ? String(sum.violationEventsTotal) : '';
      const wd = sum?.warningDaysTotal != null ? String(sum.warningDaysTotal) : '';
      const cd = sum?.criticalDaysTotal != null ? String(sum.criticalDaysTotal) : '';
      const title = entry?.recommendation?.title ?? '';
      const details = entry?.recommendation?.details ?? '';
      const logQuery = entry?.logQuery ?? '';
      const computedAt =
        entry?.computedAt != null ? format(new Date(entry.computedAt), 'yyyy-MM-dd HH:mm:ss') : '';
      const err = entry?.error != null ? String(entry.error) : '';
      const vals = [
        inst,
        friendly,
        url,
        region,
        status,
        thrPct,
        maxDb === '' ? '' : String(maxDb),
        cadbcThr,
        curDb,
        propDb,
        maxDay,
        total,
        wd,
        cd,
        title,
        details,
        logQuery,
        computedAt,
        err
      ];
      lines.push(vals.map(csvEscapeCell).join(','));
    }
    const csv = `\uFEFF${lines.join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `db-connection-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [filteredDbConnRows]);


  const heapBulkProgressLabel = useMemo(() => {
    const bp = heapBulkProgress;
    if (!bp || !Array.isArray(bp.keys) || !bp.keys.length) return null;
    const total = bp.keys.length;
    const done = Math.min(Number(bp.nextIndex) || 0, total);
    return { done, total };
  }, [heapBulkProgress]);

  useEffect(() => {
    setCustomerInstancesPage(0);
  }, [customerSearch, filterEnv, filterGroup, filterVersion, excludeNR, customerInstancesViewTab, infraColumnFilters]);

  useEffect(() => {
    if (customerInstancesViewTab !== 1) setInfraColumnFilters({});
  }, [customerInstancesViewTab]);

  useEffect(() => {
    setCustomerInstancesPage(0);
  }, [recommendationClientSearch]);

  useEffect(() => {
    setCustomerInstancesPage(0);
  }, [heapFilterMemoryChange, heapFilterRequireFocus]);

  useEffect(() => {
    setCustomerInstancesPage(0);
    setThreadPage(0);
    setDbConnPage(0);
  }, [productionAnalysisHealthFilter]);

  const chartDataByRegion = useMemo(() => {
    const countBy = {};
    filteredCustomerInstances.forEach((row) => {
      const v = getRegionValue(row);
      const key = isNamedRegion(v) ? v : 'On-Prem';
      countBy[key] = (countBy[key] || 0) + 1;
    });
    return Object.entries(countBy).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredCustomerInstances]);

  const chartDataByGroup = useMemo(() => {
    const countBy = {};
    filteredCustomerInstances.forEach((row) => {
      const v = getGroupValue(row) || '(blank)';
      countBy[v] = (countBy[v] || 0) + 1;
    });
    return Object.entries(countBy).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredCustomerInstances]);

  const chartDataByRelease = useMemo(() => {
    if (!releaseColumnKey) return [];
    const countBy = {};
    filteredCustomerInstances.forEach((row) => {
      const v = String(row[releaseColumnKey] ?? '').trim() || '(blank)';
      countBy[v] = (countBy[v] || 0) + 1;
    });
    return Object.entries(countBy).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredCustomerInstances, releaseColumnKey]);

  const instanceStats = useMemo(() => {
    const total = filteredCustomerInstances.length;
    if (total === 0) {
      return { total: 0, ciamEnabled: 0, modernCount: 0, legacyCount: 0, wijmoAsp: 0, wijmoSystem: 0 };
    }
    let ciamEnabled = 0;
    let modernCount = 0;
    let legacyCount = 0;
    let wijmoAsp = 0;
    let wijmoSystem = 0;
    filteredCustomerInstances.forEach((row) => {
      const ciam = row['CIAM'];
      if (ciam === true || ciam === 'true') ciamEnabled += 1;
      const type = String(row['Instance'] ?? row['Instance Type'] ?? '').trim();
      if (type.toLowerCase() === 'modern') modernCount += 1;
      else if (type.toLowerCase() === 'legacy') legacyCount += 1;
      const asp = row['Wijmo(ASP)'];
      if (asp === true || asp === 'true') wijmoAsp += 1;
      const sys = row['Wijmo(System)'];
      if (sys === true || sys === 'true') wijmoSystem += 1;
    });
    return { total, ciamEnabled, modernCount, legacyCount, wijmoAsp, wijmoSystem };
  }, [filteredCustomerInstances]);

  const customersForDialog = useMemo(() => {
    if (!customersDialogGroup) return [];
    const groups = DEPLOYMENT_GROUP_MAP[customersDialogGroup];
    if (!groups) return customerInstances;
    return customerInstances.filter((row) => {
      const g = getGroupValue(row);
      return groups.some((gr) => g.toLowerCase() === gr.toLowerCase());
    });
  }, [customerInstances, customersDialogGroup]);

  const monthStart = startOfMonth(new Date(year, monthIndex, 1));
  const monthEnd = endOfMonth(monthStart);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const firstDayOfWeek = getDay(monthStart);
  const leadingBlanks = Array(firstDayOfWeek).fill(null);
  const monthName = MONTH_NAMES[monthIndex];

  const handleCSVUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = ev.target?.result ?? '';
        const { rows, columns } = parseCSVWithHeaders(text);
        setError(null);
        setCustomerInstancesSaving(true);
        const saved = await saveCustomerInstances({ columns, rows });
        setCustomerColumns(saved.columns);
        setCustomerInstances(saved.rows);
      } catch (err) {
        setError(err.message?.includes('Invalid') ? err.message : 'Failed to save CSV: ' + (err.message || 'please try again'));
      } finally {
        setCustomerInstancesSaving(false);
      }
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };


  const handleRefreshCBV = async () => {
    setRefreshingCBV(true);
    setError(null);
    try {
      const { columns: cols, rows: updatedRows } = await refreshCustomerInstancesCBV();
      setCustomerColumns(cols);
      setCustomerInstances(updatedRows);
    } catch (err) {
      setError('Refresh failed: ' + (err.message || 'please try again'));
    } finally {
      setRefreshingCBV(false);
    }
  };

  const handleRefreshCBVFailed = async () => {
    setRefreshingCBV(true);
    setError(null);
    try {
      const { columns: cols, rows: updatedRows } = await refreshCustomerInstancesCBV({ onlyFailed: true });
      setCustomerColumns(cols);
      setCustomerInstances(updatedRows);
    } catch (err) {
      setError('Refresh failed: ' + (err.message || 'please try again'));
    } finally {
      setRefreshingCBV(false);
    }
  };

  const handleRefreshMSV = async () => {
    setRefreshingMSV(true);
    setError(null);
    try {
      const { columns: cols, rows: updatedRows } = await refreshCustomerInstancesMSV();
      setCustomerColumns(cols);
      setCustomerInstances(updatedRows);
    } catch (err) {
      setError('Refresh MSV failed: ' + (err.message || 'please try again'));
    } finally {
      setRefreshingMSV(false);
    }
  };

  const handleRefreshMSVFailed = async () => {
    setRefreshingMSV(true);
    setError(null);
    try {
      const { columns: cols, rows: updatedRows } = await refreshCustomerInstancesMSV({ onlyFailed: true });
      setCustomerColumns(cols);
      setCustomerInstances(updatedRows);
    } catch (err) {
      setError('Refresh MSV failed: ' + (err.message || 'please try again'));
    } finally {
      setRefreshingMSV(false);
    }
  };

  const handleRefreshAzure = async () => {
    setRefreshingAzure(true);
    setError(null);
    try {
      const { columns: cols, rows: updatedRows } = await refreshCustomerInstancesAzure();
      setCustomerColumns(cols);
      setCustomerInstances(updatedRows);
    } catch (err) {
      setError('Refresh Azure failed: ' + (err.message || 'please try again'));
    } finally {
      setRefreshingAzure(false);
    }
  };

  const handleRefreshAzureFailed = async () => {
    setRefreshingAzure(true);
    setError(null);
    try {
      const { columns: cols, rows: updatedRows } = await refreshCustomerInstancesAzure({ onlyFailed: true });
      setCustomerColumns(cols);
      setCustomerInstances(updatedRows);
    } catch (err) {
      setError('Refresh Azure failed: ' + (err.message || 'please try again'));
    } finally {
      setRefreshingAzure(false);
    }
  };

  const refreshHeapForRow = async (row) => {
    const sk = getMemoryRecommendationStableKey(row);
    const inst = String(row['Instance Name'] ?? row.instanceName ?? '').trim();
    if (!inst) return;
    setRefreshingHeapStableKey(sk);
    setError(null);
    try {
      const payloadRow = buildHeapRecommendationRowPayload(row);
      const res = await refreshHeapRecommendationOne({
        row: payloadRow,
        days: HEAP_RECOMMENDATION_DAYS,
        freePctThreshold: heapFreePctThreshold
      });
      if (res?.success && res?.data) {
        const doc = res.data;
        const mergeKey = doc.stableKey || sk;
        setHeapEntriesByStableKey((prev) => ({ ...prev, [mergeKey]: doc }));
        const q = doc.logQuery || '';
        const sum = doc.summary || {};
        console.log('[Heap recommendation] Datadog query:', q);
        console.log(
          '[Heap recommendation] Time range (UTC):',
          doc.from,
          '→',
          doc.to,
          '| logsProcessed:',
          sum.logsProcessed,
          '| violationEvents:',
          sum.violationEventsTotal
        );
        setHeapLastQueryDebug({
          query: q,
          oomQuery: doc.oomLogQuery || '',
          oomEventTotal: doc.oomSummary?.totalEvents,
          from: doc.from,
          to: doc.to,
          instanceName: doc.instanceName,
          logsProcessed: sum.logsProcessed,
          violationEventsTotal: sum.violationEventsTotal,
          isError: false
        });
      } else {
        setError(res?.message || 'Heap refresh did not return data (check login and Datadog configuration).');
      }
    } catch (err) {
      console.error('Heap refresh failed:', err);
      setError(err.message || 'Heap refresh failed');
      const d = err.details;
      if (d?.logQuery) {
        console.log('[Heap recommendation] Datadog query (from error response):', d.logQuery);
        setHeapLastQueryDebug({
          query: d.logQuery,
          from: null,
          to: null,
          instanceName: null,
          logsProcessed: null,
          violationEventsTotal: null,
          isError: true
        });
      }
    } finally {
      setRefreshingHeapStableKey(null);
      await loadHeapRecommendations({ silent: true });
      await refreshProductionAnalysisSnowForStableKeys([sk], PA_ANALYSIS.MEMORY);
    }
  };

  const startHeapRefreshAll = async (resume = false) => {
    const sourceRows = filteredHeapRows.map(({ row }) => row);
    if (!sourceRows.length) return;
    setRefreshingHeapAll(true);
    setError(null);
    try {
      const rowsPayload = sourceRows.map((r) => buildHeapRecommendationRowPayload(r));
      await refreshHeapRecommendationAll({
        rows: rowsPayload,
        days: HEAP_RECOMMENDATION_DAYS,
        resume,
        freePctThreshold: heapFreePctThreshold
      });
      await loadHeapRecommendations({ silent: true });
    } catch (err) {
      console.error('Heap bulk refresh failed:', err);
      setError(err.message || 'Heap bulk refresh failed to start');
    } finally {
      setRefreshingHeapAll(false);
    }
  };

  const refreshDbConnForRow = async (row) => {
    const sk = getMemoryRecommendationStableKey(row);
    const inst = String(row['Instance Name'] ?? row.instanceName ?? '').trim();
    if (!inst) return;
    setRefreshingDbConnStableKey(sk);
    setError(null);
    try {
      const payloadRow = buildDbConnectionRecommendationRowPayload(row);
      const res = await refreshDbConnectionRecommendationOne({
        row: payloadRow,
        days: DB_CONNECTION_RECOMMENDATION_DAYS,
        pctOfMax: dbConnPctOfMax
      });
      if (res?.success && res?.data) {
        const doc = res.data;
        const mergeKey = doc.stableKey || sk;
        setDbConnEntriesByStableKey((prev) => ({ ...prev, [mergeKey]: doc }));
        const q = doc.logQuery || '';
        const sum = doc.summary || {};
        setDbConnLastQueryDebug({
          query: q,
          from: doc.from,
          to: doc.to,
          instanceName: doc.instanceName,
          logsProcessed: sum.logsProcessed,
          violationEventsTotal: sum.violationEventsTotal,
          isError: false
        });
      } else {
        setError(res?.message || 'DB connection refresh did not return data (check login and Datadog configuration).');
      }
    } catch (err) {
      console.error('DB connection refresh failed:', err);
      setError(err.message || 'DB connection refresh failed');
      const d = err.details;
      const logQuery = d?.logQuery;
      if (logQuery) {
        setDbConnLastQueryDebug({
          query: logQuery,
          from: null,
          to: null,
          instanceName: null,
          logsProcessed: null,
          violationEventsTotal: null,
          isError: true
        });
      }
    } finally {
      setRefreshingDbConnStableKey(null);
      await loadDbConnectionRecommendations({ silent: true });
      await refreshProductionAnalysisSnowForStableKeys([sk], PA_ANALYSIS.DB);
    }
  };

  const startDbConnRefreshAll = async (resume = false) => {
    const sourceRows = filteredDbConnRows.map(({ row }) => row);
    if (!sourceRows.length) return;
    setRefreshingDbConnAll(true);
    setError(null);
    try {
      const rowsPayload = sourceRows.map((r) => buildDbConnectionRecommendationRowPayload(r));
      await refreshDbConnectionRecommendationAll({
        rows: rowsPayload,
        days: DB_CONNECTION_RECOMMENDATION_DAYS,
        resume,
        pctOfMax: dbConnPctOfMax
      });
      await loadDbConnectionRecommendations({ silent: true });
    } catch (err) {
      console.error('DB connection bulk refresh failed:', err);
      setError(err.message || 'DB connection bulk refresh failed to start');
    } finally {
      setRefreshingDbConnAll(false);
    }
  };

  const refreshThreadForRow = async (row) => {
    const sk = getMemoryRecommendationStableKey(row);
    const inst = String(row['Instance Name'] ?? row.instanceName ?? '').trim();
    if (!inst) return;
    setRefreshingThreadStableKey(sk);
    setError(null);
    try {
      const payloadRow = buildThreadRecommendationRowPayload(row);
      const res = await refreshThreadRecommendationOne({
        row: payloadRow,
        days: THREAD_RECOMMENDATION_DAYS,
        pctOfMax: threadPctOfMax
      });
      if (res?.success && res?.data) {
        const doc = res.data;
        const mergeKey = doc.stableKey || sk;
        setThreadEntriesByStableKey((prev) => ({ ...prev, [mergeKey]: doc }));
        const q = doc.logQuery || '';
        const sum = doc.summary || {};
        setThreadLastQueryDebug({
          query: q,
          from: doc.from,
          to: doc.to,
          instanceName: doc.instanceName,
          logsProcessed: sum.logsProcessed,
          violationEventsTotal: sum.violationEventsTotal,
          isError: false
        });
      } else {
        setError(res?.message || 'Thread analysis refresh did not return data (check login and Datadog configuration).');
      }
    } catch (err) {
      console.error('Thread analysis refresh failed:', err);
      setError(err.message || 'Thread analysis refresh failed');
      const d = err.details;
      const logQuery = d?.logQuery;
      if (logQuery) {
        setThreadLastQueryDebug({
          query: logQuery,
          from: null,
          to: null,
          instanceName: null,
          logsProcessed: null,
          violationEventsTotal: null,
          isError: true
        });
      }
    } finally {
      setRefreshingThreadStableKey(null);
      await loadThreadRecommendations({ silent: true });
      await refreshProductionAnalysisSnowForStableKeys([sk], PA_ANALYSIS.THREADS);
    }
  };

  const startThreadRefreshAll = async (resume = false) => {
    const sourceRows = filteredThreadRows.map(({ row }) => row);
    if (!sourceRows.length) return;
    setRefreshingThreadAll(true);
    setError(null);
    try {
      const rowsPayload = sourceRows.map((r) => buildThreadRecommendationRowPayload(r));
      await refreshThreadRecommendationAll({
        rows: rowsPayload,
        days: THREAD_RECOMMENDATION_DAYS,
        resume,
        pctOfMax: threadPctOfMax
      });
      await loadThreadRecommendations({ silent: true });
    } catch (err) {
      console.error('Thread bulk refresh failed:', err);
      setError(err.message || 'Thread bulk refresh failed to start');
    } finally {
      setRefreshingThreadAll(false);
    }
  };

  const handleOpenRefreshDialog = (onlyFailed) => {
    setRefreshDialogOnlyFailed(onlyFailed);
    setRefreshDialogSelection({ cbv: true, msv: true, azure: true, cdn: true });
    setRefreshDialogOpen(true);
  };

  const handleRunRefreshFromDialog = async () => {
    const { cbv, msv, azure, cdn } = refreshDialogSelection;
    if (!cbv && !msv && !azure && !cdn) return;
    setRefreshingFromDialog(true);
    setError(null);
    try {
      const ops = [];
      if (cbv) ops.push('cbv');
      if (msv) ops.push('msv');
      if (azure) ops.push('azure');

      if (ops.length > 0) {
        const res = await refreshCustomerInstancesBulk({ ops, onlyFailed: refreshDialogOnlyFailed });
        if (res?.alreadyRunning) {
          setError('A bulk refresh is already running — wait for it to finish before starting another.');
          return;
        }
        await loadInstanceBulkProgress(null);
      }

      if (cdn) {
        const hosts = [...new Set(customerInstances.map((r) => hostFromUrl(getInstanceUrlForRow(r))).filter(Boolean))];
        if (hosts.length > 0) {
          setCdnLookupBusy(true);
          postMigrationCdnLookup(hosts)
            .then((r) => { if (r?.success && r?.map) setCdnMap(r.map); })
            .catch(() => {})
            .finally(() => setCdnLookupBusy(false));
        }
      }

      setRefreshDialogOpen(false);
    } catch (err) {
      setError('Refresh failed: ' + (err.message || 'please try again'));
    } finally {
      setRefreshingFromDialog(false);
    }
  };

  const handleOpenRowRefreshDialog = (row) => {
    if (!row?._id) return;
    setRowRefreshDialogRowId(row._id);
    setRowRefreshDialogSelection({ cbv: true, msv: true, azure: true, cdn: true });
    setRowRefreshDialogRunning(false);
  };

  const handleOpenCbvResourceHistory = (row, opts = {}) => {
    if (!row?._id) return;
    const title =
      row['Friendly name'] != null && String(row['Friendly name']).trim() !== ''
        ? String(row['Friendly name']).trim()
        : String(row['Instance Name'] ?? '').trim() || 'Instance';
    const focus = opts.focus === 'memory' ? 'memory' : 'all';
    setCbvResourceHistoryDialog({
      rowId: row._id,
      title,
      loading: true,
      error: null,
      entries: [],
      focus
    });
    getCustomerInstanceCbvResourceHistory(row._id)
      .then((data) => {
        setCbvResourceHistoryDialog((prev) =>
          prev && prev.rowId === row._id
            ? {
                ...prev,
                loading: false,
                entries: data.entries,
                title:
                  (data.friendlyName && String(data.friendlyName).trim()) ||
                  (data.instanceName && String(data.instanceName).trim()) ||
                  prev.title
              }
            : prev
        );
      })
      .catch((err) => {
        setCbvResourceHistoryDialog((prev) =>
          prev && prev.rowId === row._id
            ? { ...prev, loading: false, error: err.message || 'Failed to load history' }
            : prev
        );
      });
  };

  const handleRunRowRefreshDialog = async () => {
    const rowId = rowRefreshDialogRowId;
    if (!rowId || rowRefreshDialogRunning) return;
    const { cbv, msv, azure, cdn } = rowRefreshDialogSelection;
    if (!cbv && !msv && !azure && !cdn) return;
    setRowRefreshDialogRunning(true);
    setRowActionLoading((prev) => ({ ...prev, [rowId]: true }));
    setError(null);
    try {
      const actions = [];
      if (cbv) actions.push('cbv');
      if (msv) actions.push('msv');
      if (azure) actions.push('azure');
      for (const action of actions) {
        let updatedRow;
        if (action === 'cbv') updatedRow = await fetchCustomerInstanceRowCBV(rowId);
        else if (action === 'msv') updatedRow = await fetchCustomerInstanceRowMSV(rowId);
        else if (action === 'azure') updatedRow = await fetchCustomerInstanceRowAzure(rowId);
        if (updatedRow) {
          setCustomerInstances((prev) => prev.map((r) => (r._id === rowId ? { ...r, ...updatedRow } : r)));
        }
      }
      if (cdn) {
        const row = customerInstances.find((r) => r._id === rowId);
        const host = row ? hostFromUrl(getInstanceUrlForRow(row)) : '';
        if (host) {
          postMigrationCdnLookup([host])
            .then((res) => { if (res?.success && res?.map) setCdnMap((prev) => ({ ...prev, ...res.map })); })
            .catch(() => {});
        }
      }
      setRowRefreshDialogRowId(null);
    } catch (err) {
      setError((err.message || 'Refresh failed').toString());
    } finally {
      setRowRefreshDialogRunning(false);
      setRowActionLoading((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const EDIT_ROW_FIELDS = ['Instance Name', 'Friendly name', 'Instance Domain', 'Release', 'Region', 'Group', 'Customer Category'];
  const EDIT_ROW_TO_PAYLOAD = {
    'Instance Name': 'instanceName',
    'Friendly name': 'friendlyName',
    'Instance Domain': 'instanceDomain',
    'Release': 'currentVersion',
    'Region': 'region',
    'Group': 'group',
    'Customer Category': 'customerCategory'
  };
  const CUSTOMER_CATEGORY_OPTIONS = ['VIP1', 'VIP2', 'Standard', 'UAT'];

  const handleOpenEditDialog = (row) => {
    if (!row?._id) return;
    setEditDialogRowId(row._id);
    const initial = {};
    EDIT_ROW_FIELDS.forEach((key) => {
      const v = row[key];
      if (key === 'Friendly name') {
        initial[key] = (v != null && String(v).trim() !== '') ? String(v) : (row['Instance Name'] != null ? String(row['Instance Name']) : '');
      } else {
        initial[key] = v != null ? String(v) : '';
      }
    });
    setEditFormValues(initial);
    setEditDialogSaving(false);
  };

  const handleBulkCategoryUpdate = async (ids, category) => {
    const results = await Promise.allSettled(
      ids.map((id) => updateCustomerInstanceRow(id, { customerCategory: category }))
    );
    setCustomerInstances((prev) =>
      prev.map((r) => {
        const hit = results.find((res, idx) => res.status === 'fulfilled' && ids[idx] === r._id);
        return hit ? { ...r, ...hit.value } : r;
      })
    );
  };

  const handleSaveEditDialog = async () => {
    if (!editDialogRowId || editDialogSaving) return;
    setEditDialogSaving(true);
    setError(null);
    try {
      const payload = {};
      EDIT_ROW_FIELDS.forEach((displayKey) => {
        const apiKey = EDIT_ROW_TO_PAYLOAD[displayKey];
        const val = editFormValues[displayKey];
        if (val !== undefined && val !== null) payload[apiKey] = typeof val === 'string' ? val : String(val);
      });
      const updatedRow = await updateCustomerInstanceRow(editDialogRowId, payload);
      setCustomerInstances((prev) => prev.map((r) => (r._id === editDialogRowId ? { ...r, ...updatedRow } : r)));
      setEditDialogRowId(null);
    } catch (err) {
      setError('Save failed: ' + (err.message || 'please try again'));
    } finally {
      setEditDialogSaving(false);
    }
  };

  const handleDeleteRow = async () => {
    if (!deleteConfirmRowId || deleteRowLoading) return;
    setDeleteRowLoading(true);
    setError(null);
    try {
      await deleteCustomerInstanceRow(deleteConfirmRowId);
      setCustomerInstances((prev) => prev.filter((r) => r._id !== deleteConfirmRowId));
      setDeleteConfirmRowId(null);
      setDeleteConfirmRowName('');
    } catch (err) {
      setError('Delete failed: ' + (err.message || 'please try again'));
    } finally {
      setDeleteRowLoading(false);
    }
  };

  const handleOpenAddInstanceDialog = () => {
    setAddInstanceFormValues({});
    setAddInstanceSaving(false);
    setAddInstanceDialogOpen(true);
  };

  const handleSaveAddInstance = async () => {
    if (addInstanceSaving) return;
    const instanceName = String(addInstanceFormValues['Instance Name'] || '').trim();
    if (!instanceName) {
      setError('Instance Name is required');
      return;
    }
    setAddInstanceSaving(true);
    setError(null);
    try {
      const payload = {
        instanceName,
        instanceDomain: String(addInstanceFormValues['Instance Domain'] || '').trim(),
        friendlyName: String(addInstanceFormValues['Friendly name'] || '').trim(),
        currentVersion: String(addInstanceFormValues['Release'] || '').trim(),
        region: String(addInstanceFormValues['Region'] || '').trim(),
        group: String(addInstanceFormValues['Group'] || '').trim()
      };
      const newRow = await addCustomerInstanceRow(payload);
      setCustomerInstances((prev) => [...prev, newRow]);
      setAddInstanceDialogOpen(false);
    } catch (err) {
      setError('Failed to add instance: ' + (err.message || 'please try again'));
    } finally {
      setAddInstanceSaving(false);
    }
  };

  const handleCustomerSort = (key) => {
    if (customerSortBy === key) {
      setCustomerSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setCustomerSortBy(key);
      setCustomerSortOrder('asc');
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.04)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, letterSpacing: -0.3, lineHeight: 1.2, flex: '1 1 auto', minWidth: 0 }}>
            Release planning &amp; customer instances
          </Typography>
          <Tooltip
            placement="bottom-start"
            enterTouchDelay={0}
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 400,
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  fontSize: '0.8125rem',
                  lineHeight: 1.55,
                  py: 1.25,
                  px: 1.5,
                  border: '1px solid',
                  borderColor: 'grey.700',
                  '& strong': { color: '#fff', fontWeight: 600 }
                }
              }
            }}
            title={
              <span>
                Use <strong>Calendar</strong> for release and deployment milestones. <strong>Customer instances</strong> holds the
                synced instance catalog (release alignment and infrastructure views). <strong>Production analysis</strong> covers JVM
                memory, threads, and DB connection signals against configured limits — same data, focused tooling.
              </span>
            }
          >
            <IconButton
              size="small"
              aria-label="About release planning and customer instances"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => {
            setTabValue(v);
            if (v === 1) setCustomerInstancesViewTab(0);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('section', 'release-calendar');
              next.set('rcTab', String(v));
              if (v === 2) {
                next.set('paSub', String(productionAnalysisTab));
              } else {
                next.delete('paSub');
              }
              if (v === 1) {
                next.set('ciView', '0');
              } else {
                next.delete('ciView');
              }
              return next;
            }, { replace: true });
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab icon={<CalendarMonthNavIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Calendar" />
          <Tab icon={<TableRowsIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Customer instances" />
          <Tab icon={<AssessmentIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Production analysis" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {tabValue === 0 && (
        <>
      <Box display="flex" alignItems="center" justifyContent="center" gap={2} sx={{ mb: 2 }}>
        <Tooltip title="Previous month">
          <IconButton onClick={handlePrevMonth} aria-label="Previous month" size="large">
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" component="span" sx={{ minWidth: 180, textAlign: 'center' }}>
          {monthName} {year}
        </Typography>
        <Tooltip title="Next month">
          <IconButton onClick={handleNextMonth} aria-label="Next month" size="large">
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={2}
        sx={{
          p: 2,
          width: '100%',
          maxWidth: '100%',
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
          {monthName} {year}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            maxWidth: '100%',
            '& .day-cell': {
              minHeight: 80,
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'stretch',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              minWidth: 0
            }
          }}
        >
          {DAY_HEADERS.map((h) => (
            <Box key={h} className="day-cell" sx={{ fontWeight: 700, bgcolor: 'grey.200', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>
              {h}
            </Box>
          ))}
          {leadingBlanks.map((_, i) => (
            <Box key={`blank-${i}`} className="day-cell" sx={{ bgcolor: 'grey.50' }} />
          ))}
          {days.map((day) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const infoList = eventInfoByDate.get(dateKey) || [];
            const handleAddOnThisDate = (e) => {
              e.stopPropagation();
              const [y, m, d] = dateKey.split('-').map(Number);
              setAddDate(dayjs(new Date(y, m - 1, d)));
              setAddDialogOpen(true);
            };
            return (
              <Box key={dateKey} className="day-cell">
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    minWidth: 0,
                    px: 1,
                    pt: 0.5,
                    pb: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'stretch',
                    bgcolor: 'grey.50',
                    borderRadius: 0,
                    boxShadow: 'none',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.25, flexShrink: 0, mb: 0.25 }}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.95rem',
                        fontWeight: 800,
                        lineHeight: 1.2,
                        color: 'text.primary'
                      }}
                    >
                      {format(day, 'd')}
                    </Typography>
                    {isAdmin() && (
                      <Tooltip title="Add event on this date" arrow>
                        <IconButton
                          size="small"
                          onClick={handleAddOnThisDate}
                          sx={{ p: 0.2, minWidth: 0, minHeight: 0, color: 'text.primary', '&:hover': { bgcolor: 'action.hover' } }}
                          aria-label="Add event"
                        >
                          <AddIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minHeight: 0, overflow: 'auto' }}>
                    {infoList.map((info, idx) => {
                      const shortLabel = EVENT_TYPES.find((e) => e.value === info.eventType)?.shortLabel || info.eventType || '';
                      const isDeployment = ['deployment_weekend', 'deployment_non_prod', 'deployment_standard', 'deployment_vip'].includes(info.eventType);
                      const rowBg = info.eventType === 'working_day' ? 'transparent' : (EVENT_COLOR_MAP[info.eventType] || '#9e9e9e');
                      const darkRow = isDarkBg(rowBg);
                      const textColor = darkRow ? '#fff' : '#1a1a1a';
                      const linkColor = darkRow ? '#bbdefb' : '#1976d2';
                      const handleEditThisEvent = (e) => {
                        e.stopPropagation();
                        if (canAdd) openEditDialog(dateKey, info.eventType, info.id, info.release);
                      };
                      return (
                        <Tooltip key={idx} title={canAdd ? 'Click to edit' : ''} arrow>
                          <Box
                            onClick={handleEditThisEvent}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 0.5,
                              py: 0.25,
                              px: 0.5,
                              minHeight: 26,
                              minWidth: 0,
                              bgcolor: rowBg,
                              borderRadius: 0.5,
                              cursor: canAdd ? 'pointer' : 'default',
                              '&:hover': canAdd ? { filter: 'brightness(0.95)' } : {}
                            }}
                          >
                            <Box
                              component={info.release ? RouterLink : 'span'}
                              to={info.release ? `/?section=releases&release=${encodeURIComponent(info.release)}` : undefined}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                lineHeight: 1.25,
                                color: info.release ? linkColor : textColor,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                maxWidth: '48%',
                                textDecoration: 'none',
                                '&:hover': info.release ? { textDecoration: 'underline' } : {}
                              }}
                            >
                              {info.release || '—'}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 1, minWidth: 0, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                              {info.eventType && info.eventType !== 'working_day' && (
                                <>
                                  <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.25, color: textColor, wordBreak: 'break-word', whiteSpace: 'normal', textAlign: 'right' }}>
                                    {shortLabel}
                                  </Typography>
                                  {isDeployment && (
                                    <Typography
                                      component="button"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCustomersDialogGroup(info.eventType);
                                        setCustomersDialogOpen(true);
                                      }}
                                      sx={{ fontSize: '0.65rem', color: darkRow ? '#bbdefb' : '#1565c0', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', p: 0, flexShrink: 0 }}
                                    >
                                      Customers
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mt: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Legend
        </Typography>
        <Box display="flex" flexDirection="column" gap={1}>
          {LEGEND_GROUPS.map(({ lineLabel, values }) => (
            <Box key={lineLabel} display="flex" flexWrap="wrap" alignItems="center" gap={1.5} sx={{ fontSize: '0.75rem' }}>
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 140 }}>{lineLabel}</Typography>
              {values.map((value) => {
                const item = EVENT_TYPES.find((e) => e.value === value);
                if (!item) return null;
                const { label, color } = item;
                return (
                  <Box
                    key={value}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 0.75,
                        border: '1px solid',
                        borderColor: 'divider',
                        ...(color && color !== 'transparent'
                          ? { bgcolor: color, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }
                          : { bgcolor: 'background.paper', borderStyle: 'dashed' })
                      }}
                    />
                    <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>{label}</Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>
        </>
      )}

      {(tabValue === 1 || tabValue === 2) && (
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            maxWidth: '100%',
            overflow: tabValue === 2 ? 'auto' : 'hidden',
            p: tabValue === 2 ? { xs: 1.5, sm: 2 } : 2,
            pb: tabValue === 2 ? 0 : 2,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {customerInstancesLoading && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Loading stored data…
            </Typography>
          )}
          {tabValue === 1 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={customerInstancesViewTab}
              onChange={(_, v) => {
                setCustomerInstancesViewTab(v);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('section', 'release-calendar');
                  next.set('rcTab', '1');
                  next.set('ciView', String(v));
                  next.delete('paSub');
                  return next;
                }, { replace: true });
              }}
              aria-label="Customer instances view"
              sx={{ minHeight: 42 }}
            >
              <Tab label="Release view" id="customer-instances-tab-release" aria-controls="customer-instances-panel" />
              <Tab label="Infrastructure resource view" id="customer-instances-tab-infra" aria-controls="customer-instances-panel" />
              <Tab label="CDN View" id="customer-instances-tab-cdn" aria-controls="customer-instances-panel" />
              <Tab label="Azure Configuration" id="customer-instances-tab-azure" aria-controls="customer-instances-panel" />
            </Tabs>
          </Box>
          )}
          {tabValue === 2 && (
            <Box sx={{ mb: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.75,
                  flexWrap: 'wrap'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} component="span">
                    Production analysis
                  </Typography>
                  <Tooltip
                    placement="bottom-start"
                    enterTouchDelay={0}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 380,
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          fontSize: '0.8125rem',
                          lineHeight: 1.55,
                          py: 1.25,
                          px: 1.5,
                          border: '1px solid',
                          borderColor: 'grey.700',
                          '& strong': { color: '#fff', fontWeight: 600 }
                        }
                      }
                    }}
                    title={
                      <span>
                        Instance data comes from the same catalog as <strong>Customer instances</strong>. Upload CSV, refresh CBV/MSV/Azure,
                        and filter the catalog there — this view includes the full catalog for analysis (not the filtered subset from that tab), but{' '}
                        <strong>excludes NR rows</strong> (same rules as Exclude NR: release/version, Instance type, CIAM, Wijmo).
                      </span>
                    }
                  >
                    <IconButton size="small" aria-label="About production analysis data" sx={{ color: 'text.secondary' }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    flex: '1 1 auto',
                    minWidth: 0
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.45,
                      fontVariantNumeric: 'tabular-nums',
                      textAlign: { xs: 'left', sm: 'right' }
                    }}
                  >
                    {productionAnalysisSchedulerStatus == null ? (
                      <>
                        <strong>Last sync (IST):</strong> — · <strong>Next sync (IST):</strong> —
                      </>
                    ) : (
                      <>
                        <strong>Last sync (IST):</strong> {formatDateTimeIst(productionAnalysisSchedulerStatus.lastRunAt)}
                        {' · '}
                        <strong>Next sync (IST):</strong> {formatDateTimeIst(productionAnalysisSchedulerStatus.nextRunAt)}
                      </>
                    )}
                  </Typography>
                </Box>
              </Box>
              <Tabs
                value={productionAnalysisTab}
                onChange={(_, v) => {
                  setProductionAnalysisTab(v);
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('section', 'release-calendar');
                    next.set('rcTab', '2');
                    next.set('paSub', String(v));
                    return next;
                  }, { replace: true });
                }}
                aria-label="Production analysis views"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 42 }
                }}
              >
                <Tab label="Client Search" id="production-analysis-tab-search" />
                <Tab label="Memory analysis" id="production-analysis-tab-memory" />
                <Tab label="Threads analysis" id="production-analysis-tab-threads" />
                <Tab label="DB connection analysis" id="production-analysis-tab-db" />
                <Tab label="Progress" id="production-analysis-tab-progress" />
                <Tab label="Eng - Memory Improvements" id="production-analysis-tab-eng-memory" />
              </Tabs>
            </Box>
          )}
          {tabValue === 1 && customerInstancesViewTab !== 1 && customerInstancesViewTab !== 2 && customerInstancesViewTab !== 3 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              {customerInstances.length > 0 && (
                <>
                  <TextField
                    placeholder="Search all columns…"
                    size="small"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    sx={{ minWidth: 180 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      sx: { fontSize: '0.8125rem' }
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Region</InputLabel>
                    <Select
                      multiple
                      value={filterEnv}
                      onChange={(e) => setFilterEnv(e.target.value)}
                      input={<OutlinedInput label="Region" />}
                      renderValue={(selected) => selected.join(', ')}
                      sx={{ fontSize: '0.8125rem' }}
                    >
                      {uniqueEnvNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          <Checkbox checked={filterEnv.includes(name)} size="small" />
                          <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Group</InputLabel>
                    <Select
                      multiple
                      value={filterGroup}
                      onChange={(e) => setFilterGroup(e.target.value)}
                      input={<OutlinedInput label="Group" />}
                      renderValue={(selected) => selected.join(', ')}
                      sx={{ fontSize: '0.8125rem' }}
                    >
                      {uniqueGroupNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          <Checkbox checked={filterGroup.includes(name)} size="small" />
                          <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {releaseColumnKey && uniqueReleaseValues.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Release</InputLabel>
                      <Select
                        multiple
                        value={filterVersion}
                        onChange={(e) => setFilterVersion(e.target.value)}
                        input={<OutlinedInput label="Release" />}
                        renderValue={(selected) => selected.join(', ')}
                        sx={{ fontSize: '0.8125rem' }}
                      >
                        {uniqueReleaseValues.map((val) => (
                          <MenuItem key={val} value={val}>
                            <Checkbox checked={filterVersion.includes(val)} size="small" />
                            <ListItemText primary={val} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {customerInstancesViewTab === 0 && (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>CDN</InputLabel>
                      <Select
                        multiple
                        value={filterCdn}
                        onChange={(e) => setFilterCdn(e.target.value)}
                        input={<OutlinedInput label="CDN" />}
                        renderValue={(selected) => selected.join(', ')}
                        sx={{ fontSize: '0.8125rem' }}
                      >
                        {CDN_VALUES.map((val) => (
                          <MenuItem key={val} value={val}>
                            <Checkbox checked={filterCdn.includes(val)} size="small" />
                            <ListItemText primary={val} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {customerInstancesViewTab === 0 && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={excludeNR}
                          onChange={(e) => setExcludeNR(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Exclude NR"
                      sx={{ ml: 0.5 }}
                    />
                  )}
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
              {customerInstances.length > 0 && !customerInstancesLoading && (
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<FileDownloadIcon sx={{ fontSize: 18 }} />}
                  onClick={() => {
                    const csv = buildCustomerInstancesCsv(customerColumns, customerInstances);
                    if (!csv) return;
                    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `customer-instances-${format(new Date(), 'yyyy-MM-dd')}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                  sx={{ fontSize: '0.75rem', py: 0.5, px: 1.25, minHeight: 0 }}
                >
                  Export all to CSV
                </Button>
              )}
              {isAdmin() && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    component="label"
                    startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
                    disabled={customerInstancesSaving}
                    sx={{ fontSize: '0.75rem', py: 0.5, px: 1.25, minHeight: 0 }}
                  >
                    {customerInstancesSaving ? 'Saving…' : 'Upload CSV'}
                    <input type="file" accept=".csv" hidden onChange={handleCSVUpload} />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenAddInstanceDialog}
                    sx={{ fontSize: '0.75rem', py: 0.5, px: 1.25, minHeight: 0 }}
                  >
                    + Add instance
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={refreshingFromDialog ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                    disabled={refreshingFromDialog || instanceBulkProgress?.status === 'running' || customerInstances.length === 0}
                    onClick={() => handleOpenRefreshDialog(false)}
                    sx={{ fontSize: '0.75rem', py: 0.5, px: 1.25, minHeight: 0 }}
                  >
                    {refreshingFromDialog ? 'Starting…' : 'Refresh'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                    disabled={refreshingFromDialog || instanceBulkProgress?.status === 'running' || customerInstances.length === 0}
                    onClick={() => handleOpenRefreshDialog(true)}
                    sx={{ fontSize: '0.75rem', py: 0.5, px: 1.25, minHeight: 0 }}
                  >
                    Refresh Failed
                  </Button>
                </>
              )}
            </Box>
          </Box>
          )}
          {customerInstances.length > 0 && tabValue === 1 && customerInstancesViewTab === 1 && (
            <Box sx={{ pt: 1, mb: 2 }}>
              <InfrastructureResourceTab rows={filteredCustomerInstancesForTable} onBulkCategoryUpdate={handleBulkCategoryUpdate} />
            </Box>
          )}
          {tabValue === 1 && customerInstancesViewTab === 2 && (
            <Box sx={{ pt: 1, mb: 2 }}>
              <CdnView />
            </Box>
          )}
          {tabValue === 1 && customerInstancesViewTab === 3 && (
            <Box sx={{ pt: 1, mb: 2 }}>
              <AzureConfigTab customerInstances={customerInstances} />
            </Box>
          )}
          {customerInstances.length > 0 && tabValue === 1 && customerInstancesViewTab === 0 && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'stretch', minHeight: 280 }}>
              <Box sx={{ display: 'flex', gap: 2, flex: '1 1 0', minWidth: 200, flexWrap: 'wrap' }}>
                {[
                  { title: 'By Region', data: chartDataByRegion, nameCol: 'Region' },
                  { title: 'By Group', data: chartDataByGroup, nameCol: 'Group' },
                  { title: 'By Release', data: chartDataByRelease, nameCol: 'Release' }
                ].map(({ title, data, nameCol }) => {
                  const total = data.reduce((s, d) => s + d.value, 0);
                  const displayData = nameCol === 'Release'
                    ? [...data].sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }))
                    : data;
                  return (
                    <Box key={title} sx={{ flex: '1 1 0', minWidth: 200, maxWidth: 380, display: 'flex', flexDirection: 'column', minHeight: 280 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, flexShrink: 0 }}>{title}</Typography>
                      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.200' }}>{nameCol}</TableCell>
                              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.200' }} align="right">Count</TableCell>
                              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.200' }} align="right">%</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {displayData.map((row) => {
                              const pct = total ? ((row.value / total) * 100).toFixed(1) : '0';
                              return (
                                <TableRow key={row.name}>
                                  <TableCell>{row.name}</TableCell>
                                  <TableCell align="right">{row.value}</TableCell>
                                  <TableCell align="right">{pct}%</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 200, maxWidth: 280, minHeight: 280 }}>
                {[
                  {
                    title: 'CIAM ENABLED',
                    value: instanceStats.ciamEnabled,
                    sub: instanceStats.total ? `${((instanceStats.ciamEnabled / instanceStats.total) * 100).toFixed(1)}% of instances` : '—',
                    color: '#7b1fa2',
                    bg: 'rgba(123, 31, 162, 0.08)'
                  },
                  {
                    title: 'MODERN / LEGACY',
                    value: instanceStats.total
                      ? `${instanceStats.modernCount} / ${instanceStats.legacyCount}`
                      : '—',
                    sub: instanceStats.total
                      ? `${((instanceStats.modernCount / instanceStats.total) * 100).toFixed(1)}% Modern, ${((instanceStats.legacyCount / instanceStats.total) * 100).toFixed(1)}% Legacy`
                      : 'Run Azure to populate',
                    color: '#1976d2',
                    bg: 'rgba(25, 118, 210, 0.08)'
                  },
                  {
                    title: 'WIJMO (ASP)',
                    value: instanceStats.wijmoAsp,
                    sub: instanceStats.total ? `${((instanceStats.wijmoAsp / instanceStats.total) * 100).toFixed(1)}% enabled` : '—',
                    color: '#e65100',
                    bg: 'rgba(230, 81, 0, 0.08)'
                  },
                  {
                    title: 'WIJMO (SYSTEM)',
                    value: instanceStats.wijmoSystem,
                    sub: instanceStats.total ? `${((instanceStats.wijmoSystem / instanceStats.total) * 100).toFixed(1)}% enabled` : '—',
                    color: '#2e7d32',
                    bg: 'rgba(46, 125, 50, 0.08)'
                  }
                ].map((card) => (
                  <Paper
                    key={card.title}
                    elevation={0}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: card.bg,
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="caption" sx={{ color: card.color, fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: card.color }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {card.sub}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {tabValue === 2 && productionAnalysisTab === 0 && (
            <Box sx={{ pb: 2, width: '100%' }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Client Health Search</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Search across memory, DB connection, and thread analysis data to get a combined health overview for any client.
                </Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="Search by client or instance name…"
                  value={paSearchQuery}
                  onChange={(e) => { setPaSearchQuery(e.target.value); setPaSearchSelectedKey(null); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
              </Paper>

              {paSearchQuery.trim() && Object.keys(heapEntriesByStableKey).length === 0 && Object.keys(dbConnEntriesByStableKey).length === 0 && Object.keys(threadEntriesByStableKey).length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>No production analysis data loaded yet. Open the Memory, Threads, or DB tabs first to populate data.</Alert>
              )}
              {paSearchQuery.trim() && paSearchResults.length === 0 && (Object.keys(heapEntriesByStableKey).length > 0 || Object.keys(dbConnEntriesByStableKey).length > 0 || Object.keys(threadEntriesByStableKey).length > 0) && (
                <Alert severity="warning" sx={{ mb: 2 }}>No clients found matching &ldquo;{paSearchQuery}&rdquo;.</Alert>
              )}

              {paSearchQuery.trim() && paSearchResults.length > 1 && !paSearchSelectedKey && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{paSearchResults.length} clients matched — click to view:</Typography>
                  <Stack spacing={0.75}>
                    {paSearchResults.map((r) => {
                      const lvls = [r.heapEntry?.recommendation?.level, r.dbEntry?.recommendation?.level, r.threadEntry?.recommendation?.level].filter(Boolean);
                      const ol = lvls.includes('critical') ? 'critical' : lvls.includes('warning') ? 'warning' : lvls.length ? 'good' : null;
                      return (
                        <Button key={r.stableKey} variant="outlined" fullWidth onClick={() => setPaSearchSelectedKey(r.stableKey)} sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 0.75 }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Chip size="small" label={ol || 'No data'} color={ol === 'critical' ? 'error' : ol === 'warning' ? 'warning' : ol === 'good' ? 'success' : 'default'} sx={{ minWidth: 68 }} />
                            <Box sx={{ textAlign: 'left' }}>
                              <Typography variant="body2" fontWeight={600}>{r.friendlyName}</Typography>
                              {r.region && <Typography variant="caption" color="text.secondary">{r.region}</Typography>}
                            </Box>
                          </Stack>
                        </Button>
                      );
                    })}
                  </Stack>
                </Paper>
              )}

              {paSearchSelectedKey && (() => {
                const hEntry = heapEntriesByStableKey[paSearchSelectedKey];
                const dbEntry = dbConnEntriesByStableKey[paSearchSelectedKey];
                const tEntry = threadEntriesByStableKey[paSearchSelectedKey];
                const displayName = hEntry?.friendlyName || dbEntry?.friendlyName || tEntry?.friendlyName || paSearchSelectedKey;
                const region = hEntry?.region || dbEntry?.region || tEntry?.region;
                const computedAt = hEntry?.computedAt || dbEntry?.computedAt || tEntry?.computedAt;
                const levels = [hEntry?.recommendation?.level, dbEntry?.recommendation?.level, tEntry?.recommendation?.level].filter(Boolean);
                const overallLevel = levels.includes('critical') ? 'critical' : levels.includes('warning') ? 'warning' : levels.length ? 'good' : null;
                const lc = { critical: 'error', warning: 'warning', good: 'success' };
                const allTransitions = [
                  ...paSearchTransitions.memory.map(t => ({ ...t, analysisLabel: 'Memory' })),
                  ...paSearchTransitions.threads.map(t => ({ ...t, analysisLabel: 'Threads' })),
                  ...paSearchTransitions.db.map(t => ({ ...t, analysisLabel: 'DB Connection' })),
                ].sort((a, b) => (b.dayKey || '').localeCompare(a.dayKey || ''));
                const miniChartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { maxRotation: 45, font: { size: 8 } } }, y: { beginAtZero: true, ticks: { precision: 0, font: { size: 9 } } } } };
                const leftBorder = (entry) => ({ borderLeft: 4, borderLeftColor: entry?.recommendation?.level === 'critical' ? 'error.main' : entry?.recommendation?.level === 'warning' ? 'warning.main' : entry ? 'success.main' : 'divider' });

                return (
                  <Box>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                        <Chip label={overallLevel ? (overallLevel.charAt(0).toUpperCase() + overallLevel.slice(1)) : 'No Data'} color={lc[overallLevel] || 'default'} sx={{ fontWeight: 700, fontSize: '0.85rem', alignSelf: 'flex-start' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" sx={{ lineHeight: 1.3 }}>{displayName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {region ? `Region: ${region}` : ''}
                            {computedAt ? `${region ? ' · ' : ''}Last analysed: ${new Date(computedAt).toLocaleString()}` : ''}
                          </Typography>
                        </Box>
                        {paSearchResults.length > 1 && (
                          <Button size="small" variant="outlined" onClick={() => setPaSearchSelectedKey(null)}>← All results</Button>
                        )}
                      </Stack>
                    </Paper>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2, mb: 2 }}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, ...leftBorder(hEntry) }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700}>Memory (JVM Heap)</Typography>
                          {hEntry?.recommendation?.level ? <Chip size="small" label={hEntry.recommendation.level} color={lc[hEntry.recommendation.level] || 'default'} /> : <Chip size="small" label="No data" variant="outlined" />}
                        </Stack>
                        {hEntry ? (
                          <>
                            {hEntry.currentMemoryGiB != null && hEntry.proposedMemoryGiB != null && (() => {
                              const curr = hEntry.currentMemoryGiB; const prop = hEntry.proposedMemoryGiB; const delta = prop - curr;
                              return (
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                  <Chip size="small" label={`${curr.toFixed(1)} GiB`} variant="outlined" />
                                  {Math.abs(delta) > 0.01 && <><Typography variant="caption">→</Typography><Chip size="small" label={`${delta > 0 ? '↑' : '↓'} ${prop.toFixed(1)} GiB`} color={delta > 0 ? 'warning' : 'success'} /></>}
                                </Stack>
                              );
                            })()}
                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                              <Box><Typography variant="caption" color="text.secondary">Total</Typography><Typography variant="body2" fontWeight={600}>{hEntry.summary?.violationEventsTotal ?? '—'}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Critical days</Typography><Typography variant="body2" fontWeight={600} color={hEntry.summary?.criticalDaysTotal > 0 ? 'error.main' : 'text.primary'}>{hEntry.summary?.criticalDaysTotal ?? '—'}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Warn days</Typography><Typography variant="body2" fontWeight={600}>{hEntry.summary?.warningDaysTotal ?? '—'}</Typography></Box>
                            </Stack>
                            {hEntry.recommendation?.title && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{hEntry.recommendation.title}</Typography>}
                            {hEntry.dailyViolationTotals?.length > 0 && (
                              <Box sx={{ height: 140 }}>
                                <Bar data={{ labels: hEntry.dailyViolationTotals.map(d => d.date?.slice(5) ?? d.date), datasets: [{ label: 'Violations/day', data: hEntry.dailyViolationTotals.map(d => d.totalViolations ?? 0), backgroundColor: 'rgba(25,118,210,0.55)', borderColor: 'rgba(25,118,210,1)', borderWidth: 1 }] }} options={miniChartOptions} />
                              </Box>
                            )}
                          </>
                        ) : <Typography variant="body2" color="text.secondary">No memory data for this client.</Typography>}
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, ...leftBorder(dbEntry) }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700}>DB Connection</Typography>
                          {dbEntry?.recommendation?.level ? <Chip size="small" label={dbEntry.recommendation.level} color={lc[dbEntry.recommendation.level] || 'default'} /> : <Chip size="small" label="No data" variant="outlined" />}
                        </Stack>
                        {dbEntry ? (
                          <>
                            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                              {dbEntry.currentMaxDb != null && <Chip size="small" label={`Max DB: ${dbEntry.currentMaxDb}`} variant="outlined" />}
                              {dbEntry.proposedMaxDb != null && dbEntry.proposedMaxDb !== dbEntry.currentMaxDb && <Chip size="small" label={`${dbEntry.proposedMaxDb > dbEntry.currentMaxDb ? '↑' : '↓'} ${dbEntry.proposedMaxDb}`} color={dbEntry.proposedMaxDb > dbEntry.currentMaxDb ? 'warning' : 'success'} />}
                            </Stack>
                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                              <Box><Typography variant="caption" color="text.secondary">Total</Typography><Typography variant="body2" fontWeight={600}>{dbEntry.summary?.violationEventsTotal ?? '—'}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Critical days</Typography><Typography variant="body2" fontWeight={600} color={dbEntry.summary?.criticalDaysTotal > 0 ? 'error.main' : 'text.primary'}>{dbEntry.summary?.criticalDaysTotal ?? '—'}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Warn days</Typography><Typography variant="body2" fontWeight={600}>{dbEntry.summary?.warningDaysTotal ?? '—'}</Typography></Box>
                            </Stack>
                            {dbEntry.recommendation?.title && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{dbEntry.recommendation.title}</Typography>}
                            {dbEntry.dailyViolationTotals?.length > 0 && (
                              <Box sx={{ height: 140 }}>
                                <Bar data={{ labels: dbEntry.dailyViolationTotals.map(d => d.date?.slice(5) ?? d.date), datasets: [{ label: 'Violations/day', data: dbEntry.dailyViolationTotals.map(d => d.totalViolations ?? 0), backgroundColor: 'rgba(46,125,50,0.55)', borderColor: 'rgba(46,125,50,1)', borderWidth: 1 }] }} options={miniChartOptions} />
                              </Box>
                            )}
                          </>
                        ) : <Typography variant="body2" color="text.secondary">No DB connection data for this client.</Typography>}
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, ...leftBorder(tEntry) }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700}>Tomcat Threads</Typography>
                          {tEntry?.recommendation?.level ? <Chip size="small" label={tEntry.recommendation.level} color={lc[tEntry.recommendation.level] || 'default'} /> : <Chip size="small" label="No data" variant="outlined" />}
                        </Stack>
                        {tEntry ? (
                          <>
                            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                              {tEntry.currentMaxThreads != null && <Chip size="small" label={`Max Threads: ${tEntry.currentMaxThreads}`} variant="outlined" />}
                              {tEntry.proposedMaxThreads != null && tEntry.proposedMaxThreads !== tEntry.currentMaxThreads && <Chip size="small" label={`${tEntry.proposedMaxThreads > tEntry.currentMaxThreads ? '↑' : '↓'} ${tEntry.proposedMaxThreads}`} color={tEntry.proposedMaxThreads > tEntry.currentMaxThreads ? 'warning' : 'success'} />}
                            </Stack>
                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                              <Box><Typography variant="caption" color="text.secondary">Total</Typography><Typography variant="body2" fontWeight={600}>{tEntry.summary?.violationEventsTotal ?? '—'}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Critical days</Typography><Typography variant="body2" fontWeight={600} color={tEntry.summary?.criticalDaysTotal > 0 ? 'error.main' : 'text.primary'}>{tEntry.summary?.criticalDaysTotal ?? '—'}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Warn days</Typography><Typography variant="body2" fontWeight={600}>{tEntry.summary?.warningDaysTotal ?? '—'}</Typography></Box>
                            </Stack>
                            {tEntry.recommendation?.title && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{tEntry.recommendation.title}</Typography>}
                            {tEntry.dailyViolationTotals?.length > 0 && (
                              <Box sx={{ height: 140 }}>
                                <Bar data={{ labels: tEntry.dailyViolationTotals.map(d => d.date?.slice(5) ?? d.date), datasets: [{ label: 'Violations/day', data: tEntry.dailyViolationTotals.map(d => d.totalViolations ?? 0), backgroundColor: 'rgba(230,81,0,0.55)', borderColor: 'rgba(230,81,0,1)', borderWidth: 1 }] }} options={miniChartOptions} />
                              </Box>
                            )}
                          </>
                        ) : <Typography variant="body2" color="text.secondary">No thread data for this client.</Typography>}
                      </Paper>
                    </Box>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Status Change History (last 30 days)</Typography>
                      {paSearchTransitionsLoading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Loading changes…</Typography></Stack>}
                      {!paSearchTransitionsLoading && allTransitions.length === 0 && <Typography variant="body2" color="text.secondary">No status changes recorded in the last 30 days.</Typography>}
                      {!paSearchTransitionsLoading && allTransitions.map((t, i) => (
                        <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 84, fontFamily: 'monospace' }}>{t.dayKey}</Typography>
                          <Chip size="small" label={t.analysisLabel} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          <Chip size="small" label={t.previousLevel || 'none'} color={lc[t.previousLevel] || 'default'} />
                          <Typography variant="body2">→</Typography>
                          <Chip size="small" label={t.level || 'none'} color={lc[t.level] || 'default'} />
                          {t.direction === 'worsened' && <Typography variant="body2" color="error.main" fontWeight={600}>↑ Worsened</Typography>}
                          {t.direction === 'improved' && <Typography variant="body2" color="success.main" fontWeight={600}>↓ Improved</Typography>}
                          {t.recommendationTitle && <Typography variant="caption" color="text.secondary">— {t.recommendationTitle}</Typography>}
                        </Stack>
                      ))}
                    </Paper>
                  </Box>
                );
              })()}
            </Box>
          )}

          {customerInstances.length > 0 && tabValue === 2 && productionAnalysisTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2, width: '100%', minWidth: 0, flex: '1 1 auto' }}>
              {heapBulkProgressLabel && (
                <Paper
                  variant="outlined"
                  sx={(theme) => ({
                    p: 2,
                    borderRadius: 2,
                    borderLeft: 4,
                    borderLeftColor: 'warning.main',
                    bgcolor: alpha(theme.palette.warning.main, 0.06)
                  })}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <PauseCircleOutlineIcon color="warning" sx={{ flexShrink: 0, mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Bulk refresh checkpoint
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                        {heapBulkProgressLabel.done} of {heapBulkProgressLabel.total} instances processed. Use &quot;Resume bulk&quot;
                        if the job was interrupted (uses the same client list as Refresh all).
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(
                          100,
                          heapBulkProgressLabel.total > 0
                            ? (heapBulkProgressLabel.done / heapBulkProgressLabel.total) * 100
                            : 0
                        )}
                        sx={(theme) => ({
                          height: 8,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.grey[500], 0.2),
                          '& .MuiLinearProgress-bar': { borderRadius: 1 }
                        })}
                      />
                    </Box>
                  </Stack>
                </Paper>
              )}
              {refreshingHeapStableKey && (
                <Paper
                  elevation={0}
                  sx={(theme) => ({
                    mb: 2,
                    p: 0,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
                      theme.palette.info.main,
                      0.06
                    )} 55%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    position: 'relative'
                  })}
                >
                  <LinearProgress
                    sx={(theme) => ({
                      height: 4,
                      borderRadius: 0,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 0,
                        background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`
                      }
                    })}
                    variant="indeterminate"
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 2,
                      p: 2,
                      pt: 2.25,
                      flexWrap: { xs: 'wrap', sm: 'nowrap' }
                    }}
                  >
                    <Box sx={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={56}
                        thickness={2.5}
                        sx={(t) => ({ color: alpha(t.palette.primary.main, 0.12) })}
                      />
                      <CircularProgress
                        size={56}
                        thickness={2.5}
                        sx={{ position: 'absolute', left: 0, top: 0, color: 'primary.main' }}
                        variant="indeterminate"
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={800} letterSpacing={0.3} color="primary.dark">
                        Datadog heap run in progress
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {heapRefreshClientLabel ? (
                          <>
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                              {heapRefreshClientLabel}
                            </Box>
                            <Box component="span" sx={{ opacity: 0.85 }}>
                              {' '}
                              · {HEAP_DATADOG_REFRESH_PHASES[heapRefreshPhaseIndex]}
                            </Box>
                          </>
                        ) : (
                          HEAP_DATADOG_REFRESH_PHASES[heapRefreshPhaseIndex]
                        )}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
                        {HEAP_DATADOG_REFRESH_PHASES.map((label, i) => {
                          const active = i === heapRefreshPhaseIndex;
                          return (
                            <Chip
                              key={label}
                              size="small"
                              label={label}
                              sx={(theme) => ({
                                fontSize: '0.7rem',
                                height: 24,
                                fontWeight: active ? 700 : 500,
                                transition: 'all 0.35s ease',
                                borderColor: active ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.12),
                                bgcolor: active ? alpha(theme.palette.primary.main, 0.14) : 'transparent',
                                color: active ? 'primary.dark' : 'text.secondary',
                                boxShadow: active ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}` : 'none'
                              })}
                              variant="outlined"
                            />
                          );
                        })}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block', opacity: 0.9 }}>
                        Large windows can take a few minutes — keep this tab open. Errors still show in the banner above.
                      </Typography>
                    </Box>
                    <Box
                      aria-hidden
                      sx={{
                        '@keyframes heapEq': {
                          '0%': { transform: 'scaleY(0.28)' },
                          '100%': { transform: 'scaleY(1)' }
                        },
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        gap: 0.5,
                        height: 40,
                        px: 0.5,
                        flexShrink: 0
                      }}
                    >
                      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                        <Box
                          key={i}
                          sx={(theme) => ({
                            width: 5,
                            height: 28,
                            borderRadius: 0.75,
                            bgcolor: theme.palette.primary.main,
                            transformOrigin: 'bottom',
                            animation: 'heapEq 0.55s ease-in-out infinite alternate',
                            animationDelay: `${i * 0.09}s`
                          })}
                        />
                      ))}
                    </Box>
                  </Box>
                </Paper>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 1.25,
                  width: '100%',
                  alignItems: 'stretch'
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    flex: { xs: 'none', lg: '6 1 0%' },
                    minWidth: 0,
                    width: { xs: '100%' },
                    maxWidth: '100%'
                  }}
                >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 1.2, fontWeight: 700, lineHeight: 1.2 }}
                  >
                    Query &amp; actions
                  </Typography>
                  <Tooltip
                    enterTouchDelay={0}
                    title={
                      <Box sx={{ maxWidth: 340 }}>
                        <Typography variant="caption" component="div" sx={{ display: 'block', lineHeight: 1.45 }}>
                          Last <strong>{HEAP_RECOMMENDATION_DAYS} days</strong> (UTC). Uses <code>@JVM.Heap.FreeMemory</code> vs your threshold
                          % of each row&apos;s <strong>Max Memory</strong> (MB). Violation events are summed per UTC day; recommendation levels
                          from daily warn/crit rules.
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ display: 'block', mt: 1, opacity: 0.92 }}>
                          Click for full help, env options, and the last Datadog query.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Memory analysis summary and help"
                      onClick={() => setHeapHelpDialogOpen(true)}
                      sx={{ mt: -0.5 }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Stack spacing={1}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    alignItems={{ md: 'flex-start' }}
                    justifyContent="space-between"
                  >
                    <TextField
                      type="number"
                      size="small"
                      label="Threshold (% of Max Memory)"
                      value={heapFreePctThreshold}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (e.target.value === '') return;
                        if (Number.isFinite(v)) setHeapFreePctThreshold(Math.min(50, Math.max(1, Math.round(v))));
                      }}
                      inputProps={{ min: 1, max: 50, step: 1 }}
                      sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title="Violation events when free heap is below this % of each client’s Max Memory (MB) column — used in the @JVM.Heap.FreeMemory Datadog filter."
                              enterTouchDelay={0}
                            >
                              <IconButton size="small" edge="end" aria-label="Threshold details" sx={{ mr: -0.5 }}>
                                <InfoOutlinedIcon fontSize="small" color="action" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                    <Stack direction="row" flexWrap="wrap" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ flex: 1 }}>
                      <Button
                        variant="contained"
                        disableElevation
                        size="medium"
                        onClick={() => startHeapRefreshAll(false)}
                        disabled={refreshingHeapAll || filteredHeapRows.length === 0}
                        startIcon={refreshingHeapAll ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {refreshingHeapAll ? 'Starting bulk…' : 'Refresh all'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => startHeapRefreshAll(true)}
                        disabled={refreshingHeapAll || filteredHeapRows.length === 0 || !heapBulkProgress?.keys?.length}
                        sx={{ textTransform: 'none' }}
                      >
                        Resume bulk
                      </Button>
                    </Stack>
                  </Stack>
                  {refreshingHeapAll && (
                    <LinearProgress sx={{ borderRadius: 1 }} variant="indeterminate" aria-label="Bulk refresh starting" />
                  )}
                </Stack>
              </Paper>
              <ProductionAnalysisHealthReport stats={heapHealthSummary} />
              </Box>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.50', width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    px: { xs: 1.5, sm: 2 },
                    pt: { xs: 1.5, sm: 2 },
                    pb: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', flex: '1 1 auto' }}>
                    <TextField
                      size="small"
                      label="Filter clients"
                      value={recommendationClientSearch}
                      onChange={(e) => setRecommendationClientSearch(e.target.value)}
                      placeholder="Instance or friendly name"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ flex: '1 1 200px', maxWidth: 400, minWidth: 180 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 168 }}>
                      <InputLabel id="pa-region-filter-heap">Region</InputLabel>
                      <Select
                        labelId="pa-region-filter-heap"
                        label="Region"
                        value={effectiveProductionAnalysisRegionFilter}
                        onChange={(e) => setProductionAnalysisRegionFilter(e.target.value)}
                      >
                        <MenuItem value="all">All regions</MenuItem>
                        {productionAnalysisRegionOptions.regions.map((r) => (
                          <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                        {productionAnalysisRegionOptions.hasOnPrem && (
                          <MenuItem value="on-prem">On-Prem</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 168 }}>
                      <InputLabel id="pa-mem-change-filter-heap">Memory change</InputLabel>
                      <Select
                        labelId="pa-mem-change-filter-heap"
                        label="Memory change"
                        value={heapFilterMemoryChange}
                        onChange={(e) => setHeapFilterMemoryChange(e.target.value)}
                      >
                        <MenuItem value="all">All instances</MenuItem>
                        <MenuItem value="changed">Any change (up/down)</MenuItem>
                        <MenuItem value="upgraded">Upgraded</MenuItem>
                        <MenuItem value="downgraded">Downgraded</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip
                      title="Show clients with: critical days >3 in the last 7 days, OOM events in the last 7 days, or memory upgraded but still critical/OOM"
                      placement="top"
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={heapFilterRequireFocus}
                            onChange={(e) => setHeapFilterRequireFocus(e.target.checked)}
                            size="small"
                            color="warning"
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight={heapFilterRequireFocus ? 700 : 400} color={heapFilterRequireFocus ? 'warning.dark' : 'text.primary'}>
                            Require focus
                          </Typography>
                        }
                        sx={{ ml: 0.5, mr: 0 }}
                      />
                    </Tooltip>
                    <FormControl size="small" sx={{ minWidth: 152 }}>
                      <InputLabel id="pa-health-filter-heap">Status</InputLabel>
                      <Select
                        labelId="pa-health-filter-heap"
                        label="Status"
                        value={productionAnalysisHealthFilter}
                        onChange={(e) => setProductionAnalysisHealthFilter(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                        <MenuItem value="warning">Warning</MenuItem>
                        <MenuItem value="good">OK</MenuItem>
                        <MenuItem value="none">No Data</MenuItem>
                        <MenuItem value="oom">Has OOM events</MenuItem>
                        <MenuItem value="oomAutoRestart">Has OOM auto-restarts</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={selectAllFilteredHeap}
                      disabled={filteredHeapRows.length === 0}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Select all ({filteredHeapRows.length})
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={clearHeapSelection}
                      disabled={heapSelectionCount === 0}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear selection
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportHeapTableCsv}
                      disabled={filteredHeapRows.length === 0}
                      sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                    >
                      Export ({filteredHeapRows.length})
                    </Button>
                  </Box>
                </Box>
                <TableContainer sx={{ maxWidth: '100%', overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 1120, width: '100%' }}>
                    <TableHead>
                      <TableRow>
                      <TableCell
                        padding="checkbox"
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                      >
                        <Checkbox
                          size="small"
                          indeterminate={heapPageIndeterminate}
                          checked={heapPageAllSelected}
                          onChange={toggleHeapPageSelection}
                          disabled={heapPageStableKeys.length === 0}
                          inputProps={{ 'aria-label': 'Select all clients on this page' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                        Client
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                        Region
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        Current (GiB)
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        Proposed (GiB)
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        Max / day
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        Total
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        Warn days
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        Crit days
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        <Tooltip title="java.lang.OutOfMemoryError in Datadog logs (Tomcat), same lookback as heap">
                          <span>OOM events</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        <Tooltip title={`Kernel OOM kills detected via "OOM-DETECTED" source:oom-events — each event represents a process auto-restart after the Linux OOM killer fired`}>
                          <span>OOM auto-restarts</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider', minWidth: 168 }}
                      >
                        <Tooltip title="Recorded Max Memory changes from CBV sync (upgrades/downgrades). Click the clock icon in Actions to see full history.">
                          <span>Mem change</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                        Recommendation
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        align="right"
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedHeapRows.map(({ row, stableKey, entry, memoryChanges }) => {
                      const sum = entry?.summary;
                      const level = entry?.recommendation?.level;
                      const recColor =
                        level === 'critical'
                          ? 'error.main'
                          : level === 'warning'
                            ? 'warning.main'
                            : 'text.secondary';
                      const busy = refreshingHeapStableKey === stableKey;
                      const clientLabel =
                        String(row['Friendly name'] ?? '').trim() ||
                        String(row['Instance Name'] ?? row.instanceName ?? '').trim() ||
                        '(unknown)';
                      const clientHref = clientInstanceUrl(row);
                      const warnDays = sum?.warningDaysTotal;
                      const critDays = sum?.criticalDaysTotal;
                      const regionLabel = getRegionValue(row) || '—';
                      return (
                        <TableRow
                          key={stableKey}
                          hover
                          sx={(theme) => ({
                            transition: 'background-color 0.15s ease',
                            ...(busy ? { bgcolor: alpha(theme.palette.primary.main, 0.07) } : {})
                          })}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={!!paRowSelection.memory[stableKey]}
                              onChange={() => {
                                setPaRowSelection((prev) => {
                                  const next = { ...prev, memory: { ...prev.memory } };
                                  if (next.memory[stableKey]) delete next.memory[stableKey];
                                  else next.memory[stableKey] = true;
                                  return next;
                                });
                              }}
                              inputProps={{ 'aria-label': `Select ${clientLabel}` }}
                            />
                          </TableCell>
                          <TableCell>
                            {clientHref ? (
                              <MuiLink
                                href={clientHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ fontWeight: 600 }}
                              >
                                {clientLabel}
                              </MuiLink>
                            ) : (
                              clientLabel
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{regionLabel}</TableCell>
                          <TableCell align="right">
                            {entry?.currentMemoryGiB != null ? Number(entry.currentMemoryGiB).toFixed(1) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {entry?.proposedMemoryGiB != null ? Number(entry.proposedMemoryGiB).toFixed(1) : '—'}
                          </TableCell>
                          <TableCell align="right">{sum?.maxDailyCount != null ? sum.maxDailyCount : '—'}</TableCell>
                          <TableCell align="right">{sum?.violationEventsTotal != null ? sum.violationEventsTotal : '—'}</TableCell>
                          <TableCell align="right">
                            <Box component="span" sx={(theme) => heapWarnDaysCellSx(theme, warnDays)}>
                              {warnDays != null ? warnDays : '—'}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box component="span" sx={(theme) => heapCritDaysCellSx(theme, critDays)}>
                              {critDays != null ? critDays : '—'}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {(() => {
                              const oomErr = entry?.oomSummary?.error;
                              const oomTotal = entry?.oomSummary?.totalEvents;
                              if (oomErr) {
                                return (
                                  <Typography variant="caption" color="error" title={String(oomErr)}>
                                    Error
                                  </Typography>
                                );
                              }
                              if (oomTotal != null && Number.isFinite(Number(oomTotal))) {
                                const n = Number(oomTotal);
                                return (
                                  <Box
                                    component="span"
                                    sx={(theme) =>
                                      n > 0
                                        ? { color: theme.palette.error.main, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }
                                        : { fontVariantNumeric: 'tabular-nums' }
                                    }
                                  >
                                    {n}
                                  </Box>
                                );
                              }
                              return '—';
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            {(() => {
                              const restartErr = entry?.oomAutoRestartSummary?.error;
                              const restartTotal = entry?.oomAutoRestartSummary?.totalEvents;
                              if (restartErr) {
                                return (
                                  <Typography variant="caption" color="error" title={String(restartErr)}>
                                    Error
                                  </Typography>
                                );
                              }
                              if (restartTotal != null && Number.isFinite(Number(restartTotal))) {
                                const n = Number(restartTotal);
                                return (
                                  <Box
                                    component="span"
                                    sx={(theme) =>
                                      n > 0
                                        ? { color: theme.palette.warning.dark, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }
                                        : { fontVariantNumeric: 'tabular-nums' }
                                    }
                                  >
                                    {n}
                                  </Box>
                                );
                              }
                              return '—';
                            })()}
                          </TableCell>
                          <TableCell sx={{ minWidth: 168, maxWidth: 240, whiteSpace: 'normal' }}>
                            {(() => {
                              if (!memoryChanges || memoryChanges.count === 0) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                );
                              }
                              const last = memoryChanges.last;
                              const d = last?.recordedAt ? new Date(last.recordedAt) : null;
                              const dateStr =
                                d && !isNaN(d.getTime()) ? format(d, 'yyyy-MM-dd') : '—';
                              const fromG = mbToGiBDisplay(last?.fromMb);
                              const toG = mbToGiBDisplay(last?.toMb);
                              const isUp = last?.direction === 'upgrade';
                              const arrow = isUp ? '↑' : '↓';
                              const color = isUp ? 'success.main' : 'error.main';
                              const tooltipLines = memoryChanges.changes
                                .slice(-6)
                                .reverse()
                                .map((c) => {
                                  const cd = c.recordedAt ? new Date(c.recordedAt) : null;
                                  const ds = cd && !isNaN(cd.getTime()) ? format(cd, 'yyyy-MM-dd') : '—';
                                  const f = mbToGiBDisplay(c.fromMb);
                                  const t = mbToGiBDisplay(c.toMb);
                                  const a = c.direction === 'upgrade' ? '↑' : '↓';
                                  return `${ds}: ${f} → ${t} GiB ${a}`;
                                })
                                .join('\n');
                              return (
                                <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltipLines}</Box>}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
                                      {fromG != null && toG != null ? `${fromG} → ${toG} GiB ${arrow}` : arrow}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {dateStr}
                                      {memoryChanges.count > 1 ? ` • ${memoryChanges.count} changes` : ''}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {entry?.error ? (
                              <Typography color="error" variant="body2">
                                {entry.error}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: recColor }}>
                                {entry?.recommendation?.title || '—'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Refresh from Datadog">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => refreshHeapForRow(row)}
                                  disabled={busy || !String(row['Instance Name'] ?? row.instanceName ?? '').trim()}
                                  aria-label="Refresh heap from Datadog"
                                >
                                  {busy ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Daily violations chart">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => setHeapDailyDialog({ entry, label: stableKey })}
                                  disabled={!entry?.dailyViolationTotals?.length}
                                  aria-label="Open daily chart"
                                >
                                  <ShowChartIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Memory upgrade history">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenCbvResourceHistory(row, { focus: 'memory' })}
                                  disabled={!row?._id}
                                  aria-label="Open memory upgrade history"
                                  sx={{
                                    ...(memoryChanges && memoryChanges.count > 0
                                      ? { color: 'primary.main' }
                                      : {})
                                  }}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={(() => {
                                const sl = getPaAnalysisSlice(productionAnalysisNotes[stableKey], PA_ANALYSIS.MEMORY);
                                return sl.comment?.trim()
                                  ? `Comment saved for this instance (memory)`
                                  : 'More actions';
                              })()}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPaActionMenuAnchor(e.currentTarget);
                                    setPaActionMenuContext({ row, stableKey, analysisType: PA_ANALYSIS.MEMORY });
                                  }}
                                  aria-label="Open actions menu"
                                  sx={{
                                    ...(getPaAnalysisSlice(productionAnalysisNotes[stableKey], PA_ANALYSIS.MEMORY).comment?.trim()
                                      ? { color: 'secondary.main' }
                                      : {})
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  px: 1,
                  py: 0.5,
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}
              >
                <TablePagination
                  component="div"
                  count={filteredHeapRows.length}
                  page={customerInstancesPage}
                  onPageChange={(_, newPage) => setCustomerInstancesPage(newPage)}
                  rowsPerPage={CUSTOMER_INSTANCES_ROWS_PER_PAGE}
                  rowsPerPageOptions={[CUSTOMER_INSTANCES_ROWS_PER_PAGE]}
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  showFirstButton
                  showLastButton
                />
              </Box>
              </Paper>
              <Dialog
                open={heapHelpDialogOpen}
                onClose={() => setHeapHelpDialogOpen(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
                aria-labelledby="heap-help-dialog-title"
              >
                <DialogTitle id="heap-help-dialog-title">Memory analysis (Datadog)</DialogTitle>
                <DialogContent dividers>
                  <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                    The server builds a query like <code>@JVM.Heap.FreeMemory:&lt;…</code> where the threshold is your percentage of
                    each row&apos;s <strong>Max Memory</strong> (MB), rounded to a whole MB (Datadog field is in MB). Logs are pulled
                    for the last {HEAP_RECOMMENDATION_DAYS} days (UTC); violation events are summed per UTC calendar day. Warning if
                    any day has more than 10 events; critical if more than 30. Set{' '}
                    <code>HEAP_RECOMMENDATION_JVM_HEAP_FREE_UNIT=bytes</code> only if the facet is stored in bytes. Use{' '}
                    <code>HEAP_RECOMMENDATION_LEGACY_MESSAGE_PARSE=true</code> only to parse raw message text instead.
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                    Separately, the same lookback counts{' '}
                    <code>service:&lt;Instance Name&gt; &quot;java.lang.OutOfMemoryError&quot; source:tomcat</code> (override with{' '}
                    <code>HEAP_OOM_LOG_QUERY</code> using <code>{'{{instance}}'}</code>). Any match flags the row as critical and
                    recommends increasing heap.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Last Datadog query (copy into Log Explorer)
                  </Typography>
                  {heapLastQueryDebug?.query ? (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {heapLastQueryDebug.isError
                          ? 'Last attempted query (request failed).'
                          : 'Use this string in Log Explorer to reproduce the search.'}
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'ui-monospace, Consolas, monospace'
                        }}
                      >
                        {heapLastQueryDebug.query}
                      </Box>
                      {!heapLastQueryDebug.isError && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                          {heapLastQueryDebug.from && heapLastQueryDebug.to
                            ? `Range (UTC): ${heapLastQueryDebug.from} → ${heapLastQueryDebug.to}`
                            : null}
                          {heapLastQueryDebug.instanceName ? ` · Instance: ${heapLastQueryDebug.instanceName}` : ''}
                          {heapLastQueryDebug.logsProcessed != null
                            ? ` · Log lines pulled from Datadog: ${heapLastQueryDebug.logsProcessed}`
                            : ''}
                          {heapLastQueryDebug.violationEventsTotal != null
                            ? ` · Violation events counted: ${heapLastQueryDebug.violationEventsTotal}`
                            : ''}
                          {heapLastQueryDebug.logsProcessed === 0
                            ? ' — If this stays 0, the query matches no logs (check service name, facets, and time range in Datadog).'
                            : ''}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Not available yet — run <strong>Refresh</strong> on a client to record the last query and time range here.
                    </Typography>
                  )}
                  {heapLastQueryDebug?.oomQuery ? (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                        Last OutOfMemoryError query (copy into Log Explorer)
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'ui-monospace, Consolas, monospace'
                        }}
                      >
                        {heapLastQueryDebug.oomQuery}
                      </Box>
                      {heapLastQueryDebug.oomEventTotal != null ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          OOM log events counted: {heapLastQueryDebug.oomEventTotal}
                        </Typography>
                      ) : null}
                    </>
                  ) : null}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setHeapHelpDialogOpen(false)} variant="contained" size="small">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}
          {tabValue === 2 && productionAnalysisTab === 1 && customerInstances.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Upload or sync customer instances on the <strong>Customer instances</strong> tab to run memory analysis.
            </Alert>
          )}
          {tabValue === 2 && productionAnalysisTab === 2 && customerInstances.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Upload or sync customer instances on the <strong>Customer instances</strong> tab (CBV <strong>Max Threads</strong> required) for
              thread analysis.
            </Alert>
          )}
          {tabValue === 2 && productionAnalysisTab === 2 && customerInstances.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2, width: '100%', minWidth: 0, flex: '1 1 auto' }}>
              {threadBulkProgressLabel && (
                <Paper
                  variant="outlined"
                  sx={(theme) => ({
                    p: 2,
                    borderRadius: 2,
                    borderLeft: 4,
                    borderLeftColor: 'warning.main',
                    bgcolor: alpha(theme.palette.warning.main, 0.06)
                  })}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <PauseCircleOutlineIcon color="warning" sx={{ flexShrink: 0, mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Bulk refresh checkpoint
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                        {threadBulkProgressLabel.done} of {threadBulkProgressLabel.total} instances processed. Use &quot;Resume bulk&quot; if
                        interrupted (same client list as Refresh all).
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(
                          100,
                          threadBulkProgressLabel.total > 0
                            ? (threadBulkProgressLabel.done / threadBulkProgressLabel.total) * 100
                            : 0
                        )}
                        sx={(theme) => ({
                          height: 8,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.grey[500], 0.2),
                          '& .MuiLinearProgress-bar': { borderRadius: 1 }
                        })}
                      />
                    </Box>
                  </Stack>
                </Paper>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 1.25,
                  width: '100%',
                  alignItems: 'stretch'
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    flex: { xs: 'none', lg: '6 1 0%' },
                    minWidth: 0,
                    width: { xs: '100%' },
                    maxWidth: '100%'
                  }}
                >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2, fontWeight: 700, lineHeight: 1.2 }}>
                    Query &amp; actions
                  </Typography>
                  <Tooltip
                    enterTouchDelay={0}
                    title={
                      <Box sx={{ maxWidth: 340 }}>
                        <Typography variant="caption" component="div" sx={{ display: 'block', lineHeight: 1.45 }}>
                          Last <strong>{THREAD_RECOMMENDATION_DAYS} days</strong> (UTC). Logs: <code>@AT</code> &gt;{' '}
                          <strong>{threadPctOfMax}%</strong> of the row&apos;s <strong>Max Threads</strong> (Tomcat). Warn / crit days:{' '}
                          <strong>&gt;2</strong> / <strong>&gt;5</strong> events per UTC day.
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ display: 'block', mt: 1, opacity: 0.92 }}>
                          Click for query shape, thresholds, and last Datadog query.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Thread analysis summary and help"
                      onClick={() => setThreadHelpDialogOpen(true)}
                      sx={{ mt: -0.5 }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Stack spacing={1}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    alignItems={{ md: 'flex-start' }}
                    justifyContent="space-between"
                  >
                    <TextField
                      type="number"
                      size="small"
                      label="Threshold (% of Max Threads)"
                      value={threadPctOfMax}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (e.target.value === '') return;
                        if (Number.isFinite(v)) setThreadPctOfMax(Math.min(99, Math.max(1, Math.round(v))));
                      }}
                      inputProps={{ min: 1, max: 99, step: 1 }}
                      sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title="Violation events when active threads (@AT) exceed this percent of the row’s Max Threads — used in the Datadog log filter."
                              enterTouchDelay={0}
                            >
                              <IconButton size="small" edge="end" aria-label="Threshold details" sx={{ mr: -0.5 }}>
                                <InfoOutlinedIcon fontSize="small" color="action" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                    <Stack direction="row" flexWrap="wrap" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ flex: 1 }}>
                      <Button
                        variant="contained"
                        disableElevation
                        size="medium"
                        onClick={() => startThreadRefreshAll(false)}
                        disabled={refreshingThreadAll || filteredThreadRows.length === 0}
                        startIcon={refreshingThreadAll ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {refreshingThreadAll ? 'Starting bulk…' : 'Refresh all'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => startThreadRefreshAll(true)}
                        disabled={refreshingThreadAll || filteredThreadRows.length === 0 || !threadBulkProgress?.keys?.length}
                        sx={{ textTransform: 'none' }}
                      >
                        Resume bulk
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={handleRefreshProductionAnalysisSnow}
                        disabled={refreshingPaSnow || filteredThreadRows.length === 0}
                        startIcon={refreshingPaSnow ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        {refreshingPaSnow ? 'SNOW…' : 'Refresh SNOW status'}
                      </Button>
                    </Stack>
                  </Stack>
                  {refreshingThreadAll && <LinearProgress sx={{ borderRadius: 1 }} variant="indeterminate" aria-label="Bulk thread refresh starting" />}
                </Stack>
              </Paper>
              <ProductionAnalysisHealthReport stats={threadHealthSummary} snow={threadSnowSummary} />
              </Box>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.50', width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    px: { xs: 1.5, sm: 2 },
                    pt: { xs: 1.5, sm: 2 },
                    pb: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', flex: '1 1 auto' }}>
                    <TextField
                      size="small"
                      label="Filter clients"
                      value={threadClientSearch}
                      onChange={(e) => setThreadClientSearch(e.target.value)}
                      placeholder="Instance or friendly name"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ flex: '1 1 200px', maxWidth: 400, minWidth: 180 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 168 }}>
                      <InputLabel id="pa-region-filter-thread">Region</InputLabel>
                      <Select
                        labelId="pa-region-filter-thread"
                        label="Region"
                        value={effectiveProductionAnalysisRegionFilter}
                        onChange={(e) => setProductionAnalysisRegionFilter(e.target.value)}
                      >
                        <MenuItem value="all">All regions</MenuItem>
                        {productionAnalysisRegionOptions.regions.map((r) => (
                          <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                        {productionAnalysisRegionOptions.hasOnPrem && (
                          <MenuItem value="on-prem">On-Prem</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      type="number"
                      label="Warn days ≥"
                      value={threadFilterWarnMin}
                      onChange={(e) => setThreadFilterWarnMin(e.target.value.replace(/[^\d]/g, ''))}
                      placeholder="Any"
                      inputProps={{ min: 0 }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Crit days ≥"
                      value={threadFilterCritMin}
                      onChange={(e) => setThreadFilterCritMin(e.target.value.replace(/[^\d]/g, ''))}
                      placeholder="Any"
                      inputProps={{ min: 0 }}
                      sx={{ width: 120 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 128 }}>
                      <InputLabel id="pa-health-filter-thread">Status</InputLabel>
                      <Select
                        labelId="pa-health-filter-thread"
                        label="Status"
                        value={productionAnalysisHealthFilter}
                        onChange={(e) => setProductionAnalysisHealthFilter(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                        <MenuItem value="warning">Warning</MenuItem>
                        <MenuItem value="good">OK</MenuItem>
                        <MenuItem value="none">No Data</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 152 }}>
                      <InputLabel id="pa-snow-filter-thread">SNOW</InputLabel>
                      <Select
                        labelId="pa-snow-filter-thread"
                        label="SNOW"
                        value={productionAnalysisSnowFilter}
                        onChange={(e) => setProductionAnalysisSnowFilter(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="only">Only SNOW</MenuItem>
                        <MenuItem value="not">Not SNOW</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={selectAllFilteredThread}
                      disabled={filteredThreadRows.length === 0}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Select all ({filteredThreadRows.length})
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={clearThreadSelection}
                      disabled={threadSelectionCount === 0}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear selection
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => openPaBulkSnowDialog(PA_ANALYSIS.THREADS)}
                      disabled={!isAuthenticated() || threadSelectionCount === 0}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Set SNOW ({threadSelectionCount})
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportThreadTableCsv}
                      disabled={filteredThreadRows.length === 0}
                      sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                    >
                      Export ({filteredThreadRows.length})
                    </Button>
                  </Box>
                </Box>
                <TableContainer sx={{ maxWidth: '100%', overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 1120, width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          padding="checkbox"
                          sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        >
                          <Checkbox
                            size="small"
                            indeterminate={threadPageIndeterminate}
                            checked={threadPageAllSelected}
                            onChange={toggleThreadPageSelection}
                            disabled={threadPageStableKeys.length === 0}
                            inputProps={{ 'aria-label': 'Select all clients on this page' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Client
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Region
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        >
                          Thr. %
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Max threads
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Active Threads &gt;
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Max / day
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Warn days
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Crit days
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Recommendation
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider', minWidth: 168 }}>
                          SNOW
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedThreadRows.map(({ row, stableKey, entry }) => {
                        const sum = entry?.summary;
                        const level = entry?.recommendation?.level;
                        const recColor =
                          level === 'critical' ? 'error.main' : level === 'warning' ? 'warning.main' : 'text.secondary';
                        const busy = refreshingThreadStableKey === stableKey;
                        const clientLabel =
                          String(row['Friendly name'] ?? '').trim() ||
                          String(row['Instance Name'] ?? row.instanceName ?? '').trim() ||
                          '(unknown)';
                        const clientHref = clientInstanceUrl(row);
                        const warnDays = sum?.warningDaysTotal;
                        const critDays = sum?.criticalDaysTotal;
                        const maxThrCol = row['Max Threads'];
                        const thr = entry?.thresholds?.atThreshold;
                        const regionLabel = getRegionValue(row) || '—';
                        return (
                          <TableRow key={stableKey} hover sx={(theme) => ({ ...(busy ? { bgcolor: alpha(theme.palette.primary.main, 0.07) } : {}) })}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={!!paRowSelection.threads[stableKey]}
                                onChange={() => {
                                  setPaRowSelection((prev) => {
                                    const next = { ...prev, threads: { ...prev.threads } };
                                    if (next.threads[stableKey]) delete next.threads[stableKey];
                                    else next.threads[stableKey] = true;
                                    return next;
                                  });
                                }}
                                inputProps={{ 'aria-label': `Select ${clientLabel}` }}
                              />
                            </TableCell>
                            <TableCell>
                              {clientHref ? (
                                <MuiLink href={clientHref} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
                                  {clientLabel}
                                </MuiLink>
                            ) : (
                              clientLabel
                            )}
                          </TableCell>
                            <TableCell sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{regionLabel}</TableCell>
                            <TableCell align="right">
                              {entry?.pctOfMax != null ? `${entry.pctOfMax}%` : '—'}
                            </TableCell>
                            <TableCell align="right">{maxThrCol != null && String(maxThrCol).trim() !== '' ? maxThrCol : '—'}</TableCell>
                            <TableCell align="right">{thr != null ? thr : '—'}</TableCell>
                            <TableCell align="right">{sum?.maxDailyCount != null ? sum.maxDailyCount : '—'}</TableCell>
                            <TableCell align="right">{sum?.violationEventsTotal != null ? sum.violationEventsTotal : '—'}</TableCell>
                            <TableCell align="right">
                              <Box component="span" sx={(theme) => heapWarnDaysCellSx(theme, warnDays)}>
                                {warnDays != null ? warnDays : '—'}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box component="span" sx={(theme) => heapCritDaysCellSx(theme, critDays)}>
                                {critDays != null ? critDays : '—'}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {entry?.error ? (
                                <Typography color="error" variant="body2">
                                  {entry.error}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ fontWeight: 600, color: recColor }}>
                                  {entry?.recommendation?.title || '—'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8125rem', verticalAlign: 'top', maxWidth: 220 }}>
                              {(() => {
                                const sn = productionAnalysisSnowCellContent(productionAnalysisNotes[stableKey], PA_ANALYSIS.THREADS);
                                return (
                                  <Tooltip title={sn.error || ''}>
                                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                      {sn.raisedLine ? <span>{sn.raisedLine}</span> : null}
                                      {sn.executedLine ? <span>{sn.executedLine}</span> : null}
                                    </Box>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Refresh from Datadog">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => refreshThreadForRow(row)}
                                    disabled={busy || !String(row['Instance Name'] ?? row.instanceName ?? '').trim()}
                                    aria-label="Refresh thread analysis"
                                  >
                                    {busy ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Daily violations chart">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => setThreadDailyDialog({ entry, label: stableKey })}
                                    disabled={!entry?.dailyViolationTotals?.length}
                                    aria-label="Open daily chart"
                                  >
                                    <ShowChartIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title={(() => {
                                  const sl = getPaAnalysisSlice(productionAnalysisNotes[stableKey], PA_ANALYSIS.THREADS);
                                  return sl.serviceNowNumber?.trim() || sl.comment?.trim()
                                    ? `ServiceNow / comment saved for this instance (threads)`
                                    : 'More actions';
                                })()}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPaActionMenuAnchor(e.currentTarget);
                                      setPaActionMenuContext({ row, stableKey, analysisType: PA_ANALYSIS.THREADS });
                                    }}
                                    aria-label="Open actions menu"
                                    sx={{
                                      ...(getPaAnalysisSlice(productionAnalysisNotes[stableKey], PA_ANALYSIS.THREADS).comment?.trim() ||
                                      getPaAnalysisSlice(productionAnalysisNotes[stableKey], PA_ANALYSIS.THREADS).serviceNowNumber?.trim()
                                        ? { color: 'secondary.main' }
                                        : {})
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: 1,
                    py: 0.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}
                >
                  <TablePagination
                    component="div"
                    count={filteredThreadRows.length}
                    page={threadPage}
                    onPageChange={(_, newPage) => setThreadPage(newPage)}
                    rowsPerPage={CUSTOMER_INSTANCES_ROWS_PER_PAGE}
                    rowsPerPageOptions={[CUSTOMER_INSTANCES_ROWS_PER_PAGE]}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Paper>
              <Dialog
                open={threadHelpDialogOpen}
                onClose={() => setThreadHelpDialogOpen(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
                aria-labelledby="thread-help-dialog-title"
              >
                <DialogTitle id="thread-help-dialog-title">Thread analysis (Datadog)</DialogTitle>
                <DialogContent dividers>
                  <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                    Default query: <code>service:&lt;instance&gt; source:tomcat @AT:&gt;T</code> where <strong>T</strong> is{' '}
                    <strong>Threshold %</strong> of <strong>Max Threads</strong> (CBV column), rounded — same idea as Memory <strong>Thr. %</strong>.
                    The control above defaults to <strong>75%</strong>; server default without a request body is{' '}
                    <code>THREAD_RECOMMENDATION_PCT_OF_MAX</code> (0–1 fraction, e.g. <code>0.75</code>). Override with{' '}
                    <code>THREAD_RECOMMENDATION_LOG_QUERY</code> and <code>{'{{instance}}'}</code>, <code>{'{{atThreshold}}'}</code>,{' '}
                    <code>{'{{maxThreads}}'}</code>. Facet: <code>THREAD_RECOMMENDATION_AT_ATTR</code> (default <code>@AT</code>).
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>UTC days:</strong> warn if &gt;2 events/day; crit if &gt;5 (<code>THREAD_RECOMMENDATION_WARN_DAILY</code> /{' '}
                    <code>CRIT_DAILY</code>). Window: <strong>7 days</strong>.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Last Datadog query
                  </Typography>
                  {threadLastQueryDebug?.query ? (
                    <>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'ui-monospace, Consolas, monospace'
                        }}
                      >
                        {threadLastQueryDebug.query}
                      </Box>
                      {!threadLastQueryDebug.isError && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                          {threadLastQueryDebug.from && threadLastQueryDebug.to
                            ? `Range (UTC): ${threadLastQueryDebug.from} → ${threadLastQueryDebug.to}`
                            : null}
                          {threadLastQueryDebug.instanceName ? ` · Instance: ${threadLastQueryDebug.instanceName}` : ''}
                          {threadLastQueryDebug.logsProcessed != null ? ` · Log lines: ${threadLastQueryDebug.logsProcessed}` : ''}
                          {threadLastQueryDebug.violationEventsTotal != null
                            ? ` · Violations: ${threadLastQueryDebug.violationEventsTotal}`
                            : ''}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Run <strong>Refresh</strong> on a client to record the last query here.
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setThreadHelpDialogOpen(false)} variant="contained" size="small">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}
          {tabValue === 2 && productionAnalysisTab === 3 && customerInstances.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Upload or sync customer instances on the <strong>Customer instances</strong> tab (CBV <strong>Max DB Connections</strong> required)
              to run DB connection analysis.
            </Alert>
          )}
          {tabValue === 2 && productionAnalysisTab === 3 && customerInstances.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2, width: '100%', minWidth: 0, flex: '1 1 auto' }}>
              {dbConnBulkProgressLabel && (
                <Paper
                  variant="outlined"
                  sx={(theme) => ({
                    p: 2,
                    borderRadius: 2,
                    borderLeft: 4,
                    borderLeftColor: 'warning.main',
                    bgcolor: alpha(theme.palette.warning.main, 0.06)
                  })}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <PauseCircleOutlineIcon color="warning" sx={{ flexShrink: 0, mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Bulk refresh checkpoint
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                        {dbConnBulkProgressLabel.done} of {dbConnBulkProgressLabel.total} instances processed. Use &quot;Resume bulk&quot; if
                        interrupted (same client list as Refresh all).
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(
                          100,
                          dbConnBulkProgressLabel.total > 0
                            ? (dbConnBulkProgressLabel.done / dbConnBulkProgressLabel.total) * 100
                            : 0
                        )}
                        sx={(theme) => ({
                          height: 8,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.grey[500], 0.2),
                          '& .MuiLinearProgress-bar': { borderRadius: 1 }
                        })}
                      />
                    </Box>
                  </Stack>
                </Paper>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 1.25,
                  width: '100%',
                  alignItems: 'stretch'
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    flex: { xs: 'none', lg: '6 1 0%' },
                    minWidth: 0,
                    width: { xs: '100%' },
                    maxWidth: '100%'
                  }}
                >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2, fontWeight: 700, lineHeight: 1.2 }}>
                    Query &amp; actions
                  </Typography>
                  <Tooltip
                    enterTouchDelay={0}
                    title={
                      <Box sx={{ maxWidth: 340 }}>
                        <Typography variant="caption" component="div" sx={{ display: 'block', lineHeight: 1.45 }}>
                          Last <strong>{DB_CONNECTION_RECOMMENDATION_DAYS} days</strong> (UTC). Logs: <code>@CADBC</code> &gt;{' '}
                          <strong>{dbConnPctOfMax}%</strong> of the row&apos;s <strong>Max DB Connections</strong>. Warn / crit:{' '}
                          <strong>&gt;2</strong> / <strong>&gt;5</strong> violation events per UTC day.
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ display: 'block', mt: 1, opacity: 0.92 }}>
                          Click for query shape, thresholds, and last Datadog query.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="DB connection analysis summary and help"
                      onClick={() => setDbConnHelpDialogOpen(true)}
                      sx={{ mt: -0.5 }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Stack spacing={1}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    alignItems={{ md: 'flex-start' }}
                    justifyContent="space-between"
                  >
                    <TextField
                      type="number"
                      size="small"
                      label="Threshold (% of Max DB Connections)"
                      value={dbConnPctOfMax}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (e.target.value === '') return;
                        if (Number.isFinite(v)) setDbConnPctOfMax(Math.min(99, Math.max(1, Math.round(v))));
                      }}
                      inputProps={{ min: 1, max: 99, step: 1 }}
                      sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title="Violation events when active DB connections (@CADBC) exceed this percent of the row’s Max DB Connections — used in the Datadog log filter."
                              enterTouchDelay={0}
                            >
                              <IconButton size="small" edge="end" aria-label="Threshold details" sx={{ mr: -0.5 }}>
                                <InfoOutlinedIcon fontSize="small" color="action" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                    <Stack direction="row" flexWrap="wrap" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ flex: 1 }}>
                      <Button
                        variant="contained"
                        disableElevation
                        size="medium"
                        onClick={() => startDbConnRefreshAll(false)}
                        disabled={refreshingDbConnAll || filteredDbConnRows.length === 0}
                        startIcon={refreshingDbConnAll ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {refreshingDbConnAll ? 'Starting bulk…' : 'Refresh all'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => startDbConnRefreshAll(true)}
                        disabled={
                          refreshingDbConnAll || filteredDbConnRows.length === 0 || !dbConnBulkProgress?.keys?.length
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        Resume bulk
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={handleRefreshProductionAnalysisSnow}
                        disabled={refreshingPaSnow || filteredDbConnRows.length === 0}
                        startIcon={refreshingPaSnow ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        {refreshingPaSnow ? 'SNOW…' : 'Refresh SNOW status'}
                      </Button>
                    </Stack>
                  </Stack>
                  {refreshingDbConnAll && (
                    <LinearProgress sx={{ borderRadius: 1 }} variant="indeterminate" aria-label="Bulk DB connection refresh starting" />
                  )}
                </Stack>
              </Paper>
              <ProductionAnalysisHealthReport stats={dbConnHealthSummary} snow={dbConnSnowSummary} />
              </Box>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.50', width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    px: { xs: 1.5, sm: 2 },
                    pt: { xs: 1.5, sm: 2 },
                    pb: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', flex: '1 1 auto' }}>
                    <TextField
                      size="small"
                      label="Filter clients"
                      value={dbConnClientSearch}
                      onChange={(e) => setDbConnClientSearch(e.target.value)}
                      placeholder="Instance or friendly name"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ flex: '1 1 200px', maxWidth: 400, minWidth: 180 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 168 }}>
                      <InputLabel id="pa-region-filter-db">Region</InputLabel>
                      <Select
                        labelId="pa-region-filter-db"
                        label="Region"
                        value={effectiveProductionAnalysisRegionFilter}
                        onChange={(e) => setProductionAnalysisRegionFilter(e.target.value)}
                      >
                        <MenuItem value="all">All regions</MenuItem>
                        {productionAnalysisRegionOptions.regions.map((r) => (
                          <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                        {productionAnalysisRegionOptions.hasOnPrem && (
                          <MenuItem value="on-prem">On-Prem</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      type="number"
                      label="Warn days ≥"
                      value={dbConnFilterWarnMin}
                      onChange={(e) => setDbConnFilterWarnMin(e.target.value.replace(/[^\d]/g, ''))}
                      placeholder="Any"
                      inputProps={{ min: 0 }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Crit days ≥"
                      value={dbConnFilterCritMin}
                      onChange={(e) => setDbConnFilterCritMin(e.target.value.replace(/[^\d]/g, ''))}
                      placeholder="Any"
                      inputProps={{ min: 0 }}
                      sx={{ width: 120 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 128 }}>
                      <InputLabel id="pa-health-filter-db">Status</InputLabel>
                      <Select
                        labelId="pa-health-filter-db"
                        label="Status"
                        value={productionAnalysisHealthFilter}
                        onChange={(e) => setProductionAnalysisHealthFilter(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                        <MenuItem value="warning">Warning</MenuItem>
                        <MenuItem value="good">OK</MenuItem>
                        <MenuItem value="none">No Data</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 152 }}>
                      <InputLabel id="pa-snow-filter-db">SNOW</InputLabel>
                      <Select
                        labelId="pa-snow-filter-db"
                        label="SNOW"
                        value={productionAnalysisSnowFilter}
                        onChange={(e) => setProductionAnalysisSnowFilter(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="only">Only SNOW</MenuItem>
                        <MenuItem value="not">Not SNOW</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={selectAllFilteredDb}
                      disabled={filteredDbConnRows.length === 0}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Select all ({filteredDbConnRows.length})
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={clearDbSelection}
                      disabled={dbSelectionCount === 0}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear selection
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => openPaBulkSnowDialog(PA_ANALYSIS.DB)}
                      disabled={!isAuthenticated() || dbSelectionCount === 0}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Set SNOW ({dbSelectionCount})
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportDbConnTableCsv}
                      disabled={filteredDbConnRows.length === 0}
                      sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                    >
                      Export ({filteredDbConnRows.length})
                    </Button>
                  </Box>
                </Box>
                <TableContainer sx={{ maxWidth: '100%', overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 1120, width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          padding="checkbox"
                          sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        >
                          <Checkbox
                            size="small"
                            indeterminate={dbPageIndeterminate}
                            checked={dbPageAllSelected}
                            onChange={toggleDbPageSelection}
                            disabled={dbPageStableKeys.length === 0}
                            inputProps={{ 'aria-label': 'Select all clients on this page' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Client
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Region
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}
                        >
                          Thr. %
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Max DB
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Active DB connections &gt;
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Max / day
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Warn days
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Crit days
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Recommendation
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider', minWidth: 168 }}>
                          SNOW
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.100', py: 1.25, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedDbConnRows.map(({ row, stableKey, entry }) => {
                        const sum = entry?.summary;
                        const level = entry?.recommendation?.level;
                        const recColor =
                          level === 'critical' ? 'error.main' : level === 'warning' ? 'warning.main' : 'text.secondary';
                        const busy = refreshingDbConnStableKey === stableKey;
                        const clientLabel =
                          String(row['Friendly name'] ?? '').trim() ||
                          String(row['Instance Name'] ?? row.instanceName ?? '').trim() ||
                          '(unknown)';
                        const clientHref = clientInstanceUrl(row);
                        const warnDays = sum?.warningDaysTotal;
                        const critDays = sum?.criticalDaysTotal;
                        const maxDbCol = row['Max DB Connections'];
                        const thr = entry?.thresholds?.cadbcThreshold;
                        const regionLabel = getRegionValue(row) || '—';
                        return (
                          <TableRow key={stableKey} hover sx={(theme) => ({ ...(busy ? { bgcolor: alpha(theme.palette.primary.main, 0.07) } : {}) })}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={!!paRowSelection.db[stableKey]}
                                onChange={() => {
                                  setPaRowSelection((prev) => {
                                    const next = { ...prev, db: { ...prev.db } };
                                    if (next.db[stableKey]) delete next.db[stableKey];
                                    else next.db[stableKey] = true;
                                    return next;
                                  });
                                }}
                                inputProps={{ 'aria-label': `Select ${clientLabel}` }}
                              />
                            </TableCell>
                            <TableCell>
                              {clientHref ? (
                                <MuiLink href={clientHref} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
                                  {clientLabel}
                                </MuiLink>
                              ) : (
                                clientLabel
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{regionLabel}</TableCell>
                            <TableCell align="right">
                              {entry?.pctOfMax != null ? `${entry.pctOfMax}%` : '—'}
                            </TableCell>
                            <TableCell align="right">{maxDbCol != null && String(maxDbCol).trim() !== '' ? maxDbCol : '—'}</TableCell>
                            <TableCell align="right">{thr != null ? thr : '—'}</TableCell>
                            <TableCell align="right">{sum?.maxDailyCount != null ? sum.maxDailyCount : '—'}</TableCell>
                            <TableCell align="right">{sum?.violationEventsTotal != null ? sum.violationEventsTotal : '—'}</TableCell>
                            <TableCell align="right">
                              <Box component="span" sx={(theme) => heapWarnDaysCellSx(theme, warnDays)}>
                                {warnDays != null ? warnDays : '—'}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Box component="span" sx={(theme) => heapCritDaysCellSx(theme, critDays)}>
                                {critDays != null ? critDays : '—'}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {entry?.error ? (
                                <Typography color="error" variant="body2">
                                  {entry.error}
                                </Typography>
                              ) : (
                                <Typography variant="body2" sx={{ fontWeight: 600, color: recColor }}>
                                  {entry?.recommendation?.title || '—'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8125rem', verticalAlign: 'top', maxWidth: 220 }}>
                              {(() => {
                                const sn = productionAnalysisSnowCellContent(productionAnalysisNotes[stableKey], PA_ANALYSIS.DB);
                                return (
                                  <Tooltip title={sn.error || ''}>
                                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                      {sn.raisedLine ? <span>{sn.raisedLine}</span> : null}
                                      {sn.executedLine ? <span>{sn.executedLine}</span> : null}
                                    </Box>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Refresh from Datadog">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => refreshDbConnForRow(row)}
                                    disabled={busy || !String(row['Instance Name'] ?? row.instanceName ?? '').trim()}
                                    aria-label="Refresh DB connection analysis"
                                  >
                                    {busy ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Daily violations chart">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => setDbConnDailyDialog({ entry, label: stableKey })}
                                    disabled={!entry?.dailyViolationTotals?.length}
                                    aria-label="Open daily chart"
                                  >
                                    <ShowChartIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title={(() => {
                                  const sl = getPaAnalysisSlice(productionAnalysisNotes[stableKey], PA_ANALYSIS.DB);
                                  return sl.serviceNowNumber?.trim() || sl.comment?.trim()
                                    ? `ServiceNow / comment saved for this instance (DB connections)`
                                    : 'More actions';
                                })()}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPaActionMenuAnchor(e.currentTarget);
                                      setPaActionMenuContext({ row, stableKey, analysisType: PA_ANALYSIS.DB });
                                    }}
                                    aria-label="Open actions menu"
                                    sx={{
                                      ...(getPaAnalysisSlice(productionAnalysisNotes[stableKey], PA_ANALYSIS.DB).comment?.trim() ||
                                      getPaAnalysisSlice(productionAnalysisNotes[stableKey], PA_ANALYSIS.DB).serviceNowNumber?.trim()
                                        ? { color: 'secondary.main' }
                                        : {})
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: 1,
                    py: 0.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                  }}
                >
                  <TablePagination
                    component="div"
                    count={filteredDbConnRows.length}
                    page={dbConnPage}
                    onPageChange={(_, newPage) => setDbConnPage(newPage)}
                    rowsPerPage={CUSTOMER_INSTANCES_ROWS_PER_PAGE}
                    rowsPerPageOptions={[CUSTOMER_INSTANCES_ROWS_PER_PAGE]}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Paper>
              <Dialog
                open={dbConnHelpDialogOpen}
                onClose={() => setDbConnHelpDialogOpen(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
                aria-labelledby="db-conn-help-dialog-title"
              >
                <DialogTitle id="db-conn-help-dialog-title">DB connection analysis (Datadog)</DialogTitle>
                <DialogContent dividers>
                  <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                    Uses the same Logs Search flow as memory analysis. The default query is{' '}
                    <code>service:&lt;instance&gt; source:tomcat @CADBC:&gt;T</code> where <strong>T</strong> is{' '}
                    <strong>Threshold %</strong> of the instance&apos;s <strong>Max DB Connections</strong> (CBV column), rounded. The control
                    above defaults to <strong>75%</strong>; server default without a request body is{' '}
                    <code>DB_CONNECTION_RECOMMENDATION_PCT_OF_MAX</code>. Override with <code>DB_CONNECTION_RECOMMENDATION_LOG_QUERY</code> and
                    placeholders <code>{'{{instance}}'}</code>, <code>{'{{cadbcThreshold}}'}</code>, <code>{'{{maxDbConnections}}'}</code>.
                    Attribute name: <code>DB_CONNECTION_RECOMMENDATION_CADBC_ATTR</code> (default <code>@CADBC</code>).
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>UTC calendar days:</strong> violation events are summed per day. <strong>Warn days</strong> count days with &gt;2
                    events; <strong>Crit days</strong> count days with &gt;5 events (configurable via{' '}
                    <code>DB_CONNECTION_RECOMMENDATION_WARN_DAILY</code> / <code>CRIT_DAILY</code>). Window: <strong>7 days</strong> for now.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Last Datadog query (copy into Log Explorer)
                  </Typography>
                  {dbConnLastQueryDebug?.query ? (
                    <>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'ui-monospace, Consolas, monospace'
                        }}
                      >
                        {dbConnLastQueryDebug.query}
                      </Box>
                      {!dbConnLastQueryDebug.isError && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                          {dbConnLastQueryDebug.from && dbConnLastQueryDebug.to
                            ? `Range (UTC): ${dbConnLastQueryDebug.from} → ${dbConnLastQueryDebug.to}`
                            : null}
                          {dbConnLastQueryDebug.instanceName ? ` · Instance: ${dbConnLastQueryDebug.instanceName}` : ''}
                          {dbConnLastQueryDebug.logsProcessed != null
                            ? ` · Log lines: ${dbConnLastQueryDebug.logsProcessed}`
                            : ''}
                          {dbConnLastQueryDebug.violationEventsTotal != null
                            ? ` · Violations counted: ${dbConnLastQueryDebug.violationEventsTotal}`
                            : ''}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Run <strong>Refresh</strong> on a client to record the last query here.
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDbConnHelpDialogOpen(false)} variant="contained" size="small">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}
          {tabValue === 2 && productionAnalysisTab === 4 && (
            <Box sx={{ pb: 2, width: '100%' }}>
              <ProductionProgressTab />
            </Box>
          )}
          {tabValue === 2 && productionAnalysisTab === 5 && (
            <Box sx={{ pb: 2, width: '100%' }}>
              <MemoryImprovementsTab />
            </Box>
          )}
          {tabValue === 2 && customerInstances.length > 0 && (
            <>
              <Menu
                anchorEl={paActionMenuAnchor}
                open={Boolean(paActionMenuAnchor)}
                onClose={() => {
                  setPaActionMenuAnchor(null);
                  setPaActionMenuContext(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    const ctx = paActionMenuContext;
                    setPaActionMenuAnchor(null);
                    setPaActionMenuContext(null);
                    if (ctx?.analysisType) {
                      const n = productionAnalysisNotes[ctx.stableKey];
                      const slice = getPaAnalysisSlice(n, ctx.analysisType);
                      setPaNoteDialog({
                        row: ctx.row,
                        stableKey: ctx.stableKey,
                        analysisType: ctx.analysisType,
                        comment: slice.comment,
                        serviceNowNumber: slice.serviceNowNumber,
                        snowRaisedOn: slice.snowRaisedOn,
                        snowExecutedOn: slice.snowExecutedOn,
                        snowSyncError: slice.snowSyncError
                      });
                    }
                  }}
                >
                  <CommentIcon fontSize="small" sx={{ mr: 1, opacity: 0.85 }} />
                  {paActionMenuContext?.analysisType === PA_ANALYSIS.MEMORY ? 'Comment' : 'Comment & ServiceNow'}
                </MenuItem>
              </Menu>
              <Dialog
                open={Boolean(paNoteDialog)}
                onClose={() => !paNoteSaving && setPaNoteDialog(null)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="pa-note-dialog-title"
              >
                <DialogTitle id="pa-note-dialog-title">
                  {paNoteDialog
                    ? `${paNoteDialog.analysisType === PA_ANALYSIS.MEMORY ? 'Comment' : 'Comment & ServiceNow'} — ${
                        PA_ANALYSIS_LABEL[paNoteDialog.analysisType] || 'Production analysis'
                      }`
                    : 'Comment'}
                </DialogTitle>
                <DialogContent dividers>
                  {paNoteDialog && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {String(paNoteDialog.row['Friendly name'] ?? '').trim() ||
                          String(paNoteDialog.row['Instance Name'] ?? paNoteDialog.row.instanceName ?? '').trim() ||
                          '(unknown)'}
                      </Typography>
                      {paNoteDialog.analysisType !== PA_ANALYSIS.MEMORY && (
                        <>
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2, display: 'block' }}>
                            {(() => {
                              const sn = productionAnalysisSnowCellContent(
                                {
                                  analyses: {
                                    [paNoteDialog.analysisType]: {
                                      snowRaisedOn: paNoteDialog.snowRaisedOn,
                                      snowExecutedOn: paNoteDialog.snowExecutedOn,
                                      snowSyncError: paNoteDialog.snowSyncError
                                    }
                                  }
                                },
                                paNoteDialog.analysisType
                              );
                              const lines = [sn.raisedLine, sn.executedLine].filter(Boolean);
                              return lines.length ? lines.join(' · ') : '—';
                            })()}
                          </Typography>
                          {paNoteDialog.snowSyncError ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              {paNoteDialog.snowSyncError}
                            </Alert>
                          ) : null}
                          <TextField
                            label="ServiceNow number"
                            value={paNoteDialog.serviceNowNumber}
                            onChange={(e) =>
                              setPaNoteDialog((d) => (d ? { ...d, serviceNowNumber: e.target.value } : d))
                            }
                            fullWidth
                            size="small"
                            placeholder="e.g. INC00123456 or RITM0115375"
                            sx={{ mb: 2 }}
                          />
                        </>
                      )}
                      <TextField
                        label="Comment"
                        value={paNoteDialog.comment}
                        onChange={(e) =>
                          setPaNoteDialog((d) => (d ? { ...d, comment: e.target.value } : d))
                        }
                        fullWidth
                        multiline
                        minRows={4}
                      />
                      {!isAuthenticated() && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Sign in to save notes to the server.
                        </Alert>
                      )}
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setPaNoteDialog(null)} disabled={paNoteSaving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveProductionAnalysisNote}
                    disabled={paNoteSaving || !isAuthenticated() || !paNoteDialog}
                  >
                    {paNoteSaving ? 'Saving…' : 'Save'}
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog
                open={Boolean(paBulkSnowDialog)}
                onClose={() => !paBulkSnowSaving && setPaBulkSnowDialog(null)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="pa-bulk-snow-dialog-title"
              >
                <DialogTitle id="pa-bulk-snow-dialog-title">
                  {paBulkSnowDialog
                    ? `Set ServiceNow for ${paBulkSnowDialog.keys.length} client${
                        paBulkSnowDialog.keys.length === 1 ? '' : 's'
                      } — ${PA_ANALYSIS_LABEL[paBulkSnowDialog.analysisType] || 'Production analysis'}`
                    : 'Set ServiceNow'}
                </DialogTitle>
                <DialogContent dividers>
                  {paBulkSnowDialog && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The same ServiceNow ticket number and optional comment will be saved for every selected row in
                        this analysis tab.
                      </Typography>
                      <TextField
                        label="ServiceNow number"
                        value={paBulkSnowDialog.serviceNowNumber}
                        onChange={(e) =>
                          setPaBulkSnowDialog((d) => (d ? { ...d, serviceNowNumber: e.target.value } : d))
                        }
                        fullWidth
                        size="small"
                        placeholder="e.g. INC00123456 or RITM0115375"
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Comment"
                        value={paBulkSnowDialog.comment}
                        onChange={(e) =>
                          setPaBulkSnowDialog((d) => (d ? { ...d, comment: e.target.value } : d))
                        }
                        fullWidth
                        multiline
                        minRows={3}
                      />
                      {!isAuthenticated() && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Sign in to save notes to the server.
                        </Alert>
                      )}
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setPaBulkSnowDialog(null)} disabled={paBulkSnowSaving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSavePaBulkSnow}
                    disabled={
                      paBulkSnowSaving ||
                      !isAuthenticated() ||
                      !paBulkSnowDialog ||
                      !String(paBulkSnowDialog.serviceNowNumber || '').trim()
                    }
                  >
                    {paBulkSnowSaving ? 'Saving…' : 'Save for all selected'}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
          {instanceBulkProgress?.status === 'running' && tabValue === 1 && customerInstancesViewTab === 0 && (
            <Box sx={{ mb: 1.5, px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {stoppingBulk
                    ? `Stopping — finishing current item… ${instanceBulkProgress.completedInstances ?? 0} / ${instanceBulkProgress.totalInstances ?? 0} done`
                    : `Refreshing ${(instanceBulkProgress.currentOp || '').toUpperCase()} — ${instanceBulkProgress.completedInstances ?? 0} / ${instanceBulkProgress.totalInstances ?? 0} instances`}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={stoppingBulk}
                  onClick={handleStopBulk}
                  sx={{ py: 0, px: 1, minWidth: 0, fontSize: '0.7rem', lineHeight: 1.6 }}
                >
                  {stoppingBulk ? 'Stopping…' : 'Stop'}
                </Button>
              </Box>
              <LinearProgress
                variant={instanceBulkProgress.totalInstances > 0 ? 'determinate' : 'indeterminate'}
                value={instanceBulkProgress.totalInstances > 0
                  ? Math.min(100, ((instanceBulkProgress.completedInstances ?? 0) / instanceBulkProgress.totalInstances) * 100)
                  : 0}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
          )}
          {customerInstances.length > 0 && tabValue !== 2 && !(tabValue === 1 && (customerInstancesViewTab === 1 || customerInstancesViewTab === 2 || customerInstancesViewTab === 3)) && (
            <>
              <TableContainer sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      {effectiveTableColumns.map((key) => {
                        const isNameOrDomain = key === 'Friendly name' || key === 'Instance Domain';
                        const isWijmoHeader = key === 'Wijmo(ASP)' || key === 'Wijmo(System)';
                        const headerLabel = key === 'Wijmo(ASP)'
                          ? (<><span>Wijmo</span><br /><span>(ASP)</span></>)
                          : key === 'Wijmo(System)'
                            ? (<><span>Wijmo</span><br /><span>(System)</span></>)
                            : key === 'Friendly name'
                              ? 'Name'
                              : key;
                        return (
                          <TableCell
                            key={key}
                            sx={{
                              fontWeight: 700,
                              bgcolor: 'grey.200',
                              ...(isNameOrDomain
                                ? {
                                    whiteSpace: 'normal',
                                    ...(customerInstancesViewTab === 1
                                      ? key === 'Friendly name'
                                        ? { width: '6%', maxWidth: 96, minWidth: 64 }
                                        : { width: '9%', maxWidth: 132, minWidth: 80 }
                                      : { width: key === 'Friendly name' ? '12%' : '14%' })
                                  }
                                : isWijmoHeader
                                  ? { whiteSpace: 'normal', lineHeight: 1.2, minWidth: 72, width: 72 }
                                  : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 })
                            }}
                          >
                            <TableSortLabel
                              active={customerSortBy === key}
                              direction={customerSortBy === key ? customerSortOrder : 'asc'}
                              onClick={() => handleCustomerSort(key)}
                              sx={isWijmoHeader ? { whiteSpace: 'normal', '& .MuiTableSortLabel-icon': { marginLeft: 0 } } : undefined}
                            >
                              <Box component="span" sx={isWijmoHeader ? { display: 'block' } : undefined}>
                                {headerLabel}
                              </Box>
                            </TableSortLabel>
                          </TableCell>
                        );
                      })}
                      {customerInstancesViewTab === 0 && (
                        <TableCell
                          key="cdn"
                          sx={{ fontWeight: 700, bgcolor: 'grey.200', whiteSpace: 'nowrap', minWidth: 90, width: 90 }}
                        >
                          CDN
                        </TableCell>
                      )}
                      <TableCell
                        key="cbv-resource-history"
                        sx={{ fontWeight: 700, bgcolor: 'grey.200', width: 40, minWidth: 40, maxWidth: 40, px: 0.25, textAlign: 'center' }}
                      >
                        <Tooltip title="History when Max Memory, Max Threads, or Max DB Connections changed (CBV sync)" arrow>
                          <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 700, lineHeight: 1.1, display: 'block' }}>
                            Hist.
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {showCustomerInstanceRowActions && (
                        <TableCell key="actions" sx={{ fontWeight: 700, bgcolor: 'grey.200', whiteSpace: 'nowrap', width: 148 }}>
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                    {customerInstancesViewTab === 1 && (
                      <TableRow>
                        {effectiveTableColumns.map((key) => (
                          <TableCell
                            key={`infra-filter-${key}`}
                            sx={{
                              py: 0.75,
                              bgcolor: 'grey.100',
                              verticalAlign: 'top',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              ...(key === 'Friendly name' ? { width: '6%', maxWidth: 96, minWidth: 64 } : {}),
                              ...(key === 'Instance Domain' ? { width: '9%', maxWidth: 132, minWidth: 80 } : {})
                            }}
                          >
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Filter…"
                              value={infraColumnFilters[key] ?? ''}
                              onChange={(e) =>
                                setInfraColumnFilters((prev) => ({
                                  ...prev,
                                  [key]: e.target.value
                                }))
                              }
                              InputProps={{ sx: { fontSize: '0.75rem' } }}
                            />
                          </TableCell>
                        ))}
                        <TableCell sx={{ bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider', width: 40, minWidth: 40 }} />
                        {showCustomerInstanceRowActions && (
                          <TableCell sx={{ bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }} />
                        )}
                      </TableRow>
                    )}
                  </TableHead>
                  <TableBody>
                    {paginatedCustomerInstances.map((row, i) => (
                      <TableRow key={row._id || i}>
                        {effectiveTableColumns.map((key) => {
                          const val = formatCellValue(row[key]);
                          const isNr = (releaseColumnKey && key === releaseColumnKey && val === 'NR') || (key === 'Instance' && val === 'NR') || (key === 'CIAM' && val === 'NR') || (key === 'Wijmo(ASP)' && val === 'NR') || (key === 'Wijmo(System)' && val === 'NR');
                          const isNameOrDomain = key === 'Friendly name' || key === 'Instance Domain';
                          const infraNarrowNameDomain = customerInstancesViewTab === 1 && isNameOrDomain;
                          return (
                            <TableCell
                              key={key}
                              sx={{
                                ...(isNameOrDomain
                                  ? {
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      ...(infraNarrowNameDomain
                                        ? {
                                            maxWidth: key === 'Friendly name' ? 96 : 132,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                          }
                                        : { overflow: 'visible' })
                                    }
                                  : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }),
                                ...(isNr ? { color: 'error.main', fontWeight: 600 } : {})
                              }}
                              title={
                                (isNameOrDomain || (!isNameOrDomain && typeof val === 'string' && val.length > 0))
                                  ? (typeof val === 'string' && val.length > 0 ? val : undefined)
                                  : undefined
                              }
                            >
                              {key === 'Friendly name' && getCbvUrl(row) ? (
                                <Box
                                  component="a"
                                  href={getCbvUrl(row)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                    wordBreak: 'break-word',
                                    ...(infraNarrowNameDomain && {
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    })
                                  }}
                                >
                                  {val}
                                </Box>
                              ) : (
                                val
                              )}
                            </TableCell>
                          );
                        })}
                        {customerInstancesViewTab === 0 && (() => {
                          const cdnVal = getCdnDisplayForRow(row);
                          return (
                            <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 90, width: 90 }}>
                              {cdnVal === null ? (
                                <CircularProgress size={14} />
                              ) : (
                                <Typography variant="body2" color={cdnVal === 'No CDN' ? 'text.secondary' : 'success.main'} fontWeight={cdnVal !== 'No CDN' && cdnVal !== '—' ? 600 : undefined}>
                                  {cdnVal}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })()}
                        <TableCell sx={{ width: 40, minWidth: 40, maxWidth: 40, px: 0.25, textAlign: 'center' }}>
                          {row._id ? (
                            <Tooltip title="Max Memory, Threads, DB connections — change history" arrow>
                              <IconButton size="small" onClick={() => handleOpenCbvResourceHistory(row)} sx={{ p: 0.5 }}>
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        {showCustomerInstanceRowActions && (
                          <TableCell sx={{ whiteSpace: 'nowrap', width: 100 }}>
                            {row._id ? (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                {isAdmin() && (
                                  <>
                                    <Tooltip title="Edit" arrow>
                                      <IconButton size="small" onClick={() => handleOpenEditDialog(row)} sx={{ p: 0.75 }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete instance" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() => { setDeleteConfirmRowId(row._id); setDeleteConfirmRowName(row['Instance Name'] || row._id); }}
                                        sx={{ p: 0.75, color: 'error.main' }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Refresh (CBV / MSV / Azure)" arrow>
                                      <IconButton
                                        size="small"
                                        disabled={!!rowActionLoading[row._id]}
                                        onClick={() => handleOpenRowRefreshDialog(row)}
                                        sx={{ p: 0.75 }}
                                      >
                                        {rowActionLoading[row._id] ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            ) : null}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 1.5
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Showing {paginatedCustomerInstances.length ? customerInstancesPage * CUSTOMER_INSTANCES_ROWS_PER_PAGE + 1 : 0}–
                  {customerInstancesPage * CUSTOMER_INSTANCES_ROWS_PER_PAGE + paginatedCustomerInstances.length} of {sortedCustomerInstances.length}
                  {sortedCustomerInstances.length !== customerInstances.length &&
                    ` (filtered from ${customerInstances.length})`}
                </Typography>
                <TablePagination
                  component="div"
                  count={sortedCustomerInstances.length}
                  page={customerInstancesPage}
                  onPageChange={(_, newPage) => setCustomerInstancesPage(newPage)}
                  rowsPerPage={CUSTOMER_INSTANCES_ROWS_PER_PAGE}
                  rowsPerPageOptions={[CUSTOMER_INSTANCES_ROWS_PER_PAGE]}
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          )}
        </Paper>
      )}

      <Dialog open={addDialogOpen} onClose={() => !submitting && setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add release calendar event</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <DatePicker
                label="Date"
                value={addDate}
                onChange={(v) => setAddDate(v)}
                minDate={dayjs(`${year}-01-01`)}
                maxDate={dayjs(`${year}-12-31`)}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <FormControl fullWidth>
                <InputLabel>Event type</InputLabel>
                <Select
                  value={addEventType}
                  label="Event type"
                  onChange={(e) => setAddEventType(e.target.value)}
                >
                  {EVENT_TYPES.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Release"
                value={addRelease}
                onChange={(e) => setAddRelease(e.target.value)}
                placeholder="e.g. 5.9.21"
                fullWidth
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddEvent} disabled={submitting || !addDate || !addEventType}>
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => !editSubmitting && setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit release calendar event</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <DatePicker
                label="Date"
                value={editDate}
                onChange={(v) => setEditDate(v)}
                minDate={dayjs(`${year}-01-01`)}
                maxDate={dayjs(`${year}-12-31`)}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <FormControl fullWidth>
                <InputLabel>Event type</InputLabel>
                <Select
                  value={editEventType}
                  label="Event type"
                  onChange={(e) => setEditEventType(e.target.value)}
                >
                  {EVENT_TYPES.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Release"
                value={editRelease}
                onChange={(e) => setEditRelease(e.target.value)}
                placeholder="e.g. 5.9.21"
                fullWidth
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          {editId && (
            <Button color="error" onClick={handleDeleteEvent} disabled={editSubmitting} sx={{ mr: 'auto' }}>
              {editSubmitting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setEditDialogOpen(false)} disabled={editSubmitting}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleEditEvent} disabled={editSubmitting || !editDate || !editEventType}>
              {editSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={customersDialogOpen}
        onClose={() => { setCustomersDialogOpen(false); setCustomersDialogGroup(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {customersDialogGroup ? EVENT_TYPES.find((e) => e.value === customersDialogGroup)?.shortLabel || 'Customers' : 'Customers'}
        </DialogTitle>
        <DialogContent>
          {customersForDialog.length === 0 ? (
            <Typography color="textSecondary">
              No customers in this group. Upload a Customer Instances CSV in the Customer Instances tab with a column named Group or group_name and values like vip, Standard, or preprod.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {CUSTOMER_LIST_COLUMNS.map((col) => (
                      <TableCell key={col.label} sx={{ fontWeight: 700, bgcolor: 'grey.200' }}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customersForDialog.map((row, i) => (
                    <TableRow key={i}>
                      {CUSTOMER_LIST_COLUMNS.map((col) => (
                        <TableCell key={col.label}>{getCustomerListValue(row, col)}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCustomersDialogOpen(false); setCustomersDialogGroup(null); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={refreshDialogOpen}
        onClose={() => !refreshingFromDialog && setRefreshDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{refreshDialogOnlyFailed ? 'Refresh Failed' : 'Refresh'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {refreshDialogOnlyFailed ? 'Refresh only instances with NR/failed data for:' : 'Refresh all instances for:'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={refreshDialogSelection.cbv}
                onChange={(e) => setRefreshDialogSelection((s) => ({ ...s, cbv: e.target.checked }))}
                disabled={refreshingFromDialog}
              />
              <ListItemText primary="CBV (Release)" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={refreshDialogSelection.msv}
                onChange={(e) => setRefreshDialogSelection((s) => ({ ...s, msv: e.target.checked }))}
                disabled={refreshingFromDialog}
              />
              <ListItemText primary="MSV" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={refreshDialogSelection.azure}
                onChange={(e) => setRefreshDialogSelection((s) => ({ ...s, azure: e.target.checked }))}
                disabled={refreshingFromDialog}
              />
              <ListItemText primary="Azure" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={refreshDialogSelection.cdn}
                onChange={(e) => setRefreshDialogSelection((s) => ({ ...s, cdn: e.target.checked }))}
                disabled={refreshingFromDialog}
              />
              <ListItemText primary="CDN" />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !refreshingFromDialog && setRefreshDialogOpen(false)} disabled={refreshingFromDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRunRefreshFromDialog}
            disabled={refreshingFromDialog || (!refreshDialogSelection.cbv && !refreshDialogSelection.msv && !refreshDialogSelection.azure && !refreshDialogSelection.cdn)}
            startIcon={refreshingFromDialog ? <CircularProgress size={16} /> : null}
          >
            {refreshingFromDialog ? 'Refreshing…' : 'Run'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rowRefreshDialogRowId != null}
        onClose={() => !rowRefreshDialogRunning && setRowRefreshDialogRowId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Refresh this instance</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which to run for this row:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={rowRefreshDialogSelection.cbv}
                onChange={(e) => setRowRefreshDialogSelection((s) => ({ ...s, cbv: e.target.checked }))}
                disabled={rowRefreshDialogRunning}
              />
              <ListItemText primary="CBV (Release)" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={rowRefreshDialogSelection.msv}
                onChange={(e) => setRowRefreshDialogSelection((s) => ({ ...s, msv: e.target.checked }))}
                disabled={rowRefreshDialogRunning}
              />
              <ListItemText primary="MSV" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={rowRefreshDialogSelection.azure}
                onChange={(e) => setRowRefreshDialogSelection((s) => ({ ...s, azure: e.target.checked }))}
                disabled={rowRefreshDialogRunning}
              />
              <ListItemText primary="Azure" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={rowRefreshDialogSelection.cdn}
                onChange={(e) => setRowRefreshDialogSelection((s) => ({ ...s, cdn: e.target.checked }))}
                disabled={rowRefreshDialogRunning}
              />
              <ListItemText primary="CDN" />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !rowRefreshDialogRunning && setRowRefreshDialogRowId(null)} disabled={rowRefreshDialogRunning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRunRowRefreshDialog}
            disabled={rowRefreshDialogRunning || (!rowRefreshDialogSelection.cbv && !rowRefreshDialogSelection.msv && !rowRefreshDialogSelection.azure && !rowRefreshDialogSelection.cdn)}
            startIcon={rowRefreshDialogRunning ? <CircularProgress size={16} /> : null}
          >
            {rowRefreshDialogRunning ? 'Running…' : 'Run'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cbvResourceHistoryDialog != null}
        onClose={() => setCbvResourceHistoryDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {cbvResourceHistoryDialog?.focus === 'memory' ? 'Memory upgrade history' : 'CBV resource history'} —{' '}
          {cbvResourceHistoryDialog?.title ?? ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {cbvResourceHistoryDialog?.focus === 'memory'
              ? 'Dated snapshots where Max Memory changed after a CBV sync (newest first). Δ shows the change vs. the previous memory value.'
              : 'Recorded when a CBV sync finds a new value for Max Memory, Max Threads, or Max DB Connections (newest first).'}
          </Typography>
          {cbvResourceHistoryDialog?.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : cbvResourceHistoryDialog?.error ? (
            <Alert severity="error">{cbvResourceHistoryDialog.error}</Alert>
          ) : !cbvResourceHistoryDialog?.entries?.length ? (
            <Typography variant="body2" color="text.secondary">
              No history yet. Run a CBV refresh; when any of these values change, a dated row is stored.
            </Typography>
          ) : (() => {
            const isMemoryFocus = cbvResourceHistoryDialog.focus === 'memory';
            const allEntries = cbvResourceHistoryDialog.entries;
            let displayEntries = allEntries;
            if (isMemoryFocus) {
              const oldestFirst = [...allEntries].sort(
                (a, b) => new Date(a.recordedAt) - new Date(b.recordedAt)
              );
              const memoryChanges = [];
              for (let i = 1; i < oldestFirst.length; i++) {
                const prev = oldestFirst[i - 1];
                const cur = oldestFirst[i];
                if (prev.maxMemory == null || cur.maxMemory == null) continue;
                if (Number(cur.maxMemory) !== Number(prev.maxMemory)) {
                  memoryChanges.push({
                    recordedAt: cur.recordedAt,
                    fromMb: Number(prev.maxMemory),
                    toMb: Number(cur.maxMemory),
                    deltaMb: Number(cur.maxMemory) - Number(prev.maxMemory)
                  });
                }
              }
              displayEntries = memoryChanges.sort(
                (a, b) => new Date(b.recordedAt) - new Date(a.recordedAt)
              );
            }
            if (displayEntries.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  No memory changes recorded for this instance yet.
                </Typography>
              );
            }
            const fmtNum = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : String(Number(v)));
            return (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date (UTC)</TableCell>
                      {isMemoryFocus ? (
                        <>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>From (MB)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>To (MB)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Δ (MB)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>From (GiB)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>To (GiB)</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Max Memory</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Max Threads</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Max DB Connections</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayEntries.map((entry, idx) => {
                      const d = entry.recordedAt ? new Date(entry.recordedAt) : null;
                      const dateStr =
                        d && !isNaN(d.getTime()) ? format(d, 'yyyy-MM-dd HH:mm:ss') : '—';
                      if (isMemoryFocus) {
                        const delta = entry.deltaMb;
                        const deltaColor = delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary';
                        return (
                          <TableRow key={`${entry.recordedAt}-${idx}`}>
                            <TableCell>{dateStr}</TableCell>
                            <TableCell align="right">{fmtNum(entry.fromMb)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtNum(entry.toMb)}</TableCell>
                            <TableCell align="right" sx={{ color: deltaColor, fontWeight: 700 }}>
                              {delta > 0 ? `+${delta}` : String(delta)}
                            </TableCell>
                            <TableCell align="right">
                              {mbToGiBDisplay(entry.fromMb) ?? '—'}
                            </TableCell>
                            <TableCell align="right">
                              {mbToGiBDisplay(entry.toMb) ?? '—'}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return (
                        <TableRow key={`${entry.recordedAt}-${idx}`}>
                          <TableCell>{dateStr}</TableCell>
                          <TableCell align="right">{fmtNum(entry.maxMemory)}</TableCell>
                          <TableCell align="right">{fmtNum(entry.totalThreads)}</TableCell>
                          <TableCell align="right">{fmtNum(entry.maxDbConnections)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCbvResourceHistoryDialog(null)} variant="contained" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogRowId != null}
        onClose={() => !editDialogSaving && setEditDialogRowId(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit instance</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {EDIT_ROW_FIELDS.map((key) => {
              const val = editFormValues[key];
              if (key === 'Customer Category') {
                return (
                  <FormControl key={key} size="small" fullWidth>
                    <InputLabel>Customer Category</InputLabel>
                    <Select
                      value={val ?? ''}
                      label="Customer Category"
                      disabled={editDialogSaving}
                      onChange={(e) => setEditFormValues((prev) => ({ ...prev, [key]: e.target.value }))}
                    >
                      <MenuItem value=""><em>(None)</em></MenuItem>
                      {CUSTOMER_CATEGORY_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }
              return (
                <TextField
                  key={key}
                  label={key}
                  value={val ?? ''}
                  onChange={(e) => setEditFormValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  disabled={editDialogSaving}
                  fullWidth
                  size="small"
                  placeholder={key === 'Friendly name' ? 'Defaults to instance name' : undefined}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !editDialogSaving && setEditDialogRowId(null)} disabled={editDialogSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEditDialog} disabled={editDialogSaving}>
            {editDialogSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addInstanceDialogOpen}
        onClose={() => !addInstanceSaving && setAddInstanceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add instance</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {EDIT_ROW_FIELDS.map((key) => {
              if (key === 'Customer Category') {
                return (
                  <FormControl key={key} size="small" fullWidth>
                    <InputLabel>Customer Category</InputLabel>
                    <Select
                      value={addInstanceFormValues[key] ?? ''}
                      label="Customer Category"
                      disabled={addInstanceSaving}
                      onChange={(e) => setAddInstanceFormValues((prev) => ({ ...prev, [key]: e.target.value }))}
                    >
                      <MenuItem value=""><em>(None)</em></MenuItem>
                      {CUSTOMER_CATEGORY_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }
              return (
                <TextField
                  key={key}
                  label={key}
                  value={addInstanceFormValues[key] ?? ''}
                  onChange={(e) => setAddInstanceFormValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  disabled={addInstanceSaving}
                  fullWidth
                  size="small"
                  required={key === 'Instance Name'}
                  placeholder={key === 'Friendly name' ? 'Defaults to instance name' : undefined}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !addInstanceSaving && setAddInstanceDialogOpen(false)} disabled={addInstanceSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveAddInstance} disabled={addInstanceSaving}>
            {addInstanceSaving ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmRowId != null}
        onClose={() => !deleteRowLoading && setDeleteConfirmRowId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete instance?</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteConfirmRowName}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !deleteRowLoading && setDeleteConfirmRowId(null)} disabled={deleteRowLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteRow} disabled={deleteRowLoading}>
            {deleteRowLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={heapDailyDialog != null} onClose={() => setHeapDailyDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Daily violation event counts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {heapDailyDialog?.entry?.friendlyName || heapDailyDialog?.entry?.instanceName || heapDailyDialog?.label} —{' '}
            {heapDailyDialog?.entry?.days ?? HEAP_RECOMMENDATION_DAYS} day window (UTC days). Bars are total events per day
            (heap pressure + OutOfMemoryError). The line is OOM-only events per day.
          </Typography>
          {heapDailyDialog?.entry?.currentMemoryGiB != null && heapDailyDialog?.entry?.proposedMemoryGiB != null && (() => {
            const curr = heapDailyDialog.entry.currentMemoryGiB;
            const prop = heapDailyDialog.entry.proposedMemoryGiB;
            const delta = prop - curr;
            const isIncrease = delta > 0;
            const isDecrease = delta < 0;
            return (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Memory:</Typography>
                <Chip size="small" label={`Current: ${curr.toFixed(1)} GiB`} variant="outlined" />
                {(isIncrease || isDecrease) && (
                  <>
                    <Typography variant="body2" color="text.secondary">→</Typography>
                    <Chip
                      size="small"
                      label={`${isIncrease ? '↑' : '↓'} Proposed: ${prop.toFixed(1)} GiB (${isIncrease ? '+' : ''}${delta.toFixed(1)} GiB)`}
                      color={isIncrease ? 'warning' : 'success'}
                    />
                  </>
                )}
                {!isIncrease && !isDecrease && (
                  <Chip size="small" label="No change recommended" variant="outlined" color="default" />
                )}
              </Stack>
            );
          })()}
          <Box sx={{ height: 360, width: '100%' }}>
            {heapDailyDialog?.entry?.dailyViolationTotals?.length ? (
              <Chart
                type="bar"
                data={{
                  labels: heapDailyDialog.entry.dailyViolationTotals.map((d) => d.date),
                  datasets: [
                    {
                      type: 'bar',
                      label: 'Total (heap + OOM)',
                      data: heapDailyDialog.entry.dailyViolationTotals.map((d) => d.totalViolations ?? 0),
                      backgroundColor: 'rgba(25, 118, 210, 0.55)',
                      borderColor: 'rgba(25, 118, 210, 1)',
                      borderWidth: 1,
                      order: 2
                    },
                    {
                      type: 'line',
                      label: 'OutOfMemoryError',
                      data: heapDailyDialog.entry.dailyViolationTotals.map((d) =>
                        d.oomViolations != null ? Number(d.oomViolations) : 0
                      ),
                      borderColor: 'rgb(211, 47, 47)',
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      borderWidth: 2,
                      tension: 0.15,
                      fill: false,
                      pointRadius: 3,
                      pointHoverRadius: 5,
                      order: 1,
                      yAxisID: 'y'
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: 'index', intersect: false },
                  scales: {
                    x: {
                      ticks: { maxRotation: 45, minRotation: 45, font: { size: 9 } }
                    },
                    y: { beginAtZero: true, ticks: { precision: 0 } }
                  },
                  plugins: {
                    legend: { display: true },
                    title: { display: false }
                  }
                }}
              />
            ) : (
              <Typography color="text.secondary">No daily data.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeapDailyDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dbConnDailyDialog != null} onClose={() => setDbConnDailyDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Daily DB connection violation events</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dbConnDailyDialog?.entry?.friendlyName || dbConnDailyDialog?.entry?.instanceName || dbConnDailyDialog?.label} —{' '}
            {dbConnDailyDialog?.entry?.days ?? DB_CONNECTION_RECOMMENDATION_DAYS} day window (UTC days)
          </Typography>
          <Box sx={{ height: 360, width: '100%' }}>
            {dbConnDailyDialog?.entry?.dailyViolationTotals?.length ? (
              <Bar
                data={{
                  labels: dbConnDailyDialog.entry.dailyViolationTotals.map((d) => d.date),
                  datasets: [
                    {
                      label: 'Violation events (per day)',
                      data: dbConnDailyDialog.entry.dailyViolationTotals.map((d) => d.totalViolations ?? 0),
                      backgroundColor: 'rgba(46, 125, 50, 0.65)',
                      borderColor: 'rgba(46, 125, 50, 1)',
                      borderWidth: 1
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      ticks: { maxRotation: 45, minRotation: 45, font: { size: 9 } }
                    },
                    y: { beginAtZero: true, ticks: { precision: 0 } }
                  },
                  plugins: {
                    legend: { display: true },
                    title: { display: false }
                  }
                }}
              />
            ) : (
              <Typography color="text.secondary">No daily data.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDbConnDailyDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={threadDailyDialog != null} onClose={() => setThreadDailyDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Daily Tomcat thread violation events</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {threadDailyDialog?.entry?.friendlyName || threadDailyDialog?.entry?.instanceName || threadDailyDialog?.label} —{' '}
            {threadDailyDialog?.entry?.days ?? THREAD_RECOMMENDATION_DAYS} day window (UTC days)
          </Typography>
          <Box sx={{ height: 360, width: '100%' }}>
            {threadDailyDialog?.entry?.dailyViolationTotals?.length ? (
              <Bar
                data={{
                  labels: threadDailyDialog.entry.dailyViolationTotals.map((d) => d.date),
                  datasets: [
                    {
                      label: 'Violation events (per day)',
                      data: threadDailyDialog.entry.dailyViolationTotals.map((d) => d.totalViolations ?? 0),
                      backgroundColor: 'rgba(230, 81, 0, 0.65)',
                      borderColor: 'rgba(230, 81, 0, 1)',
                      borderWidth: 1
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      ticks: { maxRotation: 45, minRotation: 45, font: { size: 9 } }
                    },
                    y: { beginAtZero: true, ticks: { precision: 0 } }
                  },
                  plugins: {
                    legend: { display: true },
                    title: { display: false }
                  }
                }}
              />
            ) : (
              <Typography color="text.secondary">No daily data.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThreadDailyDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ReleaseCalendarView;
